import { useState, useEffect, useRef, useCallback } from 'react';
import { getApiUrl, getBaseUrl } from "@/config/appConfig";
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getEmbedTargetOrigin } from '../utils/embedOrigin';
import { 
  ArrowLeft, Save, Square, Circle, Users, Sparkles, 
  MoveIcon, Trash2, Copy, Grid3x3, Undo2, Redo2,
  Utensils, DoorOpen, Music, Plus, Minus, RotateCw,
  Camera, ShieldAlert, Users2, Warehouse, X, CheckCircle,
  Type, Theater, Mic2, Beer, Bath, LogIn, LogOut, ChefHat, 
  Accessibility, Cctv, Shield, Trees, Building, UtensilsCrossed, ShoppingBag,
  Rows3, LayoutGrid, CircleDot, RectangleHorizontal, Armchair, Layers,
  Bold, Italic, Underline, Star, Heart, AlertTriangle, Info, Zap, Target,
  Clock, MapPin, Wifi, Coffee, Car, Plane, Music2, Tv, Monitor, Phone,
  Group, Ungroup, Lock, Sofa, Lamp, Eye, ChevronLeft, ChevronRight, PanelLeftClose, PanelRightClose,
  Maximize2, AlertCircle, LayoutDashboard, Bus, ArrowUp, HelpCircle
} from 'lucide-react';
import CanvasWalkthrough, { useCanvasWalkthrough } from '../components/CanvasWalkthrough';

// Using centralized config
const getAPI = () => getApiUrl();
const API = `${getAPI()}`;

// Font families available
const FONT_FAMILIES = [
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Times New Roman, serif', label: 'Times' },
  { value: 'Courier New, monospace', label: 'Courier' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet' },
  { value: 'Impact, sans-serif', label: 'Impact' },
];

// Pre-defined icons for text labels
const TEXT_ICONS = [
  { icon: null, label: 'None' },
  { icon: Star, label: 'Star' },
  { icon: Heart, label: 'Heart' },
  { icon: AlertTriangle, label: 'Warning' },
  { icon: Info, label: 'Info' },
  { icon: Zap, label: 'Zap' },
  { icon: Target, label: 'Target' },
  { icon: Clock, label: 'Clock' },
  { icon: MapPin, label: 'Location' },
  { icon: Wifi, label: 'WiFi' },
  { icon: Coffee, label: 'Coffee' },
  { icon: Car, label: 'Parking' },
  { icon: Music2, label: 'Music' },
  { icon: Tv, label: 'Screen' },
  { icon: Phone, label: 'Phone' },
];

// Seat status colors - default theme (for checkout/widget only, not canvas design)
const SEAT_STATUS_COLORS = {
  available: '#22c55e',      // Green
  booked: '#9ca3af',         // Grey
  held: '#f59e0b',           // Amber/Yellow (current hold color)
  selected: '#3b82f6',       // Blue
  unavailable: '#6b7280'     // Grey (darker)
};

// Canvas design colors (neutral)
const CANVAS_SEAT_COLOR = '#e2e8f0';  // Light gray for design view
const CANVAS_SEAT_BORDER = '#94a3b8'; // Slate border

const TOOL_CATEGORIES = {
  seating: {
    label: 'SEATING',
    tools: {
      single_row: { icon: Rows3, label: 'Single Row', color: '#e2e8f0', width: 300, height: 40 },
      multiple_rows: { icon: LayoutGrid, label: 'Multiple Rows', color: '#e2e8f0', width: 300, height: 120 },
      bus_layout: { icon: Bus, label: 'Bus Layout', color: '#60a5fa', width: 250, height: 800 },
      round_table: { icon: CircleDot, label: 'Round Table', color: '#ddd6fe', width: 100, height: 100 },
      rect_table: { icon: RectangleHorizontal, label: 'Rect Table', color: '#ddd6fe', width: 120, height: 80 },
      seat: { icon: Sofa, label: 'Single Seat', color: '#e2e8f0', width: 50, height: 40 },
      sofa: { icon: Sofa, label: 'Sofa', color: '#a78bfa', width: 150, height: 60 },
      zone: { icon: Layers, label: 'Zone / Section', color: '#dbeafe', width: 200, height: 150 },
    }
  },
  bus_features: {
    label: 'BUS FEATURES',
    busOnly: true, // Only show in bus layout mode
    tools: {
      bus_door_front: { icon: DoorOpen, label: 'Front Door', color: '#f59e0b', width: 50, height: 80 },
      bus_door_middle: { icon: DoorOpen, label: 'Middle Door', color: '#f59e0b', width: 50, height: 80 },
      bus_door_back: { icon: DoorOpen, label: 'Back Door', color: '#f59e0b', width: 50, height: 80 },
      bus_toilet: { icon: Bath, label: 'Toilet', color: '#06b6d4', width: 60, height: 60 },
      bus_tv: { icon: Tv, label: 'TV Screen', color: '#8b5cf6', width: 80, height: 50 },
      bus_aisle: { icon: Rows3, label: 'Extra Aisle', color: '#e2e8f0', width: 30, height: 200 },
    }
  },
  areas: {
    label: 'STAGE & AREAS',
    tools: {
      screen: { icon: Monitor, label: 'Screen', color: '#6b7280', width: 400, height: 60 },
      stage: { icon: Theater, label: 'Stage', color: '#a855f7', width: 300, height: 100 },
      dj_booth: { icon: Mic2, label: 'DJ Booth', color: '#06b6d4', width: 120, height: 80 },
      ga_zone: { icon: Users, label: 'GA Zone', color: '#fef3c7', width: 200, height: 150 },
      vip_pod: { icon: Sparkles, label: 'VIP Pod', color: '#fce7f3', width: 150, height: 150 },
    }
  },
  facilities: {
    label: 'FACILITIES',
    tools: {
      bar: { icon: Beer, label: 'Bar', color: '#f97316', width: 200, height: 60 },
      toilet: { icon: Bath, label: 'Restroom', color: '#0ea5e9', width: 80, height: 80 },
      entrance: { icon: LogIn, label: 'Entrance', color: '#10b981', width: 100, height: 60 },
      exit: { icon: LogOut, label: 'Exit', color: '#ef4444', width: 100, height: 60 },
      kitchen: { icon: ChefHat, label: 'Kitchen', color: '#f59e0b', width: 150, height: 100 },
      wheelchair: { icon: Accessibility, label: 'Wheelchair', color: '#0369a1', width: 80, height: 80 },
      camera: { icon: Cctv, label: 'Camera', color: '#7c3aed', width: 100, height: 80 },
      security: { icon: Shield, label: 'Security', color: '#dc2626', width: 80, height: 80 },
      greenroom: { icon: Trees, label: 'Green Room', color: '#16a34a', width: 120, height: 100 },
      backstage: { icon: Building, label: 'Backstage', color: '#475569', width: 150, height: 120 },
      foodcourt: { icon: UtensilsCrossed, label: 'Food Court', color: '#ea580c', width: 200, height: 150 },
      merchandise: { icon: ShoppingBag, label: 'Merch', color: '#8b5cf6', width: 120, height: 100 },
    }
  },
  other: {
    label: 'OTHER',
    tools: {
      text: { icon: Type, label: 'Text Label', color: '#64748b', width: 150, height: 40 },
    }
  },
  accessories: {
    label: 'ACCESSORIES',
    tools: {
      table_lamp: { icon: Lamp, label: 'Table Lamp', color: '#fcd34d', width: 40, height: 40 },
      coffee_table: { icon: Coffee, label: 'Coffee Table', color: '#78716c', width: 80, height: 50 },
    }
  }
};

export default function AdvancedLayoutBuilder() {
  const navigate = useNavigate();
  const { layoutId } = useParams();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  const embedApiKey = searchParams.get('api_key');
  const embedOrganiserId = searchParams.get('organiser_id');
  const embedOrganiserName = searchParams.get('organiser_name') || '';
  const embedTheme = searchParams.get('theme') || 'light';
  const embedEventId = searchParams.get('event_id');
  const embedEventName = searchParams.get('event_name') || '';
  // White-label branding for the walkthrough — passed by the SDK via URL param
  // `?brand=TicketKart` so embedding companies see "Welcome to TicketKart" instead
  // of the default "the Layout Builder".
  const embedBrandName = searchParams.get('brand') || 'the Layout Builder';
  const canvasRef = useRef(null);
  const [formData, setFormData] = useState({
    name: 'New Layout',
    venue_name: '',
    grid_snap: true,
    layout_display_mode: 'manual' // Default to 'manual' for canvas positioning
  });
  const [canvasObjects, setCanvasObjects] = useState([]);
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedObjects, setSelectedObjects] = useState([]); // Multi-select
  const [draggedObject, setDraggedObject] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [activeCategory, setActiveCategory] = useState('seating');
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorField, setErrorField] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [layoutAssigned, setLayoutAssigned] = useState(false);
  const [hasBookings, setHasBookings] = useState(false);
  const [eventInfo, setEventInfo] = useState(null); // { event_id, event_name, event_status, booked_seats_count }
  const [resizing, setResizing] = useState(null); // { objectId, direction, startX, startY, startWidth, startHeight }
  const [editingTextId, setEditingTextId] = useState(null); // For double-click text editing
  const [seatColorExpanded, setSeatColorExpanded] = useState(false); // Collapsed by default for rows
  const [seatColorExpandedTable, setSeatColorExpandedTable] = useState(false); // Collapsed by default for tables
  const [seatColorExpandedSofa, setSeatColorExpandedSofa] = useState(false); // Collapsed by default for sofas
  
  // Load user preferences from localStorage
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(() => {
    // On mobile, always collapse. On desktop, load saved preference
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return true;
    }
    const saved = localStorage.getItem('canvasLeftPanelCollapsed');
    return saved === 'true';
  });
  const [statsInTopBar, setStatsInTopBar] = useState(() => {
    const saved = localStorage.getItem('canvasStatsInTopBar');
    return saved === 'true';
  });
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(() => {
    // On mobile, always collapse. On desktop, load saved preference
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return true;
    }
    const saved = localStorage.getItem('canvasRightPanelCollapsed');
    return saved === 'true';
  });

  // On-canvas walkthrough for new organizers (auto-opens on first visit,
  // replayable via the ? button in the top bar). Works both in native dashboard
  // mode and when embedded on a 3rd-party site via the SeatPilot SDK.
  const walkthrough = useCanvasWalkthrough({ autoOpenOnFirstVisit: true });
  
  // Save preferences when they change
  useEffect(() => {
    localStorage.setItem('canvasLeftPanelCollapsed', leftPanelCollapsed.toString());
  }, [leftPanelCollapsed]);
  
  useEffect(() => {
    localStorage.setItem('canvasStatsInTopBar', statsInTopBar.toString());
  }, [statsInTopBar]);
  
  useEffect(() => {
    // Only save on desktop
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      localStorage.setItem('canvasRightPanelCollapsed', rightPanelCollapsed.toString());
    }
  }, [rightPanelCollapsed]);
  
  const [overlapWarnings, setOverlapWarnings] = useState([]); // Overlap detection warnings
  const [confirmDialog, setConfirmDialog] = useState(null); // { action, count, message, onConfirm, showUnlink, canUnlink }
  const [busLayoutMode, setBusLayoutMode] = useState(false); // Exclusive bus layout mode
  const [busLayoutWarning, setBusLayoutWarning] = useState(null); // Warning modal for bus layout
  const [skipDeleteConfirm, setSkipDeleteConfirm] = useState(() => {
    // Load from session storage
    return sessionStorage.getItem('skipDeleteConfirm') === 'true';
  });
  const autoHideTimeoutRef = useRef(null);
  const autoSaveIntervalRef = useRef(null);
  
  // Handle window resize for responsive right panel
  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined' && window.innerWidth < 768) {
        setRightPanelCollapsed(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    // Initial check
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Handle keyboard shortcuts for multi-select actions
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger delete when typing in input fields
      const activeElement = document.activeElement;
      const isInputField = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );
      
      if (isInputField) {
        return; // Let the input handle the key event
      }
      
      // Delete key - show confirmation dialog (actual deletion happens on confirm)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedObjects.length > 0) {
        e.preventDefault();
        
        // Skip confirmation if user checked "don't ask again"
        if (skipDeleteConfirm) {
          if (selectedObjects.length > 1) {
            deleteSelectedObjects();
          } else {
            deleteObject();
          }
          return;
        }
        
        // Show confirmation - do NOT delete anything here, just show dialog
        if (selectedObjects.length > 1) {
          confirmDeleteSelectedObjects();
        } else {
          // Capture the current selectedObject ID for use in the callback
          const objectIdToDelete = selectedObjects[0];
          showConfirmDialog(
            'delete',
            1,
            'Are you sure you want to delete this object? This action cannot be undone.',
            () => {
              // Delete using the captured ID, not the current state
              performSingleDelete(objectIdToDelete);
              hideConfirmDialog();
            }
          );
        }
      }
      // Ctrl+D - duplicate selected objects
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedObjects.length > 0) {
        e.preventDefault();
        duplicateObject();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  // Handle resize start
  const handleResizeStart = (objectId, direction, e) => {
    const obj = canvasObjects.find(o => o.id === objectId);
    if (obj) {
      setResizing({
        objectId,
        direction,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: obj.width,
        startHeight: obj.height
      });
    }
  };

  // Handle resize move
  const handleResizeMove = useCallback((e) => {
    if (!resizing) return;
    
    const deltaX = e.clientX - resizing.startX;
    const deltaY = e.clientY - resizing.startY;
    
    const newObjects = canvasObjects.map(obj => {
      if (obj.id === resizing.objectId) {
        let newWidth = resizing.startWidth;
        let newHeight = resizing.startHeight;
        
        if (resizing.direction.includes('e')) {
          newWidth = Math.max(50, resizing.startWidth + deltaX);
        }
        if (resizing.direction.includes('s')) {
          newHeight = Math.max(50, resizing.startHeight + deltaY);
        }
        
        return { ...obj, width: newWidth, height: newHeight };
      }
      return obj;
    });
    
    setCanvasObjects(newObjects);
  }, [resizing, canvasObjects]);

  // Handle resize end
  const handleResizeEnd = useCallback(() => {
    if (resizing) {
      addToHistory(canvasObjects);
      setResizing(null);
    }
  }, [resizing, canvasObjects]);

  // Add resize event listeners
  useEffect(() => {
    if (resizing) {
      window.addEventListener('mousemove', handleResizeMove);
      window.addEventListener('mouseup', handleResizeEnd);
      return () => {
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('mouseup', handleResizeEnd);
      };
    }
  }, [resizing, handleResizeMove, handleResizeEnd]);

  // Show top-center success/error message with manual close
  const showSuccessMessage = (message, fieldId = null) => {
    setSuccessMessage(message);
    setErrorField(fieldId);
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
    }
    autoHideTimeoutRef.current = setTimeout(() => {
      setSuccessMessage(null);
      setErrorField(null);
    }, 4000);
  };

  const closeSuccessMessage = () => {
    if (autoHideTimeoutRef.current) {
      clearTimeout(autoHideTimeoutRef.current);
    }
    setSuccessMessage(null);
    setErrorField(null);
  };

  // Track if there are unsaved changes
  const [hasChanges, setHasChanges] = useState(false);
  const [currentLayoutId, setCurrentLayoutId] = useState(layoutId || null); // Track current layout ID for Preview
  const prevCanvasObjectsRef = useRef(JSON.stringify([]));

  // Auto-save functionality - only saves if there are actual changes
  const autoSave = useCallback(async () => {
    if (!autoSaveEnabled || !hasChanges || !formData.name) return;
    if (canvasObjects.length === 0 && !currentLayoutId) return;
    
    // Check for overlaps before saving
    const overlaps = detectOverlaps();
    if (overlaps.length > 0) {
      setOverlapWarnings(overlaps);
      showSuccessMessage({ 
        type: 'warning', 
        text: `Warning: ${overlaps.length} overlapping object(s) detected. Layout saved but please review overlaps.` 
      });
    } else {
      setOverlapWarnings([]);
    }
    
    try {
      const payload = {
        ...formData,
        canvas_objects: canvasObjects
      };

      if (currentLayoutId) {
        // UPDATE existing layout
        if (isEmbed && embedApiKey) {
          await axios.post(`${API}/external/layouts/${currentLayoutId}`, payload, {
            headers: { 'X-API-Key': embedApiKey }
          });
        } else {
          await axios.put(`${API}/admin/layouts/${currentLayoutId}`, payload);
        }
        setLastSaved(new Date());
        setHasChanges(false);
      } else if (canvasObjects.length > 0) {
        // CREATE new layout only once
        let response;
        if (isEmbed && embedApiKey) {
          response = await axios.post(`${API}/external/layouts`, payload, {
            headers: { 'X-API-Key': embedApiKey }
          });
        } else {
          response = await axios.post(`${API}/admin/layouts`, payload);
        }
        if (response.data?.id) {
          setCurrentLayoutId(response.data.id);
          if (!isEmbed) window.history.replaceState(null, '', `/builder/${response.data.id}`);
          setLastSaved(new Date());
          setHasChanges(false);
          // Notify parent window in embed mode with full seat details
          if (isEmbed) {
            const savedId = response.data.id;
            try {
              const seatsRes = await axios.get(`${API}/external/layouts/${savedId}/seats`, { headers: { 'X-API-Key': embedApiKey } });
              window.parent.postMessage({ type: 'layout_saved', layout: {
                layout_id: savedId, id: savedId, ...formData,
                seat_count: seatsRes.data.total_seats || canvasObjects.reduce((a, o) => a + (o.seats?.length || 0), 0),
                is_published: response.data.is_published || false,
                widget_url: `https://seatpilot.co.uk/widget/${savedId}`,
                total_seats: seatsRes.data.total_seats,
                categories: seatsRes.data.categories || [],
                seats: seatsRes.data.categories || [],
                has_pricing: seatsRes.data.has_pricing,
                currency: seatsRes.data.currency
              }}, '*');
            } catch {
              window.parent.postMessage({ type: 'layout_saved', layout: {
                layout_id: savedId, id: savedId, ...formData,
                seat_count: canvasObjects.reduce((a, o) => a + (o.seats?.length || 0), 0),
                is_published: response.data.is_published || false,
                widget_url: `https://seatpilot.co.uk/widget/${savedId}`
              }}, '*');
            }
          }
        }
      }
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  }, [formData, canvasObjects, currentLayoutId, autoSaveEnabled, hasChanges, isEmbed, embedApiKey]);


  // Embed mode: Notify parent when builder is ready
  useEffect(() => {
    if (isEmbed) {
      window.parent.postMessage({ type: 'builder_ready', status: 'ready', layoutId: currentLayoutId }, getEmbedTargetOrigin());
    }
  }, [isEmbed, currentLayoutId]);

  // Embed mode: Notify parent on loading state changes
  useEffect(() => {
    if (isEmbed) {
      window.parent.postMessage({ type: 'builder_loading', loading: loading }, getEmbedTargetOrigin());
    }
  }, [isEmbed, loading]);

  // Embed mode: Listen for commands from parent
  useEffect(() => {
    if (!isEmbed) return;
    const handleMessage = (event) => {
      const { type, ...data } = event.data || {};
      if (type === 'save_layout') handleSubmit();
      if (type === 'publish_layout') handleSubmit();
      if (type === 'load_layout' && data.layout_id) {
        window.location.href = `/builder/${data.layout_id}?embed=true&api_key=${embedApiKey}`;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isEmbed, embedApiKey]);


  // Track changes to canvas objects
  useEffect(() => {
    const currentJson = JSON.stringify(canvasObjects);
    if (currentJson !== prevCanvasObjectsRef.current) {
      setHasChanges(true);
      prevCanvasObjectsRef.current = currentJson;
    }
  }, [canvasObjects]);

  // Setup auto-save interval - only runs if auto-save is enabled
  useEffect(() => {
    if (autoSaveEnabled) {
      autoSaveIntervalRef.current = setInterval(() => {
        if (hasChanges) {
          autoSave();
        }
      }, 5000); // Auto-save every 5 seconds
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
    };
  }, [autoSaveEnabled, autoSave, hasChanges]);

  // Update currentLayoutId when URL param changes
  useEffect(() => {
    setCurrentLayoutId(layoutId);
  }, [layoutId]);

  useEffect(() => {
    if (layoutId) {
      fetchLayout();
    } else {
      // Initialize with a default Screen object at the top
      const defaultScreen = {
        id: `obj_screen_default`,
        type: 'screen',
        x: 200,
        y: 50,
        width: 400,
        height: 60,
        rotation: 0,
        color: '#7c3aed',
        label: 'SCREEN',
        properties: { icon_color: '#ffffff', text_color: '#ffffff', text_bold: true, font_size: 16 },
        seats: []
      };
      setCanvasObjects([defaultScreen]);
      setHistory([[defaultScreen]]);
      setHistoryIndex(0);
      // Auto-name for new layouts
      checkAndSetAutoName();
      // Show auto-save message for new layouts
      if (autoSaveEnabled) {
        showSuccessMessage({ type: 'success', text: '✨ Auto-save ON! Changes saved every 5 seconds when modified.' });
      }
    }
  }, [layoutId]);

  useEffect(() => {
    // Keyboard shortcuts
    const handleKeyDown = (e) => {
      // Don't trigger shortcuts when typing in input fields
      const activeElement = document.activeElement;
      const isInputField = activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );
      
      // Allow Ctrl+Z and Ctrl+Y even in input fields for undo/redo
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
        return;
      }
      
      // Note: Delete key handling is in the other useEffect (with confirmation dialog)
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [historyIndex, selectedObject, canvasObjects]);

  const checkAndSetAutoName = async () => {
    try {
      const response = await axios.get(`${API}/admin/layouts`);
      const layouts = response.data;
      const newLayoutNames = layouts.filter(l => l.name.startsWith('New Layout')).map(l => l.name);
      
      if (newLayoutNames.length === 0) {
        setFormData(prev => ({ ...prev, name: 'New Layout' }));
      } else {
        let counter = 1;
        while (newLayoutNames.includes(`New Layout ${counter}`)) {
          counter++;
        }
        setFormData(prev => ({ ...prev, name: `New Layout ${counter}` }));
      }
    } catch (error) {
      setFormData(prev => ({ ...prev, name: 'New Layout' }));
    }
  };

  const fetchLayout = async () => {
    try {
      let layout;
      if (isEmbed && embedApiKey) {
        // SDK embed mode: use external API with API key
        const response = await axios.get(`${API}/external/layouts/${layoutId}`, {
          headers: { 'X-API-Key': embedApiKey }
        });
        layout = response.data.layout;
      } else {
        // Normal mode: use admin API with cookie auth
        const response = await axios.get(`${API}/admin/layouts/${layoutId}`);
        layout = response.data;
      }
      
      setFormData({
        name: layout.name,
        venue_name: layout.venue_name || '',
        grid_snap: layout.grid_snap,
        layout_display_mode: layout.layout_display_mode || 'manual'
      });
      let objects = layout.canvas_objects || [];
      
      // Check if this is a bus layout
      const hasBusLayout = objects.some(o => o.type === 'bus_layout');
      
      // Migration: If old layout has show_screen=true but no screen object, add one
      // Skip for bus layouts as they don't use screen objects
      if (!hasBusLayout && layout.show_screen !== false && !objects.some(o => o.type === 'screen')) {
        const defaultScreen = {
          id: `obj_screen_migrated`,
          type: 'screen',
          x: 200,
          y: 50,
          width: 400,
          height: 60,
          rotation: 0,
          color: '#7c3aed',
          label: layout.screen_label || 'SCREEN',
          properties: { icon_color: '#ffffff', text_color: '#ffffff', text_bold: true, font_size: 16 },
          seats: []
        };
        objects = [defaultScreen, ...objects];
      }
      
      setCanvasObjects(objects);
      setHistory([objects]);
      setHistoryIndex(0);
      
      // Set bus mode based on layout content
      setBusLayoutMode(hasBusLayout);
      
      // Auto-zoom and center on mobile for better visibility
      if (typeof window !== 'undefined' && window.innerWidth < 768 && objects.length > 0) {
        // Calculate content bounds
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        objects.forEach(obj => {
          minX = Math.min(minX, obj.x || 0);
          minY = Math.min(minY, obj.y || 0);
          maxX = Math.max(maxX, (obj.x || 0) + (obj.width || 100));
          maxY = Math.max(maxY, (obj.y || 0) + (obj.height || 100));
        });
        
        const contentWidth = maxX - minX + 100;
        const contentHeight = maxY - minY + 100;
        const viewportWidth = window.innerWidth - 40; // Account for collapsed panels
        const viewportHeight = window.innerHeight - 200;
        
        // Calculate zoom to fit content
        const zoomToFitWidth = viewportWidth / contentWidth;
        const zoomToFitHeight = viewportHeight / contentHeight;
        const optimalZoom = Math.min(zoomToFitWidth, zoomToFitHeight, 1);
        
        setZoom(Math.max(0.3, Math.min(optimalZoom, 1)));
        
        // Scroll canvas to center content after a brief delay
        setTimeout(() => {
          if (canvasRef.current && canvasRef.current.parentElement) {
            const scrollContainer = canvasRef.current.parentElement;
            const centerX = (minX + maxX) / 2;
            const centerY = (minY + maxY) / 2;
            scrollContainer.scrollTo({
              left: Math.max(0, centerX * optimalZoom - viewportWidth / 2),
              top: Math.max(0, centerY * optimalZoom - viewportHeight / 2),
              behavior: 'smooth'
            });
          }
        }, 100);
      }
      
      // Check if layout is assigned to an event
      const isAssigned = !!layout.event_id;
      setLayoutAssigned(isAssigned);
      setHasBookings((layout.booked_seats_count || 0) > 0);
      
      // Store event info for unlink functionality
      if (isAssigned) {
        setEventInfo({
          event_id: layout.event_id,
          event_name: layout.event_name,
          event_status: layout.event_status || 'draft',
          booked_seats_count: layout.booked_seats_count || 0
        });
      } else {
        setEventInfo(null);
      }
      
      // Show auto-save message when editing
      showSuccessMessage({ type: 'success', text: '✨ Auto-save enabled! Your changes will be saved every 5 seconds.' });
    } catch (error) {
      showSuccessMessage({ type: 'error', text: 'Failed to fetch layout' });
    }
  };

  const addToHistory = (newObjects) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(JSON.parse(JSON.stringify(newObjects)));
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setCanvasObjects(JSON.parse(JSON.stringify(history[historyIndex - 1])));
      setSelectedObject(null);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setCanvasObjects(JSON.parse(JSON.stringify(history[historyIndex + 1])));
      setSelectedObject(null);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!formData.name) {
      showSuccessMessage({ type: 'error', text: 'Please enter a layout name' }, 'name');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        canvas_objects: canvasObjects
      };

      if (currentLayoutId) {
        if (isEmbed && embedApiKey) {
          await axios.post(`${API}/external/layouts/${currentLayoutId}`, payload, {
            headers: { 'X-API-Key': embedApiKey }
          });
        } else {
          await axios.put(`${API}/admin/layouts/${currentLayoutId}`, payload);
        }
        showSuccessMessage({ type: 'success', text: 'Layout saved!' });
        if (isEmbed) {
          try {
            const seatsRes = await axios.get(`${API}/external/layouts/${currentLayoutId}/seats`, { headers: { 'X-API-Key': embedApiKey } });
            window.parent.postMessage({ type: 'layout_saved', layout: { layout_id: currentLayoutId, id: currentLayoutId, ...formData, seat_count: seatsRes.data.total_seats, is_published: true, widget_url: `https://seatpilot.co.uk/widget/${currentLayoutId}`, total_seats: seatsRes.data.total_seats, categories: seatsRes.data.categories, seats: seatsRes.data.categories, has_pricing: seatsRes.data.has_pricing, currency: seatsRes.data.currency }}, getEmbedTargetOrigin());
          } catch { window.parent.postMessage({ type: 'layout_saved', layout: { layout_id: currentLayoutId, id: currentLayoutId, ...formData, seat_count: canvasObjects.reduce((a, o) => a + (o.seats?.length || 0), 0), is_published: true, widget_url: `https://seatpilot.co.uk/widget/${currentLayoutId}` }}, getEmbedTargetOrigin()); }
        }
      } else {
        let response;
        if (isEmbed && embedApiKey) {
          response = await axios.post(`${API}/external/layouts`, payload, {
            headers: { 'X-API-Key': embedApiKey }
          });
        } else {
          response = await axios.post(`${API}/admin/layouts`, payload);
        }
        showSuccessMessage({ type: 'success', text: 'Layout created!' });
        if (response.data?.id) {
          setCurrentLayoutId(response.data.id);
          if (!isEmbed) window.history.replaceState(null, '', `/builder/${response.data.id}`);
          if (isEmbed) {
            const newId = response.data.id;
            try {
              const seatsRes = await axios.get(`${API}/external/layouts/${newId}/seats`, { headers: { 'X-API-Key': embedApiKey } });
              window.parent.postMessage({ type: 'layout_saved', layout: { layout_id: newId, id: newId, ...formData, seat_count: seatsRes.data.total_seats, is_published: response.data.is_published || false, widget_url: `https://seatpilot.co.uk/widget/${newId}`, total_seats: seatsRes.data.total_seats, categories: seatsRes.data.categories, seats: seatsRes.data.categories, has_pricing: seatsRes.data.has_pricing, currency: seatsRes.data.currency }}, getEmbedTargetOrigin());
            } catch { window.parent.postMessage({ type: 'layout_saved', layout: { layout_id: newId, id: newId, ...formData, seat_count: response.data.seat_count || 0, is_published: response.data.is_published || false, widget_url: `https://seatpilot.co.uk/widget/${newId}` }}, getEmbedTargetOrigin()); }
          }
        }
      }
      setHasChanges(false);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Save error:', error);
      // Show specific error message based on the response
      const errorDetail = error.response?.data?.detail || '';
      let errorMessage = 'Failed to save layout';
      
      if (errorDetail.toLowerCase().includes('subscription') || 
          errorDetail.toLowerCase().includes('active plan') ||
          errorDetail.toLowerCase().includes('no active')) {
        errorMessage = 'Please activate your subscription to save layouts. Go to Dashboard → Billing to choose a plan.';
      } else if (errorDetail.toLowerCase().includes('limit')) {
        errorMessage = 'Layout limit reached. Please upgrade your plan to create more layouts.';
      } else if (errorDetail) {
        errorMessage = errorDetail;
      }
      
      showSuccessMessage({ type: 'error', text: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  // Convert number to Excel-style column label (1=A, 26=Z, 27=AA, 28=AB, etc.)
  const numberToExcelLabel = (num) => {
    let label = '';
    while (num > 0) {
      num--;
      label = String.fromCharCode(65 + (num % 26)) + label;
      num = Math.floor(num / 26);
    }
    return label || 'A';
  };

  // Convert Excel-style label to number (A=1, Z=26, AA=27, AB=28, etc.)
  const excelLabelToNumber = (label) => {
    let num = 0;
    for (let i = 0; i < label.length; i++) {
      num = num * 26 + (label.charCodeAt(i) - 64);
    }
    return num;
  };

  // Get next available row label (supports AA, AB, AC after Z)
  const getNextAvailableRowLabel = (zone = '') => {
    const usedLabels = new Set();
    canvasObjects.forEach(obj => {
      // Only consider rows in the same zone
      const objZone = obj.properties?.zone || '';
      if (objZone !== zone) return;
      
      if (obj.type === 'single_row') {
        usedLabels.add(obj.properties?.row_label || 'A');
      } else if (obj.type === 'multiple_rows') {
        const start = obj.properties?.start_label || 'A';
        const count = obj.properties?.row_count || 1;
        const startNum = excelLabelToNumber(start);
        for (let i = 0; i < count; i++) {
          usedLabels.add(numberToExcelLabel(startNum + i));
        }
      }
    });
    
    let num = 1;
    while (usedLabels.has(numberToExcelLabel(num))) {
      num++;
    }
    return numberToExcelLabel(num);
  };

  // Check if a row label is already used by another row IN THE SAME ZONE
  // Row A in Zone 1 is different from Row A in Zone 2 or default zone
  const isRowLabelDuplicate = (label, excludeObjectId, zone = '') => {
    return canvasObjects.some(obj => {
      if (obj.id === excludeObjectId) return false;
      // Only check rows in the same zone
      const objZone = obj.properties?.zone || '';
      if (objZone !== zone) return false;
      
      if (obj.type === 'single_row') {
        return obj.properties?.row_label === label;
      } else if (obj.type === 'multiple_rows') {
        const start = obj.properties?.start_label || 'A';
        const count = obj.properties?.row_count || 1;
        const startNum = excelLabelToNumber(start);
        const checkNum = excelLabelToNumber(label);
        return checkNum >= startNum && checkNum < startNum + count;
      }
      return false;
    });
  };

  // Get next available label for multi-row that avoids existing labels IN THE SAME ZONE (supports Excel-style)
  const getNextAvailableMultiRowStartLabel = (rowCount, zone = '') => {
    const usedLabels = new Set();
    canvasObjects.forEach(obj => {
      // Only consider rows in the same zone
      const objZone = obj.properties?.zone || '';
      if (objZone !== zone) return;
      
      if (obj.type === 'single_row') {
        usedLabels.add(obj.properties?.row_label || 'A');
      } else if (obj.type === 'multiple_rows') {
        const start = obj.properties?.start_label || 'A';
        const count = obj.properties?.row_count || 1;
        const startNum = excelLabelToNumber(start);
        for (let i = 0; i < count; i++) {
          usedLabels.add(numberToExcelLabel(startNum + i));
        }
      }
    });
    
    // Find a starting label where all consecutive labels are available
    let startNum = 1;
    while (true) {
      let allAvailable = true;
      for (let i = 0; i < rowCount; i++) {
        const checkLabel = numberToExcelLabel(startNum + i);
        if (usedLabels.has(checkLabel)) {
          allAvailable = false;
          break;
        }
      }
      if (allAvailable) return numberToExcelLabel(startNum);
      startNum++;
      if (startNum > 1000) break; // Safety limit
    }
    return 'A';
  };

  // Check if multi-row labels would conflict IN THE SAME ZONE (supports Excel-style)
  const wouldMultiRowConflict = (startLabel, rowCount, excludeObjectId, zone = '') => {
    const conflictingLabels = [];
    const startNum = excelLabelToNumber(startLabel);
    for (let i = 0; i < rowCount; i++) {
      const checkLabel = numberToExcelLabel(startNum + i);
      if (isRowLabelDuplicate(checkLabel, excludeObjectId, zone)) {
        conflictingLabels.push(checkLabel);
      }
    }
    return conflictingLabels;
  };

  // Check if table name is duplicate IN THE SAME ZONE
  const isTableNameDuplicate = (name, excludeObjectId, zone = '') => {
    return canvasObjects.some(obj => {
      if (obj.id === excludeObjectId) return false;
      // Only check tables in the same zone
      const objZone = obj.properties?.zone || '';
      if (objZone !== zone) return false;
      
      if (obj.type === 'round_table' || obj.type === 'rect_table') {
        return obj.label === name;
      }
      return false;
    });
  };

  // Get next available table number IN THE SAME ZONE
  const getNextAvailableTableNumber = (zone = '') => {
    const usedNumbers = new Set();
    canvasObjects.forEach(obj => {
      // Only consider tables in the same zone
      const objZone = obj.properties?.zone || '';
      if (objZone !== zone) return;
      
      if (obj.type === 'round_table' || obj.type === 'rect_table') {
        const num = parseInt(obj.label.replace(/[^0-9]/g, '')) || 0;
        usedNumbers.add(num);
      }
    });
    let num = 1;
    while (usedNumbers.has(num)) num++;
    return num;
  };

  // Check if zone/section name is duplicate
  const isZoneNameDuplicate = (name, excludeObjectId) => {
    return canvasObjects.some(obj => {
      if (obj.id === excludeObjectId) return false;
      if (obj.type === 'ga_zone' || obj.type === 'vip_pod' || obj.type === 'zone') {
        return obj.label === name;
      }
      return false;
    });
  };

  // Get list of available zones
  const getAvailableZones = () => {
    return canvasObjects
      .filter(obj => obj.type === 'ga_zone' || obj.type === 'vip_pod' || obj.type === 'zone')
      .map(obj => obj.label);
  };

  // Get next available single seat number (SS1, SS2, etc.) IN THE SAME ZONE
  const getNextAvailableSingleSeatNumber = (zone = '') => {
    const usedNumbers = new Set();
    canvasObjects.forEach(obj => {
      // Only consider single seats in the same zone
      const objZone = obj.properties?.zone || '';
      if (objZone !== zone) return;
      
      if (obj.type === 'seat') {
        const num = parseInt(obj.label.replace(/[^0-9]/g, '')) || 0;
        usedNumbers.add(num);
      }
    });
    let num = 1;
    while (usedNumbers.has(num)) num++;
    return num;
  };

  // Check if single seat name is duplicate IN THE SAME ZONE
  const isSingleSeatNameDuplicate = (name, excludeObjectId, zone = '') => {
    return canvasObjects.some(obj => {
      if (obj.id === excludeObjectId) return false;
      const objZone = obj.properties?.zone || '';
      if (objZone !== zone) return false;
      
      if (obj.type === 'seat') {
        return obj.label === name;
      }
      return false;
    });
  };

  // Get next available sofa number (S1, S2, etc.) IN THE SAME ZONE
  const getNextAvailableSofaNumber = (zone = '') => {
    const usedNumbers = new Set();
    canvasObjects.forEach(obj => {
      // Only consider sofas in the same zone
      const objZone = obj.properties?.zone || '';
      if (objZone !== zone) return;
      
      if (obj.type === 'sofa') {
        const num = parseInt(obj.label.replace(/[^0-9]/g, '')) || 0;
        usedNumbers.add(num);
      }
    });
    let num = 1;
    while (usedNumbers.has(num)) num++;
    return num;
  };

  // Check if sofa name is duplicate IN THE SAME ZONE
  const isSofaNameDuplicate = (name, excludeObjectId, zone = '') => {
    return canvasObjects.some(obj => {
      if (obj.id === excludeObjectId) return false;
      const objZone = obj.properties?.zone || '';
      if (objZone !== zone) return false;
      
      if (obj.type === 'sofa') {
        return obj.label === name;
      }
      return false;
    });
  };

  // Get next available zone number
  const getNextAvailableZoneNumber = () => {
    const usedNumbers = new Set();
    canvasObjects.forEach(obj => {
      if (obj.type === 'zone') {
        const num = parseInt(obj.label.replace(/[^0-9]/g, '')) || 0;
        usedNumbers.add(num);
      }
    });
    let num = 1;
    while (usedNumbers.has(num)) num++;
    return num;
  };

  // Detect overlapping objects - returns array of overlap warnings
  const detectOverlaps = () => {
    const warnings = [];
    const seatingObjects = canvasObjects.filter(obj => 
      ['single_row', 'multiple_rows', 'round_table', 'rect_table', 'seat', 'sofa'].includes(obj.type)
    );
    
    for (let i = 0; i < seatingObjects.length; i++) {
      for (let j = i + 1; j < seatingObjects.length; j++) {
        const objA = seatingObjects[i];
        const objB = seatingObjects[j];
        
        // Skip overlap check for rows that are clearly stacked vertically (adjacent rows)
        const isRowA = objA.type === 'single_row' || objA.type === 'multiple_rows';
        const isRowB = objB.type === 'single_row' || objB.type === 'multiple_rows';
        
        // If both are rows, use much larger tolerance for curved rows
        let verticalTolerance = 5;
        let horizontalTolerance = 5;
        
        if (isRowA && isRowB) {
          const curveA = Math.abs(objA.properties?.curve_amount || 0);
          const curveB = Math.abs(objB.properties?.curve_amount || 0);
          const maxCurve = Math.max(curveA, curveB);
          
          // Calculate the actual visual height addition from the curve
          // Curved rows visually extend beyond their bounding box
          const curveHeight = maxCurve * 0.4; // Approximate curve height in pixels
          
          // Significantly increase tolerance for curved rows
          verticalTolerance = 15 + curveHeight;
          
          // Also check if rows are simply stacked vertically (same or similar X position)
          const xOverlap = Math.abs(objA.x - objB.x) < 50; // Within 50px horizontal alignment
          const verticalDistance = Math.abs(objA.y - objB.y);
          const expectedRowSpacing = 40; // Typical row height
          
          // If rows are aligned horizontally and vertically spaced normally, skip overlap check
          if (xOverlap && verticalDistance >= expectedRowSpacing * 0.7 && verticalDistance <= expectedRowSpacing * 2) {
            continue; // Skip this pair - they're just adjacent rows
          }
        }
        
        // Simple bounding box collision
        const aLeft = objA.x;
        const aRight = objA.x + objA.width;
        const aTop = objA.y;
        const aBottom = objA.y + objA.height;
        
        const bLeft = objB.x;
        const bRight = objB.x + objB.width;
        const bTop = objB.y;
        const bBottom = objB.y + objB.height;
        
        // Check overlap with tolerance (objects touching is OK)
        const overlapX = aRight > bLeft + horizontalTolerance && aLeft < bRight - horizontalTolerance;
        const overlapY = aBottom > bTop + verticalTolerance && aTop < bBottom - verticalTolerance;
        
        if (overlapX && overlapY) {
          warnings.push({
            objectA: objA.id,
            objectB: objB.id,
            labelA: objA.label,
            labelB: objB.label
          });
        }
      }
    }
    
    return warnings;
  };

  // Auto-resize zone to fit contents
  const autoResizeZone = useCallback((zoneId, objects = canvasObjects) => {
    const zone = objects.find(obj => obj.id === zoneId);
    if (!zone || zone.type !== 'zone') return objects;
    
    // Find all objects inside this zone
    const childObjects = objects.filter(obj => obj.properties?.zone === zone.label);
    
    if (childObjects.length === 0) return objects;
    
    // Calculate bounding box of all children
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    childObjects.forEach(obj => {
      minX = Math.min(minX, obj.x);
      minY = Math.min(minY, obj.y);
      maxX = Math.max(maxX, obj.x + obj.width);
      maxY = Math.max(maxY, obj.y + obj.height);
    });
    
    // Add padding
    const padding = 30;
    const newWidth = Math.max(200, (maxX - minX) + padding * 2);
    const newHeight = Math.max(150, (maxY - minY) + padding * 2 + 30); // Extra 30 for label
    
    // Check if zone needs resize (to avoid unnecessary updates)
    const needsResize = 
      zone.x !== minX - padding ||
      zone.y !== minY - padding - 20 ||
      zone.width !== newWidth ||
      zone.height !== newHeight;
    
    if (!needsResize) return objects;
    
    // Update zone position and size
    return objects.map(obj => {
      if (obj.id === zoneId) {
        return {
          ...obj,
          x: minX - padding,
          y: minY - padding - 20, // Account for label space at top
          width: newWidth,
          height: newHeight
        };
      }
      return obj;
    });
  }, [canvasObjects]);
  
  // Auto-resize all zones that contain objects
  const autoResizeAllZones = useCallback((objects) => {
    let updatedObjects = [...objects];
    const zones = objects.filter(obj => obj.type === 'zone');
    
    zones.forEach(zone => {
      updatedObjects = autoResizeZone(zone.id, updatedObjects);
    });
    
    return updatedObjects;
  }, [autoResizeZone]);

  // Validate and warn about overlaps on save
  const validateLayout = () => {
    const overlaps = detectOverlaps();
    if (overlaps.length > 0) {
      setOverlapWarnings(overlaps);
      return false;
    }
    setOverlapWarnings([]);
    return true;
  };

  // Seating object types that are incompatible with bus layout mode
  const SEATING_OBJECT_TYPES = ['single_row', 'multiple_rows', 'round_table', 'rect_table', 'seat', 'sofa', 'zone', 'ga_zone', 'vip_pod', 'screen', 'stage', 'stage_area'];
  
  // Check if canvas has existing seating objects
  const hasExistingSeatingObjects = () => {
    return canvasObjects.some(obj => SEATING_OBJECT_TYPES.includes(obj.type));
  };
  
  // Check if canvas is in bus layout mode
  const isInBusLayoutMode = () => {
    return canvasObjects.some(obj => obj.type === 'bus_layout');
  };
  
  // Handle adding bus layout with exclusive mode warning
  const handleAddBusLayout = (toolConfig) => {
    // Include SCREEN in the objects to be removed for bus layouts
    const objectsToRemove = canvasObjects.filter(obj => 
      SEATING_OBJECT_TYPES.includes(obj.type) || obj.type === 'screen'
    );
    
    if (objectsToRemove.length > 0) {
      // Store remaining objects (not IDs) to pass to addObject
      const remainingObjects = canvasObjects.filter(obj => 
        !SEATING_OBJECT_TYPES.includes(obj.type) && obj.type !== 'screen'
      );
      
      setBusLayoutWarning({
        message: `Bus layout is exclusive and cannot coexist with other seating objects.\n\n${objectsToRemove.length} object(s) will be removed:\n• ${objectsToRemove.map(o => o.label || o.type).slice(0, 5).join('\n• ')}${objectsToRemove.length > 5 ? `\n• ...and ${objectsToRemove.length - 5} more` : ''}`,
        onConfirm: () => {
          setBusLayoutMode(true);
          setBusLayoutWarning(null);
          // Add bus layout to the remaining objects directly
          addBusLayoutToObjects(toolConfig, remainingObjects);
        },
        onCancel: () => {
          setBusLayoutWarning(null);
        }
      });
    } else {
      setBusLayoutMode(true);
      actuallyAddObject('bus_layout', toolConfig);
    }
  };
  
  // Add bus layout with a specific starting array of objects
  const addBusLayoutToObjects = (toolConfig, startingObjects) => {
    const rowCount = 9;
    const seatPattern = '2x2';
    const seatWidth = 40;
    const seatHeight = 40;
    const seatSpacing = 6;
    const rowSpacing = 48;
    const aisleWidth = 35;
    const [leftSeats, rightSeats] = seatPattern.split('x').map(Number);
    
    // Position bus layout closer to top-left on mobile for better visibility
    const isMobile = window.innerWidth < 768;
    const startX = isMobile ? 20 : 400;
    const startY = isMobile ? 20 : 200;
    
    const newObject = {
      id: `obj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'bus_layout',
      label: `Bus ${startingObjects.filter(o => o.type === 'bus_layout').length + 1}`,
      x: startX,
      y: startY,
      width: leftSeats * (seatWidth + seatSpacing) + aisleWidth + rightSeats * (seatWidth + seatSpacing) - seatSpacing,
      height: rowCount * rowSpacing + 70 + 30,
      properties: { 
        row_count: rowCount, 
        seat_pattern: seatPattern,
        left_seats: leftSeats,
        right_seats: rightSeats,
        front_at_top: true,
        zone: '',
        has_driver_seat: true,
        seat_color: '#22c55e',
        is_bus_mode: true
      }
    };
    
    const seats = [];
    for (let row = 0; row < rowCount; row++) {
      const rowLabel = row + 1;
      let seatIndex = 0;
      
      for (let i = 0; i < leftSeats; i++) {
        const seatLetter = String.fromCharCode(65 + seatIndex);
        seats.push({
          id: `row${row}_seat_${seatIndex}`,
          row: row,
          seat_number: seatIndex + 1,
          seat_label: `${rowLabel}${seatLetter}`,
          x: i * (seatWidth + seatSpacing),
          y: row * rowSpacing + 70,
          side: 'left',
          status: 'available',
          bookable: true,
          group_id: null
        });
        seatIndex++;
      }
      
      for (let i = 0; i < rightSeats; i++) {
        const seatLetter = String.fromCharCode(65 + seatIndex);
        seats.push({
          id: `row${row}_seat_${seatIndex}`,
          row: row,
          seat_number: seatIndex + 1,
          seat_label: `${rowLabel}${seatLetter}`,
          x: leftSeats * (seatWidth + seatSpacing) + aisleWidth + i * (seatWidth + seatSpacing),
          y: row * rowSpacing + 70,
          side: 'right',
          status: 'available',
          bookable: true,
          group_id: null
        });
        seatIndex++;
      }
    }
    
    newObject.seats = seats;
    
    const newObjects = [...startingObjects, newObject];
    setCanvasObjects(newObjects);
    addToHistory(newObjects);
    setSelectedObject(newObject.id);
    setSelectedObjects([newObject.id]);
    
    // Auto-zoom to fit bus layout on mobile screens
    if (isMobile) {
      // Calculate optimal zoom to fit bus layout on screen
      const busWidth = newObject.width;
      const busHeight = newObject.height;
      const viewportWidth = window.innerWidth - 60; // Account for padding
      const viewportHeight = window.innerHeight - 200; // Account for toolbars
      
      const zoomToFitWidth = viewportWidth / (busWidth + 100);
      const zoomToFitHeight = viewportHeight / (busHeight + 100);
      const optimalZoom = Math.min(zoomToFitWidth, zoomToFitHeight, 0.8);
      
      setZoom(Math.max(0.5, Math.min(optimalZoom, 1)));
    }
  };
  
  // Handle exiting bus layout mode
  const handleExitBusLayoutMode = (type, toolConfig) => {
    setBusLayoutWarning({
      message: 'Adding this object will remove the Bus Layout.\n\nBus layout is exclusive and cannot coexist with other seating objects.',
      onConfirm: () => {
        // Remove bus layout and bus features
        const nonBusObjects = canvasObjects.filter(obj => 
          obj.type !== 'bus_layout' && !obj.type.startsWith('bus_')
        );
        setCanvasObjects(nonBusObjects);
        setBusLayoutMode(false);
        setBusLayoutWarning(null);
        // Now add the new object
        setTimeout(() => actuallyAddObject(type, toolConfig), 100);
      },
      onCancel: () => {
        setBusLayoutWarning(null);
      }
    });
  };

  const addObject = (type, toolConfig) => {
    // Handle bus layout exclusive mode
    if (type === 'bus_layout') {
      handleAddBusLayout(toolConfig);
      return;
    }
    
    // Check if we're in bus layout mode and trying to add a seating object
    if (isInBusLayoutMode() && SEATING_OBJECT_TYPES.includes(type)) {
      handleExitBusLayoutMode(type, toolConfig);
      return;
    }
    
    actuallyAddObject(type, toolConfig);
  };
  
  const actuallyAddObject = (type, toolConfig) => {
    // Calculate smart positioning for new objects
    let newX = 250 + (canvasObjects.length * 30) % 400;
    let newY = 200 + (canvasObjects.length * 30) % 300;
    
    // For rows, position below the last row of the same type
    if (type === 'single_row' || type === 'multiple_rows') {
      const existingRows = canvasObjects.filter(o => o.type === 'single_row' || o.type === 'multiple_rows');
      if (existingRows.length > 0) {
        // Find the last row added and position below it
        const lastRow = existingRows[existingRows.length - 1];
        newX = lastRow.x; // Align X with last row
        newY = lastRow.y + lastRow.height + 10; // Position below with 10px gap
      }
    }
    
    const newObject = {
      id: `obj_${Date.now()}`,
      type,
      x: newX,
      y: newY,
      width: toolConfig.width,
      height: toolConfig.height,
      rotation: 0,
      color: toolConfig.color,
      label: `${toolConfig.label} ${canvasObjects.filter(o => o.type === type).length + 1}`,
      properties: {},
      seats: []
    };

    if (type === 'single_row') {
      const nextLabel = getNextAvailableRowLabel();
      newObject.properties = { seat_count: 10, row_label: nextLabel, spacing: 8, curve_amount: 0, section_gap: 0, section_after: 0, zone: '' };
      const seats = [];
      for (let i = 0; i < 10; i++) {
        seats.push({
          id: `seat_${i}`,
          seat_number: i + 1,
          x: i * (36 + 8),
          y: 0,
          bookable: true
        });
      }
      newObject.seats = seats;
    } else if (type === 'multiple_rows') {
      const rowCount = 3;
      const seatCount = 10;
      const spacing = 8;
      const rowSpacing = 50;
      const nextLabel = getNextAvailableMultiRowStartLabel(rowCount);
      newObject.properties = { row_count: rowCount, seat_count: seatCount, start_label: nextLabel, spacing: spacing, curve_amount: 0, section_gap: 0, section_after: 0, zone: '' };
      const seats = [];
      for (let row = 0; row < rowCount; row++) {
        for (let i = 0; i < seatCount; i++) {
          seats.push({
            id: `row${row}_seat_${i}`,
            row: row,
            seat_number: i + 1,
            x: i * (36 + spacing),
            y: row * rowSpacing,
            bookable: true
          });
        }
      }
      newObject.seats = seats;
      // Calculate actual dimensions based on seats
      newObject.width = seatCount * (36 + spacing);
      newObject.height = rowCount * rowSpacing;
    } else if (type === 'bus_layout') {
      // Bus Layout - default 9 rows with 2x2 configuration = 36 seats total
      // This is the standard bus configuration (not 36 rows)
      const rowCount = 9; // 9 rows × 4 seats = 36 total seats
      const seatPattern = '2x2'; // Default pattern: 2 seats left, aisle, 2 seats right
      const seatWidth = 40;
      const seatHeight = 40;
      const seatSpacing = 6;
      const rowSpacing = 48;
      const aisleWidth = 35;
      
      // Parse pattern (e.g., "2x2" = 2 left + 2 right, "2x3" = 2 left + 3 right)
      const [leftSeats, rightSeats] = seatPattern.split('x').map(Number);
      const seatsPerRow = leftSeats + rightSeats;
      
      newObject.label = `Bus ${canvasObjects.filter(o => o.type === 'bus_layout').length + 1}`;
      newObject.properties = { 
        row_count: rowCount, 
        seat_pattern: seatPattern,
        left_seats: leftSeats,
        right_seats: rightSeats,
        front_at_top: true, // true = front at top, false = front at bottom
        zone: '',
        has_driver_seat: true,
        seat_color: '#22c55e', // Green for available seats (flight-style)
        is_bus_mode: true // Flag to indicate exclusive bus mode
      };
      
      const seats = [];
      // Generate seats for each row
      for (let row = 0; row < rowCount; row++) {
        const rowLabel = row + 1; // Row numbers start at 1
        let seatIndex = 0;
        
        // Left side seats (A, B for 2x2)
        for (let i = 0; i < leftSeats; i++) {
          const seatLetter = String.fromCharCode(65 + seatIndex); // A, B, C...
          seats.push({
            id: `row${row}_seat_${seatIndex}`,
            row: row,
            seat_number: seatIndex + 1,
            seat_label: `${rowLabel}${seatLetter}`,
            x: i * (seatWidth + seatSpacing),
            y: row * rowSpacing + 70, // +70 for driver area at top
            side: 'left',
            status: 'available', // available, selected, booked
            bookable: true,
            group_id: null // For group booking
          });
          seatIndex++;
        }
        
        // Right side seats (C, D for 2x2)
        for (let i = 0; i < rightSeats; i++) {
          const seatLetter = String.fromCharCode(65 + seatIndex); // C, D, E...
          seats.push({
            id: `row${row}_seat_${seatIndex}`,
            row: row,
            seat_number: seatIndex + 1,
            seat_label: `${rowLabel}${seatLetter}`,
            x: leftSeats * (seatWidth + seatSpacing) + aisleWidth + i * (seatWidth + seatSpacing),
            y: row * rowSpacing + 70, // +70 for driver area at top
            side: 'right',
            status: 'available',
            bookable: true,
            group_id: null
          });
          seatIndex++;
        }
      }
      
      newObject.seats = seats;
      // Calculate dimensions
      const totalWidth = leftSeats * (seatWidth + seatSpacing) + aisleWidth + rightSeats * (seatWidth + seatSpacing) - seatSpacing;
      const totalHeight = rowCount * rowSpacing + 70 + 30; // +70 for driver area, +30 for padding
      newObject.width = totalWidth;
      newObject.height = totalHeight;
    } else if (type === 'round_table' || type === 'rect_table') {
      const tableNum = getNextAvailableTableNumber();
      newObject.label = `T${tableNum}`;
      newObject.properties = { seats: 4, book_whole_table: false, seat_spacing: 20, zone: '' };
      const seatCount = 4;
      const seats = [];
      const radius = type === 'round_table' ? newObject.width / 2 : Math.min(newObject.width, newObject.height) / 2;
      const seatOffset = radius + 20;
      for (let i = 0; i < seatCount; i++) {
        seats.push({
          id: `seat_${i}`,
          seat_number: i + 1,
          angle: (360 / seatCount) * i,
          offset: seatOffset,
          bookable: true
        });
      }
      newObject.seats = seats;
    } else if (type === 'seat') {
      // Single Seat (sofa style)
      const seatNum = getNextAvailableSingleSeatNumber();
      newObject.label = `SS${seatNum}`;
      newObject.properties = { 
        zone: '', 
        bookable: true, 
        status: 'available',
        seat_color: SEAT_STATUS_COLORS.available 
      };
    } else if (type === 'sofa') {
      // Sofa seating - configurable seater count with enhanced features
      const sofaNum = getNextAvailableSofaNumber();
      newObject.label = `S${sofaNum}`;  // S1, S2, S3 (like T1, T2, T3 for tables)
      const defaultPrefix = 'S';
      newObject.properties = { 
        seater_count: 3,  // Default 3-seater
        seat_label_prefix: defaultPrefix,  // Default prefix for seat labels
        zone: '',
        bookable: true,
        with_table: false,
        with_lights: false,
        table_position: 'front',
        sofa_color: '#a78bfa',
        seat_color: SEAT_STATUS_COLORS.available,
        spacing: 8,
        booking_mode: 'individual' // 'individual' or 'whole'
      };
      // Create seats for sofa with S1-1, S1-2, S1-3 labels (Prefix + SofaNum + SeatNum)
      const seats = [];
      for (let i = 0; i < 3; i++) {
        seats.push({
          id: `seat_${i}`,
          seat_number: i + 1,
          seat_label: `${defaultPrefix}${sofaNum}-${i + 1}`,  // S1-1, S1-2, S1-3
          x: i * 45,
          y: 10,
          bookable: true,
          status: 'available'
        });
      }
      newObject.seats = seats;
    } else if (type === 'zone') {
      // Zone / Section for grouping seats
      const zoneNum = getNextAvailableZoneNumber();
      newObject.label = `Zone ${zoneNum}`;
      newObject.properties = { 
        capacity: 0, 
        icon_color: '#3b82f6', 
        text_color: '#1e40af', 
        text_bold: true, 
        bg_color: '#dbeafe', 
        locked: false,
        border_color: '#3b82f6',
        border_style: 'dashed'
      };
    } else if (type === 'ga_zone' || type === 'vip_pod') {
      const zoneCount = canvasObjects.filter(o => o.type === type).length + 1;
      const zoneName = type === 'ga_zone' ? `GA Zone ${zoneCount}` : `VIP ${zoneCount}`;
      newObject.label = zoneName;
      newObject.properties = { capacity: 20, icon_color: '#ffffff', text_color: '#ffffff', text_bold: true, font_size: 14, text_italic: false, text_underline: false, bg_color: '' };
    } else if (type === 'screen') {
      // Screen is a special object that appears at the top of the layout
      const screenCount = canvasObjects.filter(o => o.type === 'screen').length + 1;
      newObject.label = screenCount === 1 ? 'SCREEN' : `SCREEN ${screenCount}`;
      newObject.y = 50; // Position at top by default
      newObject.x = 150; // Center-ish
      newObject.properties = { icon_color: '#ffffff', text_color: '#ffffff', text_bold: true, font_size: 16, text_italic: false, text_underline: false };
    } else if (type === 'text') {
      newObject.label = 'Custom Text';
      newObject.properties = { 
        text_content: 'Custom Text', 
        font_size: 16, 
        text_color: '#334155', 
        text_bold: false, 
        text_italic: false, 
        text_underline: false, 
        bg_color: '', 
        icon: null 
      };
    } else if (type === 'bus_door_front' || type === 'bus_door_middle' || type === 'bus_door_back') {
      // Bus door objects
      const doorType = type.replace('bus_door_', '');
      newObject.label = `${doorType.charAt(0).toUpperCase() + doorType.slice(1)} Door`;
      newObject.properties = { 
        door_position: doorType,
        is_entry: doorType === 'front',
        is_exit: doorType === 'back' || doorType === 'middle'
      };
    } else if (type === 'bus_toilet') {
      newObject.label = 'Toilet';
      newObject.properties = { 
        position: 'back', // front, middle, back
        is_accessible: false
      };
    } else if (type === 'bus_tv') {
      newObject.label = 'TV Screen';
      newObject.properties = { 
        position: 'front', // front, middle
        content: 'Entertainment'
      };
    } else if (type === 'bus_aisle') {
      newObject.label = 'Extra Aisle';
      newObject.properties = { 
        orientation: 'vertical'
      };
    } else if (['screen', 'stage', 'dj_booth', 'bar', 'toilet', 'entrance', 'exit', 'kitchen', 'wheelchair', 'camera', 'security', 'greenroom', 'backstage', 'foodcourt', 'merchandise'].includes(type)) {
      newObject.properties = { icon_color: '#ffffff', text_color: '#ffffff', text_bold: true, font_size: 12, text_italic: false, text_underline: false };
    } else if (['table_lamp', 'coffee_table'].includes(type)) {
      // Accessories
      newObject.properties = { icon_color: '#1e293b', text_color: '#1e293b', text_bold: false, font_size: 10 };
    }

    let newObjects = [...canvasObjects, newObject];
    
    // If the new object has a zone, auto-resize that zone
    if (newObject.properties?.zone) {
      const targetZone = newObjects.find(z => z.label === newObject.properties.zone && z.type === 'zone');
      if (targetZone) {
        newObjects = autoResizeZone(targetZone.id, newObjects);
      }
    }
    
    setCanvasObjects(newObjects);
    addToHistory(newObjects);
    setSelectedObject(newObject.id);
    setSelectedObjects([newObject.id]);
  };

  // Check if event can be unlinked based on status and bookings
  const canUnlinkEvent = () => {
    if (!eventInfo) return false;
    // Can ONLY unlink if there are NO bookings (all seats available)
    // Regardless of event status - if there are bookings, cannot unlink
    return eventInfo.booked_seats_count === 0;
  };

  // Get human-readable event status
  const getEventStatusDisplay = (status) => {
    const statusMap = {
      'draft': 'Draft',
      'published': 'Published',
      'on_sale': 'On Sale',
      'live': 'Live',
      'cancelled': 'Cancelled',
      'postponed': 'Postponed',
      'sold_out': 'Sold Out',
      'completed': 'Completed'
    };
    return statusMap[status] || status;
  };

  // Unlink layout from event
  const unlinkFromEvent = async () => {
    if (!layoutId || !eventInfo) return;
    
    try {
      await axios.delete(`${API}/admin/layouts/${layoutId}/unlink-event`);
      showSuccessMessage({ type: 'success', text: `Layout unlinked from "${eventInfo.event_name}" successfully!` });
      setLayoutAssigned(false);
      setEventInfo(null);
      setHasBookings(false);
      hideConfirmDialog();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to unlink layout';
      showSuccessMessage({ type: 'error', text: errorMsg });
    }
  };

  // Show confirmation dialog with optional unlink
  const showConfirmDialog = (action, count, message, onConfirm, options = {}) => {
    setConfirmDialog({ action, count, message, onConfirm, ...options });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog(null);
  };

  // Show linked event warning with unlink option
  const showLinkedEventWarning = (actionDescription) => {
    if (!eventInfo) return;
    
    const status = getEventStatusDisplay(eventInfo.event_status);
    const bookedCount = eventInfo.booked_seats_count || 0;
    const canUnlink = canUnlinkEvent();
    
    let message = `This layout is linked to event "${eventInfo.event_name}" (Status: ${status}).`;
    
    if (bookedCount > 0) {
      message += `\n\n⚠️ ${bookedCount} ticket(s) are booked. All bookings must be refunded/cancelled before unlinking.`;
    } else {
      message += `\n\nNo tickets booked. You can unlink this layout from the event.`;
    }
    
    showConfirmDialog(
      'linked_warning',
      0,
      message,
      canUnlink ? unlinkFromEvent : null,
      { 
        showUnlink: true, 
        canUnlink,
        eventName: eventInfo.event_name,
        eventStatus: status,
        bookedCount 
      }
    );
  };

  // Confirm and delete multiple selected objects
  const confirmDeleteSelectedObjects = () => {
    if (selectedObjects.length > 0) {
      if (layoutAssigned) {
        showLinkedEventWarning('delete objects');
        return;
      }
      showConfirmDialog(
        'delete',
        selectedObjects.length,
        `Are you sure you want to delete ${selectedObjects.length} object(s)? This action cannot be undone.`,
        executeDeleteSelectedObjects
      );
    }
  };

  const executeDeleteSelectedObjects = () => {
    if (selectedObjects.length > 0) {
      if (layoutAssigned) {
        showLinkedEventWarning('delete objects');
        hideConfirmDialog();
        return;
      }
      const newObjects = canvasObjects.filter(obj => !selectedObjects.includes(obj.id));
      setCanvasObjects(newObjects);
      addToHistory(newObjects);
      setSelectedObject(null);
      setSelectedObjects([]);
      showSuccessMessage({ type: 'success', text: `Deleted ${selectedObjects.length} object(s)` });
      hideConfirmDialog();
    }
  };

  // Delete multiple selected objects (keeping original for keyboard shortcut)
  const deleteSelectedObjects = () => {
    if (selectedObjects.length > 0) {
      if (layoutAssigned) {
        showLinkedEventWarning('delete objects');
        return;
      }
      const newObjects = canvasObjects.filter(obj => !selectedObjects.includes(obj.id));
      setCanvasObjects(newObjects);
      addToHistory(newObjects);
      setSelectedObject(null);
      setSelectedObjects([]);
      showSuccessMessage({ type: 'success', text: `Deleted ${selectedObjects.length} object(s)` });
    }
  };

  // Confirm and duplicate multiple selected objects
  const confirmDuplicateSelectedObjects = () => {
    if (selectedObjects.length > 0) {
      showConfirmDialog(
        'duplicate',
        selectedObjects.length,
        `Are you sure you want to duplicate ${selectedObjects.length} object(s)?`,
        executeDuplicateSelectedObjects
      );
    }
  };

  const executeDuplicateSelectedObjects = () => {
    if (selectedObjects.length > 0) {
      const newObjs = [];
      let offset = 30;
      
      selectedObjects.forEach(objId => {
        const obj = canvasObjects.find(o => o.id === objId);
        if (obj) {
          let newLabel = obj.label;
          if (obj.type === 'single_row') {
            newLabel = getNextAvailableRowLabel();
          } else if (obj.type === 'round_table' || obj.type === 'rect_table') {
            newLabel = `T${getNextAvailableTableNumber()}`;
          } else if (obj.type === 'seat') {
            newLabel = `SS${getNextAvailableSingleSeatNumber()}`;
          } else if (obj.type === 'zone') {
            newLabel = `Zone ${getNextAvailableZoneNumber()}`;
          } else if (obj.type === 'sofa') {
            newLabel = `S${getNextAvailableSofaNumber()}`;
          }
          
          const newObj = {
            ...JSON.parse(JSON.stringify(obj)),
            id: `obj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            label: newLabel,
            x: obj.x + offset,
            y: obj.y + offset
          };
          
          if (obj.type === 'single_row' && newObj.properties) {
            newObj.properties.row_label = newLabel;
          } else if (obj.type === 'sofa' && newObj.seats) {
            const prefix = newObj.properties?.seat_label_prefix || 'S';
            const sofaNum = newLabel?.replace(/\D/g, '') || '1';
            newObj.seats = newObj.seats.map((seat, idx) => ({
              ...seat,
              id: `seat_${idx}`,
              seat_number: idx + 1,
              seat_label: `${prefix}${sofaNum}-${idx + 1}`
            }));
          }
          
          newObjs.push(newObj);
          offset += 10;
        }
      });
      
      const newObjects = [...canvasObjects, ...newObjs];
      setCanvasObjects(newObjects);
      addToHistory(newObjects);
      setSelectedObjects(newObjs.map(o => o.id));
      showSuccessMessage({ type: 'success', text: `Duplicated ${newObjs.length} object(s)` });
      hideConfirmDialog();
    }
  };

  // Keep original duplicateSelectedObjects for keyboard shortcut (no confirm)
  const duplicateSelectedObjects = () => {
    if (selectedObjects.length > 0) {
      const newObjs = [];
      let offset = 30;
      
      selectedObjects.forEach(objId => {
        const obj = canvasObjects.find(o => o.id === objId);
        if (obj) {
          let newLabel = obj.label;
          if (obj.type === 'single_row') {
            newLabel = getNextAvailableRowLabel();
          } else if (obj.type === 'round_table' || obj.type === 'rect_table') {
            newLabel = `T${getNextAvailableTableNumber()}`;
          } else if (obj.type === 'seat') {
            newLabel = `SS${getNextAvailableSingleSeatNumber()}`;
          } else if (obj.type === 'zone') {
            newLabel = `Zone ${getNextAvailableZoneNumber()}`;
          } else if (obj.type === 'sofa') {
            newLabel = `S${getNextAvailableSofaNumber()}`;
          }
          
          const newObj = {
            ...JSON.parse(JSON.stringify(obj)),
            id: `obj_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            label: newLabel,
            x: obj.x + offset,
            y: obj.y + offset
          };
          
          if (obj.type === 'single_row' && newObj.properties) {
            newObj.properties.row_label = newLabel;
          } else if (obj.type === 'sofa' && newObj.seats) {
            const prefix = newObj.properties?.seat_label_prefix || 'S';
            const sofaNum = newLabel?.replace(/\D/g, '') || '1';
            newObj.seats = newObj.seats.map((seat, idx) => ({
              ...seat,
              id: `seat_${idx}`,
              seat_number: idx + 1,
              seat_label: `${prefix}${sofaNum}-${idx + 1}`
            }));
          }
          
          newObjs.push(newObj);
          offset += 10;
        }
      });
      
      const newObjects = [...canvasObjects, ...newObjs];
      setCanvasObjects(newObjects);
      addToHistory(newObjects);
      setSelectedObjects(newObjs.map(o => o.id));
      showSuccessMessage({ type: 'success', text: `Duplicated ${newObjs.length} object(s)` });
    }
  };

  // Delete a single object by its ID (used for keyboard delete with confirmation)
  const performSingleDelete = (objectId) => {
    if (!objectId) return;
    
    // Check if layout is assigned and prevent deletion
    if (layoutAssigned) {
      showSuccessMessage({ type: 'error', text: 'Cannot delete objects from a layout assigned to an event.' });
      return;
    }
    
    const newObjects = canvasObjects.filter(obj => obj.id !== objectId);
    setCanvasObjects(newObjects);
    addToHistory(newObjects);
    setSelectedObject(null);
    setSelectedObjects([]);
    showSuccessMessage({ type: 'success', text: 'Object deleted' });
  };

  const deleteObject = () => {
    // If multiple objects selected, delete all
    if (selectedObjects.length > 1) {
      deleteSelectedObjects();
      return;
    }
    
    if (selectedObject) {
      // Check if layout is assigned and prevent deletion
      if (layoutAssigned) {
        showSuccessMessage({ type: 'error', text: 'Cannot delete objects from a layout assigned to an event.' });
        return;
      }
      const newObjects = canvasObjects.filter(obj => obj.id !== selectedObject);
      setCanvasObjects(newObjects);
      addToHistory(newObjects);
      setSelectedObject(null);
      setSelectedObjects([]);
    }
  };

  const duplicateObject = () => {
    // If multiple objects selected, duplicate all
    if (selectedObjects.length > 1) {
      duplicateSelectedObjects();
      return;
    }
    
    if (selectedObject) {
      const obj = canvasObjects.find(o => o.id === selectedObject);
      if (obj) {
        // Handle duplicate seat numbering
        let newLabel = obj.label;
        if (obj.type === 'single_row') {
          newLabel = getNextAvailableRowLabel();
        } else if (obj.type === 'round_table' || obj.type === 'rect_table') {
          newLabel = `T${getNextAvailableTableNumber()}`;
        } else if (obj.type === 'seat') {
          newLabel = `SS${getNextAvailableSingleSeatNumber()}`;
        } else if (obj.type === 'zone') {
          newLabel = `Zone ${getNextAvailableZoneNumber()}`;
        } else if (obj.type === 'sofa') {
          // Get next available sofa number for unique label (S1, S2, etc.)
          newLabel = `S${getNextAvailableSofaNumber()}`;
        }
        
        const newObj = {
          ...JSON.parse(JSON.stringify(obj)),
          id: `obj_${Date.now()}`,
          label: newLabel,
          x: obj.x + 30,
          y: obj.y + 30
        };
        
        // If it's a row type, update the row_label property too
        if (obj.type === 'single_row' && newObj.properties) {
          newObj.properties.row_label = newLabel;
        } else if (obj.type === 'multiple_rows' && newObj.properties) {
          newObj.properties.start_label = getNextAvailableMultiRowStartLabel(newObj.properties.row_count || 3, newObj.properties.zone || '');
        } else if (obj.type === 'sofa' && newObj.seats) {
          // Update seats to use the new prefix from properties and include sofa number
          const prefix = newObj.properties?.seat_label_prefix || 'S';
          const sofaNum = newLabel?.replace(/\D/g, '') || '1';
          newObj.seats = newObj.seats.map((seat, idx) => ({
            ...seat,
            id: `seat_${idx}`,
            seat_number: idx + 1,
            seat_label: `${prefix}${sofaNum}-${idx + 1}`
          }));
        }
        
        const newObjects = [...canvasObjects, newObj];
        setCanvasObjects(newObjects);
        addToHistory(newObjects);
        setSelectedObject(newObj.id);
        setSelectedObjects([newObj.id]);
      }
    }
  };

  // Update multiple selected objects at once
  const updateMultipleObjectsProperty = (key, value) => {
    if (selectedObjects.length === 0) return;
    
    const newObjects = canvasObjects.map(obj => {
      if (selectedObjects.includes(obj.id)) {
        if (key === 'properties') {
          // Merge properties
          const updated = { ...obj, properties: { ...obj.properties, ...value } };
          
          // Handle zone transfer for bulk objects
          if (value.zone !== undefined) {
            const oldZone = obj.properties?.zone || '';
            const newZone = value.zone || '';
            
            if (oldZone !== newZone) {
              // Check for name conflicts in the new zone and auto-rename
              if (obj.type === 'single_row') {
                const rowLabel = obj.properties?.row_label || 'A';
                if (isRowLabelDuplicate(rowLabel, obj.id, newZone)) {
                  const newRowLabel = getNextAvailableRowLabel(newZone);
                  updated.label = newRowLabel;
                  updated.properties = { ...updated.properties, row_label: newRowLabel };
                  showSuccessMessage({ type: 'info', text: `Row renamed to "${newRowLabel}" to avoid conflict in ${newZone || 'default zone'}` });
                }
              } else if (obj.type === 'multiple_rows') {
                const startLabel = obj.properties?.start_label || 'A';
                const rowCount = obj.properties?.row_count || 3;
                const conflicts = wouldMultiRowConflict(startLabel, rowCount, obj.id, newZone);
                if (conflicts.length > 0) {
                  const newStartLabel = getNextAvailableMultiRowStartLabel(rowCount, newZone);
                  updated.properties = { ...updated.properties, start_label: newStartLabel };
                  showSuccessMessage({ type: 'info', text: `Rows renamed from "${startLabel}" to "${newStartLabel}" to avoid conflict in ${newZone || 'default zone'}` });
                }
              } else if (obj.type === 'round_table' || obj.type === 'rect_table') {
                if (isTableNameDuplicate(obj.label, obj.id, newZone)) {
                  const newTableLabel = `T${getNextAvailableTableNumber(newZone)}`;
                  updated.label = newTableLabel;
                  showSuccessMessage({ type: 'info', text: `Table renamed to "${newTableLabel}" to avoid conflict in ${newZone || 'default zone'}` });
                }
              } else if (obj.type === 'seat') {
                if (isSingleSeatNameDuplicate(obj.label, obj.id, newZone)) {
                  const newSeatLabel = `SS${getNextAvailableSingleSeatNumber(newZone)}`;
                  updated.label = newSeatLabel;
                  showSuccessMessage({ type: 'info', text: `Seat renamed to "${newSeatLabel}" to avoid conflict in ${newZone || 'default zone'}` });
                }
              } else if (obj.type === 'sofa') {
                if (isSofaNameDuplicate(obj.label, obj.id, newZone)) {
                  const newSofaLabel = `S${getNextAvailableSofaNumber(newZone)}`;
                  updated.label = newSofaLabel;
                  showSuccessMessage({ type: 'info', text: `Sofa renamed to "${newSofaLabel}" to avoid conflict in ${newZone || 'default zone'}` });
                }
              }
              
              // Visual movement into zone
              const targetZone = canvasObjects.find(z => z.label === newZone && (z.type === 'zone' || z.type === 'ga_zone' || z.type === 'vip_pod'));
              if (targetZone) {
                const zoneX = targetZone.x || 0;
                const zoneY = targetZone.y || 0;
                const zoneW = targetZone.width || 200;
                const zoneH = targetZone.height || 150;
                const objectsInZone = canvasObjects.filter(o => o.properties?.zone === newZone && !selectedObjects.includes(o.id));
                const idx = selectedObjects.indexOf(obj.id);
                const offsetY = (objectsInZone.length + idx) * 50;
                
                updated.x = zoneX + 20;
                updated.y = zoneY + 30 + (offsetY % (zoneH - 60));
              }
            }
          }
          
          // Update seats for rows if spacing/curve changes
          if (['single_row', 'multiple_rows'].includes(obj.type)) {
            const spacing = value.spacing ?? obj.properties?.spacing ?? 8;
            const curve = value.curve_amount ?? obj.properties?.curve_amount ?? 0;
            const count = obj.properties?.seat_count || 10;
            const sectionGap = obj.properties?.section_gap || 0;
            const sectionAfter = obj.properties?.section_after || 0;
            
            if (value.spacing !== undefined || value.curve_amount !== undefined) {
              const seats = [];
              if (obj.type === 'single_row') {
                let xOffset = 0;
                for (let i = 0; i < count; i++) {
                  if (sectionAfter > 0 && i === sectionAfter) xOffset += sectionGap;
                  const centerOffset = (count - 1) / 2;
                  const distanceFromCenter = i - centerOffset;
                  const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                  seats.push({ id: `seat_${i}`, seat_number: i + 1, x: i * (36 + spacing) + xOffset, y: curveY, bookable: true });
                }
                const newWidth = count * (36 + spacing) + (sectionGap || 0);
                const maxCurve = curve !== 0 ? Math.pow((count - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                const newHeight = 36 + maxCurve + 20;
                updated.width = newWidth;
                updated.height = newHeight;
                updated.seats = seats;
              } else if (obj.type === 'multiple_rows') {
                const rowCount = obj.properties?.row_count || 3;
                for (let row = 0; row < rowCount; row++) {
                  let xOffset = 0;
                  for (let i = 0; i < count; i++) {
                    if (sectionAfter > 0 && i === sectionAfter) xOffset += sectionGap;
                    const centerOffset = (count - 1) / 2;
                    const distanceFromCenter = i - centerOffset;
                    const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                    seats.push({ id: `row${row}_seat_${i}`, row, seat_number: i + 1, x: i * (36 + spacing) + xOffset, y: row * 50 + curveY, bookable: true });
                  }
                }
                const newWidth = count * (36 + spacing) + (sectionGap || 0);
                const maxCurve = curve !== 0 ? Math.pow((count - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                const newHeight = rowCount * 50 + maxCurve + 20;
                updated.width = newWidth;
                updated.height = newHeight;
                updated.seats = seats;
              }
            }
          }
          
          return updated;
        }
        return { ...obj, [key]: value };
      }
      return obj;
    });
    
    setCanvasObjects(newObjects);
    addToHistory(newObjects);
  };

  const updateObjectProperty = (key, value) => {
    if (selectedObject) {
      let newObjects = canvasObjects.map(obj => {
        if (obj.id === selectedObject) {
          const updated = { ...obj, [key]: value };
          
          if (key === 'properties' && (obj.type === 'round_table' || obj.type === 'rect_table')) {
            const seatCount = value.seats || 4;
            const seats = [];
            for (let i = 0; i < seatCount; i++) {
              seats.push({
                id: `seat_${i}`,
                seat_number: i + 1,
                angle: (360 / seatCount) * i,
                offset: 45,
                bookable: true
              });
            }
            updated.seats = seats;
          }
          
          // Handle zone transfer - auto-rename if name conflicts in new zone
          if (key === 'properties' && value.zone !== undefined) {
            const oldZone = obj.properties?.zone || '';
            const newZone = value.zone || '';
            
            if (oldZone !== newZone) {
              // Check for name conflicts in the new zone and auto-rename
              if (obj.type === 'single_row') {
                const rowLabel = obj.properties?.row_label || 'A';
                if (isRowLabelDuplicate(rowLabel, obj.id, newZone)) {
                  const newRowLabel = getNextAvailableRowLabel(newZone);
                  updated.label = newRowLabel;
                  updated.properties = { ...updated.properties, row_label: newRowLabel };
                  showSuccessMessage({ type: 'info', text: `Row renamed to "${newRowLabel}" to avoid conflict in ${newZone || 'default zone'}` });
                }
              } else if (obj.type === 'multiple_rows') {
                const startLabel = obj.properties?.start_label || 'A';
                const rowCount = obj.properties?.row_count || 3;
                const conflicts = wouldMultiRowConflict(startLabel, rowCount, obj.id, newZone);
                if (conflicts.length > 0) {
                  const newStartLabel = getNextAvailableMultiRowStartLabel(rowCount, newZone);
                  updated.properties = { ...updated.properties, start_label: newStartLabel };
                  showSuccessMessage({ type: 'info', text: `Rows renamed from "${startLabel}" to "${newStartLabel}" to avoid conflict in ${newZone || 'default zone'}` });
                }
              } else if (obj.type === 'round_table' || obj.type === 'rect_table') {
                if (isTableNameDuplicate(obj.label, obj.id, newZone)) {
                  const newTableLabel = `T${getNextAvailableTableNumber(newZone)}`;
                  updated.label = newTableLabel;
                  showSuccessMessage({ type: 'info', text: `Table renamed to "${newTableLabel}" to avoid conflict in ${newZone || 'default zone'}` });
                }
              } else if (obj.type === 'seat') {
                if (isSingleSeatNameDuplicate(obj.label, obj.id, newZone)) {
                  const newSeatLabel = `SS${getNextAvailableSingleSeatNumber(newZone)}`;
                  updated.label = newSeatLabel;
                  showSuccessMessage({ type: 'info', text: `Seat renamed to "${newSeatLabel}" to avoid conflict in ${newZone || 'default zone'}` });
                }
              } else if (obj.type === 'sofa') {
                if (isSofaNameDuplicate(obj.label, obj.id, newZone)) {
                  const newSofaLabel = `S${getNextAvailableSofaNumber(newZone)}`;
                  updated.label = newSofaLabel;
                  showSuccessMessage({ type: 'info', text: `Sofa renamed to "${newSofaLabel}" to avoid conflict in ${newZone || 'default zone'}` });
                }
              }
              
              // Visual movement into zone - update object position to be inside the zone OR expand zone to fit
              const targetZone = canvasObjects.find(z => z.label === newZone && (z.type === 'zone' || z.type === 'ga_zone' || z.type === 'vip_pod'));
              if (targetZone) {
                // Check if object is already inside the zone
                const objRight = obj.x + obj.width;
                const objBottom = obj.y + obj.height;
                const zoneRight = targetZone.x + targetZone.width;
                const zoneBottom = targetZone.y + targetZone.height;
                
                const isInsideZone = obj.x >= targetZone.x && obj.y >= targetZone.y && 
                                     objRight <= zoneRight && objBottom <= zoneBottom;
                
                if (!isInsideZone) {
                  // Object is outside zone - trigger auto-resize after update
                  setTimeout(() => {
                    const zoneObj = canvasObjects.find(z => z.label === newZone && z.type === 'zone');
                    if (zoneObj) {
                      autoResizeZone(zoneObj.id);
                    }
                  }, 100);
                }
              }
            }
          }
          
          return updated;
        }
        return obj;
      });
      
      // IMPORTANT: When a zone's label is changed, update all child objects that reference this zone
      const selectedObj = canvasObjects.find(obj => obj.id === selectedObject);
      if (key === 'label' && selectedObj && (selectedObj.type === 'zone' || selectedObj.type === 'ga_zone' || selectedObj.type === 'vip_pod')) {
        const oldLabel = selectedObj.label;
        const newLabel = value;
        if (oldLabel !== newLabel) {
          // Update all objects that have properties.zone pointing to the old label
          newObjects = newObjects.map(obj => {
            if (obj.properties?.zone === oldLabel) {
              return {
                ...obj,
                properties: {
                  ...obj.properties,
                  zone: newLabel
                }
              };
            }
            return obj;
          });
        }
      }
      
      // After zone assignment, trigger auto-resize for the target zone
      if (key === 'properties' && value.zone) {
        const targetZone = newObjects.find(z => z.label === value.zone && z.type === 'zone');
        if (targetZone) {
          newObjects = autoResizeZone(targetZone.id, newObjects);
        }
      }
      
      // Also auto-resize all zones in case object positions/sizes changed
      newObjects = autoResizeAllZones(newObjects);
      
      setCanvasObjects(newObjects);
    }
  };

  const handleCanvasMouseDown = (e) => {
    if (!canvasRef.current) return;
    
    // On mobile, collapse side panels when clicking on canvas for more space
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setLeftPanelCollapsed(true);
      setRightPanelCollapsed(true);
    }
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    let clicked = null;
    // Iterate in reverse to get top-most object first, but also consider locked zones
    for (let i = canvasObjects.length - 1; i >= 0; i--) {
      const obj = canvasObjects[i];
      // Skip locked zones when looking for clickable objects
      if (obj.type === 'zone' && obj.properties?.locked) {
        continue;
      }
      
      // For rows, use the actual seats' bounding box for better hit detection
      if (obj.type === 'single_row' || obj.type === 'multiple_rows') {
        // Calculate actual bounds including row labels padding and curve tolerance
        const curveAmount = Math.abs(obj.properties?.curve_amount || 0);
        const padding = 10 + (curveAmount > 0 ? 5 : 0); // Extra padding for curved rows
        const actualWidth = obj.width + padding * 2;
        const actualHeight = obj.height + padding * 2;
        const actualX = obj.x - padding;
        const actualY = obj.y - padding;
        
        if (x >= actualX && x <= actualX + actualWidth && y >= actualY && y <= actualY + actualHeight) {
          clicked = obj;
          break;
        }
      } else {
        if (x >= obj.x && x <= obj.x + obj.width && y >= obj.y && y <= obj.y + obj.height) {
          clicked = obj;
          break;
        }
      }
    }

    if (clicked) {
      // Handle Ctrl+click for multi-select
      if (e.ctrlKey || e.metaKey) {
        setSelectedObjects(prev => {
          if (prev.includes(clicked.id)) {
            // Deselect if already selected
            return prev.filter(id => id !== clicked.id);
          } else {
            // Add to selection
            return [...prev, clicked.id];
          }
        });
        setSelectedObject(clicked.id);
      } else {
        // Check if clicking on an already selected object in a multi-selection
        if (selectedObjects.length > 1 && selectedObjects.includes(clicked.id)) {
          // Start dragging all selected objects
          setDraggedObject(clicked.id);
          setDragOffset({ x: x - clicked.x, y: y - clicked.y });
        } else {
          // Normal click - single select
          setSelectedObjects([clicked.id]);
          setSelectedObject(clicked.id);
          setDraggedObject(clicked.id);
          setDragOffset({ x: x - clicked.x, y: y - clicked.y });
        }
      }
    } else {
      // Only clear selection if NOT holding Ctrl/Cmd
      // This allows Ctrl+clicking on empty space to keep existing selection
      if (!(e.ctrlKey || e.metaKey)) {
        setSelectedObject(null);
        setSelectedObjects([]);
      }
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (!draggedObject || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;
    
    let newX = x - dragOffset.x;
    let newY = y - dragOffset.y;

    if (formData.grid_snap) {
      const gridSize = 20;
      newX = Math.round(newX / gridSize) * gridSize;
      newY = Math.round(newY / gridSize) * gridSize;
    }

    // Real-time collision detection function
    const checkCollisionAt = (objId, testX, testY) => {
      const dragObj = canvasObjects.find(obj => obj.id === objId);
      if (!dragObj) return false;
      
      const seatingTypes = ['single_row', 'multiple_rows', 'round_table', 'rect_table', 'seat', 'sofa'];
      if (!seatingTypes.includes(dragObj.type)) return false;
      
      const otherSeating = canvasObjects.filter(obj => 
        obj.id !== objId && seatingTypes.includes(obj.type)
      );
      
      for (const other of otherSeating) {
        const tolerance = 5;
        const aLeft = testX;
        const aRight = testX + dragObj.width;
        const aTop = testY;
        const aBottom = testY + dragObj.height;
        
        const bLeft = other.x;
        const bRight = other.x + other.width;
        const bTop = other.y;
        const bBottom = other.y + other.height;
        
        const overlapX = aRight > bLeft + tolerance && aLeft < bRight - tolerance;
        const overlapY = aBottom > bTop + tolerance && aTop < bBottom - tolerance;
        
        if (overlapX && overlapY) return true;
      }
      return false;
    };

    // If multiple objects are selected, move all of them together
    if (selectedObjects.length > 1 && selectedObjects.includes(draggedObject)) {
      const draggedObj = canvasObjects.find(obj => obj.id === draggedObject);
      if (draggedObj) {
        const deltaX = newX - draggedObj.x;
        const deltaY = newY - draggedObj.y;
        
        // Check if any selected object would collide
        let hasCollision = false;
        for (const objId of selectedObjects) {
          const obj = canvasObjects.find(o => o.id === objId);
          if (obj && checkCollisionAt(objId, obj.x + deltaX, obj.y + deltaY)) {
            hasCollision = true;
            break;
          }
        }
        
        // Update objects with collision info
        setCanvasObjects(canvasObjects.map(obj => {
          if (selectedObjects.includes(obj.id)) {
            let objNewX = obj.x + deltaX;
            let objNewY = obj.y + deltaY;
            
            if (formData.grid_snap) {
              const gridSize = 20;
              objNewX = Math.round(objNewX / gridSize) * gridSize;
              objNewY = Math.round(objNewY / gridSize) * gridSize;
            }
            
            return { ...obj, x: objNewX, y: objNewY, _hasCollision: hasCollision };
          }
          return obj;
        }));
      }
    } else {
      // Single object drag - check for collision
      const hasCollision = checkCollisionAt(draggedObject, newX, newY);
      
      setCanvasObjects(canvasObjects.map(obj =>
        obj.id === draggedObject ? { ...obj, x: newX, y: newY, _hasCollision: hasCollision } : obj
      ));
    }
  };

  const handleCanvasMouseUp = () => {
    if (draggedObject) {
      // Clear collision flags from all objects
      const cleanedObjects = canvasObjects.map(obj => {
        const { _hasCollision, ...rest } = obj;
        return rest;
      });
      
      // Auto-resize all zones after any drag operation
      const updatedObjects = autoResizeAllZones(cleanedObjects);
      if (JSON.stringify(updatedObjects) !== JSON.stringify(cleanedObjects)) {
        setCanvasObjects(updatedObjects);
      } else {
        setCanvasObjects(cleanedObjects);
      }
      addToHistory(updatedObjects);
      setDraggedObject(null);
    }
  };

  // Touch event handlers for mobile support
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      // Convert touch to mouse-like event
      const mouseEvent = {
        clientX: touch.clientX,
        clientY: touch.clientY,
        ctrlKey: false,
        metaKey: false,
        preventDefault: () => e.preventDefault(),
        stopPropagation: () => e.stopPropagation()
      };
      handleCanvasMouseDown(mouseEvent);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1 && draggedObject) {
      e.preventDefault(); // Only prevent scrolling while dragging an object
      const touch = e.touches[0];
      const mouseEvent = {
        clientX: touch.clientX,
        clientY: touch.clientY
      };
      handleCanvasMouseMove(mouseEvent);
    }
    // Allow scrolling when not dragging an object (touchAction: auto)
  };

  const handleTouchEnd = (e) => {
    handleCanvasMouseUp();
  };

  const renderObject = (obj) => {
    const isSelected = obj.id === selectedObject;
    const isMultiSelected = selectedObjects.includes(obj.id);
    const hasOverlap = overlapWarnings.some(w => w.objectA === obj.id || w.objectB === obj.id);
    const hasLiveCollision = obj._hasCollision === true; // Real-time collision during drag
    const isDark = ['screen', 'stage', 'dj_booth', 'security', 'backstage'].includes(obj.type);
    const isVenue = ['screen', 'stage', 'dj_booth', 'bar', 'toilet', 'entrance', 'exit', 'kitchen', 'wheelchair', 'camera', 'security', 'greenroom', 'backstage', 'foodcourt', 'merchandise', 'table_lamp', 'coffee_table'].includes(obj.type);
    const isRow = ['single_row', 'multiple_rows'].includes(obj.type);
    const isText = obj.type === 'text';
    const isZone = obj.type === 'zone';
    const isSingleSeat = obj.type === 'seat';
    const isSofa = obj.type === 'sofa';
    const isAccessory = ['table_lamp', 'coffee_table'].includes(obj.type);

    // Collision indicator style
    const collisionStyle = hasLiveCollision ? {
      outline: '3px dashed #ef4444',
      outlineOffset: '2px',
      boxShadow: '0 0 20px rgba(239, 68, 68, 0.5)'
    } : {};

    // Get icon component for venue objects
    const getVenueIcon = () => {
      const iconMap = {
        screen: Monitor,
        stage: Theater,
        dj_booth: Mic2,
        bar: Beer,
        toilet: Bath,
        entrance: LogIn,
        exit: LogOut,
        kitchen: ChefHat,
        wheelchair: Accessibility,
        camera: Cctv,
        security: Shield,
        greenroom: Trees,
        backstage: Building,
        foodcourt: UtensilsCrossed,
        merchandise: ShoppingBag,
        table_lamp: Lamp,
        coffee_table: Coffee
      };
      return iconMap[obj.type];
    };

    // Get text icon for text labels
    const getTextIcon = () => {
      const iconName = obj.properties?.icon;
      if (!iconName) return null;
      const iconMap = {
        'Star': Star,
        'Heart': Heart,
        'Warning': AlertTriangle,
        'Info': Info,
        'Zap': Zap,
        'Target': Target,
        'Clock': Clock,
        'Location': MapPin,
        'WiFi': Wifi,
        'Coffee': Coffee,
        'Parking': Car,
        'Music': Music2,
        'Screen': Tv,
        'Phone': Phone,
      };
      return iconMap[iconName];
    };

    const VenueIcon = isVenue ? getVenueIcon() : null;
    const TextIcon = isText ? getTextIcon() : null;
    const iconColor = obj.properties?.icon_color || '#ffffff';
    const textColor = obj.properties?.text_color || (isDark ? '#ffffff' : '#334155');
    const textBold = obj.properties?.text_bold !== false;
    const textItalic = obj.properties?.text_italic || false;
    const textUnderline = obj.properties?.text_underline || false;
    const fontSize = obj.properties?.font_size || (isVenue ? 12 : 16);

    // For single seat objects (sofa-style)
    if (isSingleSeat) {
      // Use neutral color for canvas design view
      const seatColor = CANVAS_SEAT_COLOR;
      
      return (
        <div
          key={obj.id}
          style={{
            position: 'absolute',
            left: `${obj.x}px`,
            top: `${obj.y}px`,
            width: `${obj.width}px`,
            height: `${obj.height}px`,
            backgroundColor: seatColor,
            border: isSelected ? '3px solid #4f46e5' : `2px solid ${CANVAS_SEAT_BORDER}`,
            borderRadius: '8px 8px 4px 4px',
            transform: `rotate(${obj.rotation}deg)`,
            cursor: 'move',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-end',
            paddingBottom: '4px',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '9px',
            fontWeight: 'bold',
            color: '#1e293b',
            boxShadow: isSelected ? '0 4px 12px rgba(79, 70, 229, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
            userSelect: 'none'
          }}
        >
          {/* Sofa back rest */}
          <div style={{
            position: 'absolute',
            top: '0',
            left: '4px',
            right: '4px',
            height: '12px',
            backgroundColor: seatColor,
            borderRadius: '6px 6px 0 0',
            filter: 'brightness(0.9)',
            pointerEvents: 'none'
          }} />
          {/* Sofa icon */}
          <Sofa className="w-5 h-5 mb-1" style={{ pointerEvents: 'none', opacity: 0.5 }} />
          <span style={{ pointerEvents: 'none' }}>{obj.label}</span>
        </div>
      );
    }

    // For sofa objects (multi-seater)
    if (isSofa) {
      const seaterCount = obj.properties?.seater_count || 3;
      const sofaColor = obj.properties?.sofa_color || '#a78bfa';
      const withTable = obj.properties?.with_table || false;
      const tablePosition = obj.properties?.table_position || 'front';
      
      return (
        <div
          key={obj.id}
          style={{
            position: 'absolute',
            left: `${obj.x}px`,
            top: `${obj.y}px`,
            width: `${obj.width}px`,
            height: `${obj.height + (withTable ? 40 : 0)}px`,
            transform: `rotate(${obj.rotation}deg)`,
            cursor: 'move',
            userSelect: 'none'
          }}
        >
          {/* Coffee table in front */}
          {withTable && tablePosition === 'front' && (
            <div style={{
              position: 'absolute',
              top: `${obj.height + 8}px`,
              left: '50%',
              transform: 'translateX(-50%)',
              width: `${Math.min(obj.width * 0.7, 100)}px`,
              height: '28px',
              backgroundColor: '#78716c',
              borderRadius: '4px',
              border: '2px solid #57534e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '8px',
              color: '#fafaf9',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              <Coffee className="w-3 h-3 mr-1" />
              Table
            </div>
          )}
          
          {/* Main sofa body - with curve support */}
          {(() => {
            const curveAmount = obj.properties?.curve_amount || 0;
            // Calculate border radius based on curve
            const baseTL = 12, baseTR = 12, baseBL = 6, baseBR = 6;
            let topRadius, bottomRadius;
            
            if (curveAmount > 0) {
              // Positive curve - seats go down in middle, so sofa curves like a smile
              const curvePercent = Math.abs(curveAmount) / 200;
              topRadius = `${baseTL + curvePercent * 30}px ${baseTR + curvePercent * 30}px`;
              bottomRadius = `${baseBL}px ${baseBR}px`;
            } else if (curveAmount < 0) {
              // Negative curve - seats go up in middle, so sofa curves like a frown
              const curvePercent = Math.abs(curveAmount) / 200;
              topRadius = `${baseTL}px ${baseTR}px`;
              bottomRadius = `${baseBL + curvePercent * 30}px ${baseBR + curvePercent * 30}px`;
            } else {
              topRadius = `${baseTL}px ${baseTR}px`;
              bottomRadius = `${baseBL}px ${baseBR}px`;
            }
            
            return (
          <div style={{
            position: 'relative',
            width: '100%',
            height: `${obj.height}px`,
            backgroundColor: sofaColor,
            border: hasOverlap ? '3px solid #ef4444' : isSelected || isMultiSelected ? '3px solid #4f46e5' : '2px solid #7c3aed',
            borderRadius: `${topRadius} ${bottomRadius}`,
            boxShadow: hasOverlap ? '0 0 20px rgba(239, 68, 68, 0.5)' : isSelected ? '0 8px 25px rgba(79, 70, 229, 0.3)' : '0 4px 12px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }}>
            {/* Sofa back rest */}
            <div style={{
              position: 'absolute',
              top: '0',
              left: '6px',
              right: '6px',
              height: '18px',
              backgroundColor: sofaColor,
              borderRadius: `${topRadius.split(' ')[0]} ${topRadius.split(' ')[1]} 0 0`,
              filter: 'brightness(0.8)',
              pointerEvents: 'none'
            }} />
            
            {/* Armrests */}
            <div style={{
              position: 'absolute',
              left: '0',
              top: '8px',
              bottom: '4px',
              width: '10px',
              backgroundColor: sofaColor,
              filter: 'brightness(0.75)',
              borderRadius: '4px 0 0 4px'
            }} />
            <div style={{
              position: 'absolute',
              right: '0',
              top: '8px',
              bottom: '4px',
              width: '10px',
              backgroundColor: sofaColor,
              filter: 'brightness(0.75)',
              borderRadius: '0 4px 4px 0'
            }} />
            
            {/* Seat cushions - positioned based on seat data (supports curve) */}
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '14px',
              right: '14px',
              bottom: '6px',
              pointerEvents: 'none'
            }}>
              {obj.seats && obj.seats.map((seat, idx) => {
                // Use neutral color for canvas or custom seat color
                const seatBgColor = obj.properties?.seat_color || CANVAS_SEAT_COLOR;
                // Extract sofa number from label (e.g., "S1" -> "1", "S2" -> "2")
                const sofaNum = obj.label?.replace(/\D/g, '') || '1';
                const prefix = obj.properties?.seat_label_prefix || 'S';
                // Format: {Prefix}{SofaNum}-{SeatNum} e.g., G1-1, G1-2 for sofa S1 with prefix G
                const seatLabel = seat.seat_label || `${prefix}${sofaNum}-${seat.seat_number}`;
                const seatWidth = 36;
                const spacing = obj.properties?.spacing || 8;
                
                return (
                  <div
                    key={seat.id}
                    style={{
                      position: 'absolute',
                      left: `${seat.x || (idx * (seatWidth + spacing))}px`,
                      top: `${(seat.y || 10) - 10}px`,
                      width: `${seatWidth}px`,
                      height: '30px',
                      backgroundColor: seatBgColor,
                      borderRadius: '4px',
                      border: `1px solid ${CANVAS_SEAT_BORDER}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '7px',
                      fontWeight: 'bold',
                      color: '#1e293b'
                    }}
                  >
                    {seatLabel}
                  </div>
                );
              })}
            </div>
            
            {/* Label - positioned with background to prevent overlap */}
            <div style={{
              position: 'absolute',
              bottom: '-22px',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '10px',
              fontWeight: 'bold',
              color: '#475569',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              backgroundColor: 'rgba(255,255,255,0.95)',
              padding: '2px 6px',
              borderRadius: '3px',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              zIndex: 10
            }}>
              {obj.label} ({obj.properties?.seater_count || 3} seats)
            </div>
          </div>
            );
          })()}
          
          {/* Resize handles */}
          {isSelected && (
            <>
              <div 
                style={{
                  position: 'absolute',
                  right: '-6px',
                  bottom: withTable ? '34px' : '-6px',
                  width: '10px',
                  height: '10px',
                  backgroundColor: '#4f46e5',
                  borderRadius: '2px',
                  cursor: 'se-resize',
                  border: '2px solid white',
                  zIndex: 10
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleResizeStart(obj.id, 'se', e);
                }}
              />
              <div 
                style={{
                  position: 'absolute',
                  right: '-6px',
                  top: `${obj.height / 2 - 5}px`,
                  width: '10px',
                  height: '10px',
                  backgroundColor: '#4f46e5',
                  borderRadius: '2px',
                  cursor: 'e-resize',
                  border: '2px solid white',
                  zIndex: 10
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleResizeStart(obj.id, 'e', e);
                }}
              />
            </>
          )}
        </div>
      );
    }

    // For zone objects with resize handles
    if (isZone) {
      const labelPos = obj.properties?.label_position || 'top';
      const bgColor = obj.properties?.bg_color || '#dbeafe';
      const borderColor = obj.properties?.border_color || '#3b82f6';
      const borderStyle = obj.properties?.border_style || 'dashed';
      const isLocked = obj.properties?.locked || false;
      
      // Calculate label position
      const getLabelStyle = () => {
        const baseStyle = {
          position: 'absolute',
          backgroundColor: bgColor,
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: `${obj.properties?.font_size || 12}px`,
          fontWeight: textBold ? 'bold' : 'normal',
          fontStyle: textItalic ? 'italic' : 'normal',
          textDecoration: textUnderline ? 'underline' : 'none',
          color: obj.properties?.text_color || '#1e40af',
          whiteSpace: 'nowrap',
          // Make label clickable to select zone when locked
          pointerEvents: isLocked ? 'auto' : 'none',
          cursor: isLocked ? 'pointer' : 'default',
          zIndex: 100
        };
        
        switch (labelPos) {
          case 'top':
            return { ...baseStyle, top: '-28px', left: '50%', transform: 'translateX(-50%)' };
          case 'top-inside':
            return { ...baseStyle, top: '8px', left: '50%', transform: 'translateX(-50%)' };
          case 'bottom':
            return { ...baseStyle, bottom: '-28px', left: '50%', transform: 'translateX(-50%)' };
          case 'bottom-inside':
            return { ...baseStyle, bottom: '8px', left: '50%', transform: 'translateX(-50%)' };
          case 'left':
            return { ...baseStyle, top: '50%', left: '-8px', transform: 'translateY(-50%) translateX(-100%)' };
          case 'right':
            return { ...baseStyle, top: '50%', right: '-8px', transform: 'translateY(-50%) translateX(100%)' };
          case 'center':
            return { ...baseStyle, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
          case 'corner-top-left':
            return { ...baseStyle, top: '8px', left: '8px' };
          case 'corner-top-right':
            return { ...baseStyle, top: '8px', right: '8px' };
          case 'corner-bottom-left':
            return { ...baseStyle, bottom: '8px', left: '8px' };
          case 'corner-bottom-right':
            return { ...baseStyle, bottom: '8px', right: '8px' };
          default:
            return { ...baseStyle, top: '-28px', left: '50%', transform: 'translateX(-50%)' };
        }
      };
      
      return (
        <div
          key={obj.id}
          style={{
            position: 'absolute',
            left: `${obj.x}px`,
            top: `${obj.y}px`,
            width: `${obj.width}px`,
            height: `${obj.height}px`,
            backgroundColor: `${bgColor}80`,
            border: isSelected ? `3px solid ${borderColor}` : `2px ${borderStyle} ${borderColor}`,
            borderRadius: '8px',
            transform: `rotate(${obj.rotation}deg)`,
            cursor: isLocked ? 'default' : 'move',
            userSelect: 'none',
            boxShadow: isSelected ? '0 8px 30px rgba(59, 130, 246, 0.3)' : 'none',
            zIndex: 0,  // Ensure zones are in background
            pointerEvents: isLocked && !isSelected ? 'none' : 'auto'  // Pass through clicks when locked
          }}
        >
          {/* Zone Label - Clickable when zone is locked to allow selection */}
          <div 
            style={getLabelStyle()}
            onClick={(e) => {
              if (isLocked) {
                e.stopPropagation();
                setSelectedObject(obj.id);
                setSelectedObjects([obj.id]);
              }
            }}
            title={isLocked ? 'Click to select zone' : ''}
          >
            {obj.label}
          </div>
          
          {/* Resize handles - only show when selected */}
          {isSelected && (
            <>
              <div 
                className="resize-handle resize-se"
                style={{
                  position: 'absolute',
                  right: '-6px',
                  bottom: '-6px',
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#3b82f6',
                  borderRadius: '2px',
                  cursor: 'se-resize',
                  border: '2px solid white'
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleResizeStart(obj.id, 'se', e);
                }}
              />
              <div 
                className="resize-handle resize-e"
                style={{
                  position: 'absolute',
                  right: '-6px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#3b82f6',
                  borderRadius: '2px',
                  cursor: 'e-resize',
                  border: '2px solid white'
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleResizeStart(obj.id, 'e', e);
                }}
              />
              <div 
                className="resize-handle resize-s"
                style={{
                  position: 'absolute',
                  bottom: '-6px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#3b82f6',
                  borderRadius: '2px',
                  cursor: 's-resize',
                  border: '2px solid white'
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleResizeStart(obj.id, 's', e);
                }}
              />
            </>
          )}
        </div>
      );
    }

    // For text objects with icon and styling - supports double-click editing
    if (isText) {
      const bgColor = obj.properties?.bg_color;
      const fontFamily = obj.properties?.font_family || 'Inter, sans-serif';
      const isEditing = editingTextId === obj.id;
      
      return (
        <div
          key={obj.id}
          style={{
            position: 'absolute',
            left: `${obj.x}px`,
            top: `${obj.y}px`,
            width: `${obj.width}px`,
            height: `${obj.height}px`,
            backgroundColor: bgColor || 'transparent',
            border: isSelected ? '2px dashed #4f46e5' : '1px dashed #94a3b8',
            borderRadius: '4px',
            transform: `rotate(${obj.rotation}deg)`,
            cursor: isEditing ? 'text' : 'move',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            fontFamily: fontFamily,
            fontSize: `${obj.properties?.font_size || 16}px`,
            fontWeight: obj.properties?.text_bold ? 'bold' : 'normal',
            fontStyle: obj.properties?.text_italic ? 'italic' : 'normal',
            textDecoration: obj.properties?.text_underline ? 'underline' : 'none',
            color: obj.properties?.text_color || '#334155',
            userSelect: isEditing ? 'text' : 'none',
            boxShadow: isSelected ? '0 4px 12px rgba(79, 70, 229, 0.2)' : 'none'
          }}
          onDoubleClick={(e) => {
            e.stopPropagation();
            setEditingTextId(obj.id);
          }}
        >
          {TextIcon && <TextIcon className="w-4 h-4" style={{ pointerEvents: 'none' }} />}
          {isEditing ? (
            <input
              type="text"
              autoFocus
              value={obj.properties?.text_content || 'Custom Text'}
              onChange={(e) => {
                const newObjects = canvasObjects.map(o => 
                  o.id === obj.id 
                    ? { ...o, properties: { ...o.properties, text_content: e.target.value } }
                    : o
                );
                setCanvasObjects(newObjects);
              }}
              onBlur={() => {
                setEditingTextId(null);
                addToHistory(canvasObjects);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                  setEditingTextId(null);
                  addToHistory(canvasObjects);
                }
              }}
              style={{
                width: '100%',
                height: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                textAlign: 'center',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                fontStyle: 'inherit',
                textDecoration: 'inherit',
                color: 'inherit'
              }}
            />
          ) : (
            <span style={{ pointerEvents: 'none' }}>{obj.properties?.text_content || obj.label}</span>
          )}
          
          {/* Resize handles for text */}
          {isSelected && !isEditing && (
            <>
              <div 
                style={{
                  position: 'absolute',
                  right: '-6px',
                  bottom: '-6px',
                  width: '10px',
                  height: '10px',
                  backgroundColor: '#4f46e5',
                  borderRadius: '2px',
                  cursor: 'se-resize',
                  border: '2px solid white'
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleResizeStart(obj.id, 'se', e);
                }}
              />
              <div 
                style={{
                  position: 'absolute',
                  right: '-6px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '10px',
                  height: '10px',
                  backgroundColor: '#4f46e5',
                  borderRadius: '2px',
                  cursor: 'e-resize',
                  border: '2px solid white'
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleResizeStart(obj.id, 'e', e);
                }}
              />
            </>
          )}
          
          {/* Edit hint */}
          {isSelected && !isEditing && (
            <div style={{
              position: 'absolute',
              bottom: '-20px',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '9px',
              color: '#64748b',
              whiteSpace: 'nowrap',
              pointerEvents: 'none'
            }}>
              Double-click to edit
            </div>
          )}
        </div>
      );
    }

    // Resize handles for venue/facility objects
    const renderResizeHandles = () => {
      if (!isSelected || isRow) return null;
      return (
        <>
          <div 
            style={{
              position: 'absolute',
              right: '-6px',
              bottom: '-6px',
              width: '10px',
              height: '10px',
              backgroundColor: '#4f46e5',
              borderRadius: '2px',
              cursor: 'se-resize',
              border: '2px solid white',
              zIndex: 10
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeStart(obj.id, 'se', e);
            }}
          />
          <div 
            style={{
              position: 'absolute',
              right: '-6px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '10px',
              height: '10px',
              backgroundColor: '#4f46e5',
              borderRadius: '2px',
              cursor: 'e-resize',
              border: '2px solid white',
              zIndex: 10
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeStart(obj.id, 'e', e);
            }}
          />
          <div 
            style={{
              position: 'absolute',
              bottom: '-6px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '10px',
              height: '10px',
              backgroundColor: '#4f46e5',
              borderRadius: '2px',
              cursor: 's-resize',
              border: '2px solid white',
              zIndex: 10
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              handleResizeStart(obj.id, 's', e);
            }}
          />
        </>
      );
    };

    return (
      <div
        key={obj.id}
        style={{
          position: 'absolute',
          left: `${obj.x}px`,
          top: `${obj.y}px`,
          width: `${obj.width}px`,
          height: `${obj.height}px`,
          backgroundColor: isRow ? 'transparent' : obj.color,
          border: hasLiveCollision ? '3px dashed #ef4444' : hasOverlap ? '3px solid #ef4444' : (isSelected || isMultiSelected) ? '3px solid #4f46e5' : (isRow ? '2px dashed #94a3b8' : '2px solid #94a3b8'),
          borderRadius: obj.type === 'round_table' ? '50%' : (obj.type === 'rect_table' ? '4px' : '8px'),
          transform: `rotate(${obj.rotation}deg)`,
          cursor: 'move',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          fontFamily: obj.properties?.font_family || 'JetBrains Mono, monospace',
          fontSize: `${obj.properties?.font_size || 11}px`,
          fontWeight: textBold ? '600' : '400',
          fontStyle: textItalic ? 'italic' : 'normal',
          textDecoration: textUnderline ? 'underline' : 'none',
          color: textColor,
          boxShadow: hasLiveCollision ? '0 0 30px rgba(239, 68, 68, 0.7)' : hasOverlap ? '0 0 20px rgba(239, 68, 68, 0.5)' : (isSelected || isMultiSelected) ? '0 8px 30px rgba(79, 70, 229, 0.3)' : (isRow ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)'),
          userSelect: 'none',
          opacity: isVenue ? 0.9 : 1,
          zIndex: (isSelected || isMultiSelected) ? 50 : (hasOverlap || hasLiveCollision ? 40 : 5),
          ...collisionStyle
        }}
      >
        {/* Don't show label on canvas for rows/multi-rows */}
        {isVenue && VenueIcon && (
          <VenueIcon className="w-6 h-6" style={{ pointerEvents: 'none', color: iconColor }} />
        )}
        {!isRow && (
          <span style={{ pointerEvents: 'none', fontSize: isVenue ? `${obj.properties?.font_size || 10}px` : '11px', color: textColor, fontWeight: textBold ? 'bold' : 'normal' }}>{obj.label}</span>
        )}
        
        {/* Resize handles for venue/facility objects */}
        {renderResizeHandles()}
        
        {(obj.type === 'single_row' || obj.type === 'multiple_rows') && obj.seats && (() => {
          const rowsByLabel = {};
          obj.seats.forEach(seat => {
            let rowLabel;
            if (obj.type === 'single_row') {
              rowLabel = obj.properties?.row_label || 'A';
            } else {
              // For multiple_rows, use Excel-style labels (A, B, ..., Z, AA, AB, ...)
              const startLabel = obj.properties?.start_label || 'A';
              const startNum = excelLabelToNumber(startLabel);
              rowLabel = numberToExcelLabel(startNum + (seat.row || 0));
            }
            if (!rowsByLabel[rowLabel]) rowsByLabel[rowLabel] = [];
            rowsByLabel[rowLabel].push(seat);
          });

          return Object.entries(rowsByLabel).map(([rowLabel, seats]) => {
            const minX = Math.min(...seats.map(s => s.x));
            const maxX = Math.max(...seats.map(s => s.x));
            const avgY = seats.reduce((sum, s) => sum + s.y, 0) / seats.length;

            return (
              <div key={rowLabel}>
                {/* Left row label */}
                <div style={{
                  position: 'absolute',
                  left: `${minX - 40}px`,
                  top: `${avgY}px`,
                  width: '30px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#64748b',
                  pointerEvents: 'none'
                }}>
                  {rowLabel}
                </div>
                
                {/* Right row label */}
                <div style={{
                  position: 'absolute',
                  left: `${maxX + 46}px`,
                  top: `${avgY}px`,
                  width: '30px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  color: '#64748b',
                  pointerEvents: 'none'
                }}>
                  {rowLabel}
                </div>

                {/* Seats */}
                {seats.map(seat => {
                  const seatLabel = `${rowLabel}${seat.seat_number}`;
                  // Use custom seat color if set, otherwise neutral color
                  const seatColor = obj.properties?.seat_color || CANVAS_SEAT_COLOR;
                  const borderColor = CANVAS_SEAT_BORDER;
                  
                  return (
                    <div
                      key={seat.id}
                      style={{
                        position: 'absolute',
                        left: `${seat.x}px`,
                        top: `${seat.y}px`,
                        width: '36px',
                        height: '36px',
                        backgroundColor: seatColor,
                        borderRadius: '6px',
                        border: `2px solid ${borderColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        color: '#1e293b',
                        pointerEvents: 'none'
                      }}
                    >
                      {seatLabel}
                    </div>
                  );
                })}
              </div>
            );
          });
        })()}
        
        {(obj.type === 'round_table' || obj.type === 'rect_table') && obj.seats && obj.seats.map((seat) => {
          const seatSpacing = obj.properties?.seat_spacing || 20;
          const baseOffset = obj.type === 'round_table' ? obj.width / 2 : Math.min(obj.width, obj.height) / 2;
          const calculatedOffset = baseOffset + seatSpacing;
          const angle = (seat.angle - obj.rotation) * (Math.PI / 180);
          const seatX = (obj.width / 2) + Math.cos(angle) * calculatedOffset - 14;
          const seatY = (obj.height / 2) + Math.sin(angle) * calculatedOffset - 14;
          // Use label directly for table name (T1, T2, etc)
          const tableName = obj.label.startsWith('T') ? obj.label : `T${obj.label.replace(/[^0-9]/g, '') || '1'}`;
          const seatLabel = `${tableName}-${seat.seat_number}`;
          // Use neutral color for canvas design
          const seatColor = CANVAS_SEAT_COLOR;
          
          return (
            <div
              key={seat.id}
              style={{
                position: 'absolute',
                left: `${seatX}px`,
                top: `${seatY}px`,
                width: '28px',
                height: '28px',
                backgroundColor: seatColor,
                borderRadius: '50%',
                border: `2px solid ${CANVAS_SEAT_BORDER}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '7px',
                color: '#1e293b',
                fontWeight: 'bold',
                pointerEvents: 'none',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              {seatLabel}
            </div>
          );
        })}
        
        {/* Bus Layout Rendering - Flight Style */}
        {obj.type === 'bus_layout' && (
          <>
            {/* Bus body background */}
            <div
              style={{
                position: 'absolute',
                left: '-5px',
                top: '-5px',
                right: '-5px',
                bottom: '-5px',
                backgroundColor: '#f8fafc',
                borderRadius: '20px 20px 10px 10px',
                border: '3px solid #cbd5e1',
                pointerEvents: 'none',
                zIndex: 0
              }}
            />
            
            {/* Driver area at top */}
            {obj.properties?.has_driver_seat && (
              <div
                style={{
                  position: 'absolute',
                  left: '0',
                  right: '0',
                  top: obj.properties?.front_at_top ? '5px' : 'auto',
                  bottom: obj.properties?.front_at_top ? 'auto' : '5px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0 10px',
                  pointerEvents: 'none',
                  zIndex: 1
                }}
              >
                {/* Front direction indicator */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '2px',
                  padding: '4px 8px',
                  backgroundColor: '#dbeafe',
                  borderRadius: '6px',
                  border: '1px solid #93c5fd'
                }}>
                  <ArrowUp size={14} style={{ color: '#2563eb' }} />
                  <span style={{ fontSize: '7px', color: '#2563eb', fontWeight: 'bold', letterSpacing: '0.5px' }}>FRONT</span>
                </div>
                
                {/* Driver seat */}
                <div style={{
                  width: '36px',
                  height: '36px',
                  backgroundColor: '#475569',
                  borderRadius: '8px',
                  border: '2px solid #334155',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  color: '#fff',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  <Bus size={16} />
                </div>
                
                {/* Steering wheel indicator */}
                <div style={{
                  width: '30px',
                  height: '30px',
                  border: '3px solid #94a3b8',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#94a3b8',
                    borderRadius: '50%'
                  }} />
                </div>
              </div>
            )}
            
            {/* Aisle - flight style */}
            <div
              style={{
                position: 'absolute',
                left: `${(obj.properties?.left_seats || 2) * 46 + 8}px`,
                top: '70px',
                width: '20px',
                height: `calc(100% - 100px)`,
                background: 'linear-gradient(to bottom, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%)',
                borderRadius: '4px',
                pointerEvents: 'none',
                zIndex: 1
              }}
            >
              {/* Aisle markers */}
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                fontSize: '6px',
                color: '#94a3b8',
                fontWeight: 'bold',
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                letterSpacing: '2px'
              }}>
                AISLE
              </div>
            </div>
            
            {/* Seats - Flight booking style with gap rules and custom row config */}
            {obj.properties && (() => {
              // Get configuration
              const gapRules = obj.properties?.gap_rules || [];
              const startRowNum = obj.properties?.start_row_number || 1;
              const rowCount = obj.properties?.row_count || 9;
              // Support per-side row counts
              const leftRowCount = obj.properties?.left_row_count ?? rowCount;
              const rightRowCount = obj.properties?.right_row_count ?? rowCount;
              const maxRowCount = Math.max(leftRowCount, rightRowCount);
              
              const defaultLeftSeats = obj.properties?.left_seats || 2;
              const defaultRightSeats = obj.properties?.right_seats || 2;
              const customRowsEnabled = obj.properties?.custom_rows_enabled || false;
              const customRows = obj.properties?.custom_rows || {};
              const columnLetters = obj.properties?.column_letters || 'ABCD';
              const labelMode = obj.properties?.label_mode || 'combined';
              const leftLetters = obj.properties?.left_letters || 'AB';
              const rightLetters = obj.properties?.right_letters || 'CD';
              
              const seatWidth = 40;
              const seatSpacing = 6;
              const rowSpacing = 48;
              const aisleWidth = 35;
              
              // Calculate cumulative Y offset for each row based on gaps
              const getRowYOffset = (rowNum) => {
                let offset = 0;
                gapRules.forEach(gap => {
                  if (gap.afterRowNumber < rowNum) {
                    offset += gap.gapHeightPx || 30;
                  }
                });
                return offset;
              };
              
              // Check if gap applies to a specific side
              const getGapForSide = (rowNum, side) => {
                return gapRules.find(gap => {
                  if (gap.afterRowNumber !== rowNum - 1) return false;
                  if (gap.applyTo === 'both') return true;
                  if (gap.applyTo === side) return true;
                  return false;
                });
              };
              
              // Render seats dynamically (same logic as checkout)
              const renderedSeats = [];
              
              for (let rowIdx = 0; rowIdx < maxRowCount; rowIdx++) {
                const rowNum = startRowNum + rowIdx;
                const rowConfig = customRows[rowNum] || {};
                const actualLeftSeats = customRowsEnabled && rowConfig.left_seats !== undefined 
                  ? rowConfig.left_seats 
                  : defaultLeftSeats;
                const actualRightSeats = customRowsEnabled && rowConfig.right_seats !== undefined 
                  ? rowConfig.right_seats 
                  : defaultRightSeats;
                
                // Check if this row is within per-side row counts
                const showLeftSide = rowIdx < leftRowCount;
                const showRightSide = rowIdx < rightRowCount;
                
                const yOffset = getRowYOffset(rowNum);
                const baseY = 70 + rowIdx * rowSpacing + yOffset;
                
                // Left side seats - only render if within leftRowCount
                if (showLeftSide) {
                  for (let seatIdx = 0; seatIdx < actualLeftSeats; seatIdx++) {
                  let seatLetter;
                  if (labelMode === 'separate') {
                    seatLetter = leftLetters[seatIdx] || String.fromCharCode(65 + seatIdx);
                  } else {
                    seatLetter = columnLetters[seatIdx] || String.fromCharCode(65 + seatIdx);
                  }
                  const seatLabel = `${rowNum}${seatLetter}`;
                  const seatX = seatIdx * (seatWidth + seatSpacing);
                  
                  // Find matching seat in obj.seats for status
                  const existingSeat = obj.seats?.find(s => s.seat_label === seatLabel);
                  const seatStatus = existingSeat?.status || 'available';
                  const bookable = existingSeat?.bookable !== false;
                  
                  let seatBgColor = '#22c55e';
                  let seatBorderColor = '#16a34a';
                  if (!bookable) {
                    seatBgColor = '#ef4444';
                    seatBorderColor = '#dc2626';
                  } else if (seatStatus === 'booked') {
                    seatBgColor = '#6b7280';
                    seatBorderColor = '#4b5563';
                  }
                  
                  renderedSeats.push(
                    <div
                      key={`${rowNum}-L-${seatIdx}`}
                      style={{
                        position: 'absolute',
                        left: `${seatX}px`,
                        top: `${baseY}px`,
                        width: '40px',
                        height: '40px',
                        backgroundColor: seatBgColor,
                        borderRadius: '8px 8px 4px 4px',
                        border: `2px solid ${seatBorderColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: '#fff',
                        pointerEvents: 'none',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      {seatLabel}
                    </div>
                  );
                  }
                }
                
                // Right side seats - only render if within rightRowCount
                if (showRightSide) {
                  for (let seatIdx = 0; seatIdx < actualRightSeats; seatIdx++) {
                    let seatLetter;
                    if (labelMode === 'separate') {
                      seatLetter = rightLetters[seatIdx] || String.fromCharCode(67 + seatIdx);
                    } else {
                      seatLetter = columnLetters[defaultLeftSeats + seatIdx] || String.fromCharCode(65 + defaultLeftSeats + seatIdx);
                    }
                    const seatLabel = `${rowNum}${seatLetter}`;
                    const seatX = defaultLeftSeats * (seatWidth + seatSpacing) + aisleWidth + seatIdx * (seatWidth + seatSpacing);
                  
                  // Find matching seat in obj.seats for status
                  const existingSeat = obj.seats?.find(s => s.seat_label === seatLabel);
                  const seatStatus = existingSeat?.status || 'available';
                  const bookable = existingSeat?.bookable !== false;
                  
                  let seatBgColor = '#22c55e';
                  let seatBorderColor = '#16a34a';
                  if (!bookable) {
                    seatBgColor = '#ef4444';
                    seatBorderColor = '#dc2626';
                  } else if (seatStatus === 'booked') {
                    seatBgColor = '#6b7280';
                    seatBorderColor = '#4b5563';
                  }
                  
                  renderedSeats.push(
                    <div
                      key={`${rowNum}-R-${seatIdx}`}
                      style={{
                        position: 'absolute',
                        left: `${seatX}px`,
                        top: `${baseY}px`,
                        width: '40px',
                        height: '40px',
                        backgroundColor: seatBgColor,
                        borderRadius: '8px 8px 4px 4px',
                        border: `2px solid ${seatBorderColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: '#fff',
                        pointerEvents: 'none',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                      }}
                    >
                      {seatLabel}
                    </div>
                  );
                  }
                }
              }
              
              return renderedSeats;
            })()}
            
            {/* Gap Rule Dividers in Canvas */}
            {(obj.properties?.gap_rules || []).map((gap, idx) => {
              const rowSpacing = 48;
              const startRowNum = obj.properties?.start_row_number || 1;
              
              // Calculate Y position for the gap
              let gapY = 70 + (gap.afterRowNumber - startRowNum + 1) * rowSpacing;
              // Add cumulative offset from previous gaps
              obj.properties?.gap_rules?.slice(0, idx).forEach(prevGap => {
                if (prevGap.afterRowNumber < gap.afterRowNumber) {
                  gapY += prevGap.gapHeightPx || 30;
                }
              });
              
              const leftSeats = obj.properties?.left_seats || 2;
              const rightSeats = obj.properties?.right_seats || 2;
              const seatWidth = 40;
              const seatSpacing = 6;
              const aisleWidth = 35;
              
              return (
                <div
                  key={`gap-${gap.id || idx}`}
                  style={{
                    position: 'absolute',
                    left: gap.applyTo === 'right' ? `${leftSeats * (seatWidth + seatSpacing) + aisleWidth}px` : '0',
                    right: gap.applyTo === 'left' ? `${rightSeats * (seatWidth + seatSpacing) + aisleWidth}px` : '0',
                    top: `${gapY}px`,
                    height: `${gap.gapHeightPx || 30}px`,
                    backgroundColor: gap.showDivider ? 'transparent' : 'rgba(148, 163, 184, 0.1)',
                    borderTop: gap.showDivider ? `2px ${gap.dividerStyle || 'dashed'} #94a3b8` : 'none',
                    borderBottom: gap.showDivider ? `2px ${gap.dividerStyle || 'dashed'} #94a3b8` : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                    zIndex: 2
                  }}
                >
                  {gap.showDivider && gap.dividerLabel && (
                    <span style={{
                      fontSize: '8px',
                      color: '#64748b',
                      backgroundColor: '#f8fafc',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      fontWeight: '600'
                    }}>
                      {gap.dividerLabel}
                    </span>
                  )}
                </div>
              );
            })}
          </>
        )}
        
        {/* Bus Feature Objects - Doors, Toilet, TV, Aisle */}
        {obj.type === 'bus_door_front' || obj.type === 'bus_door_middle' || obj.type === 'bus_door_back' ? (
          <div
            style={{
              position: 'absolute',
              left: '0',
              top: '0',
              width: '100%',
              height: '100%',
              backgroundColor: '#f59e0b',
              borderRadius: '8px',
              border: '3px solid #d97706',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}
          >
            <DoorOpen size={24} className="text-white" />
            <span style={{ fontSize: '8px', color: '#fff', fontWeight: 'bold', marginTop: '2px' }}>
              {obj.properties?.door_position?.toUpperCase() || 'DOOR'}
            </span>
            {obj.properties?.is_entry && <span style={{ fontSize: '6px', color: '#fef3c7' }}>ENTRY</span>}
            {obj.properties?.is_exit && <span style={{ fontSize: '6px', color: '#fef3c7' }}>EXIT</span>}
          </div>
        ) : null}
        
        {obj.type === 'bus_toilet' && (
          <div
            style={{
              position: 'absolute',
              left: '0',
              top: '0',
              width: '100%',
              height: '100%',
              backgroundColor: '#06b6d4',
              borderRadius: '8px',
              border: '3px solid #0891b2',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}
          >
            <Bath size={24} className="text-white" />
            <span style={{ fontSize: '8px', color: '#fff', fontWeight: 'bold', marginTop: '2px' }}>WC</span>
          </div>
        )}
        
        {obj.type === 'bus_tv' && (
          <div
            style={{
              position: 'absolute',
              left: '0',
              top: '0',
              width: '100%',
              height: '100%',
              backgroundColor: '#8b5cf6',
              borderRadius: '6px',
              border: '3px solid #7c3aed',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}
          >
            <Tv size={20} className="text-white" />
            <span style={{ fontSize: '7px', color: '#fff', fontWeight: 'bold', marginTop: '2px' }}>TV</span>
          </div>
        )}
        
        {obj.type === 'bus_aisle' && (
          <div
            style={{
              position: 'absolute',
              left: '0',
              top: '0',
              width: '100%',
              height: '100%',
              background: 'repeating-linear-gradient(0deg, #e2e8f0, #e2e8f0 10px, #f1f5f9 10px, #f1f5f9 20px)',
              borderRadius: '4px',
              border: '2px dashed #94a3b8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none'
            }}
          >
            <span style={{ 
              fontSize: '8px', 
              color: '#64748b', 
              fontWeight: 'bold',
              writingMode: 'vertical-rl',
              textOrientation: 'mixed'
            }}>AISLE</span>
          </div>
        )}
      </div>
    );
  };

  const selectedObj = canvasObjects.find(obj => obj.id === selectedObject);

  return (
    <div className="flex flex-col h-screen bg-slate-50">
      {/* Top Center Success/Error Message */}
      {successMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50">
          <div 
            className={`flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg border ${
              successMessage.type === 'success' 
                ? 'bg-green-50 border-green-300 text-green-800' 
                : 'bg-red-50 border-red-300 text-red-800'
            }`}
          >
            {successMessage.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <X className="w-5 h-5 text-red-600" />
            )}
            <span className="font-medium">{successMessage.text}</span>
            <button
              onClick={closeSuccessMessage}
              className={`ml-2 p-1 rounded-full hover:bg-opacity-20 ${
                successMessage.type === 'success' ? 'hover:bg-green-600' : 'hover:bg-red-600'
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Top Toolbar - Mobile responsive */}
      <div className="border-b border-slate-200 bg-white px-2 sm:px-6 py-2 sm:py-3 overflow-x-auto">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {!isEmbed && (
            <Button
              onClick={() => {
                // Always go to layouts tab in dashboard
                navigate('/dashboard?tab=layouts');
              }}
              variant="ghost"
              size="sm"
              className="text-slate-600 px-1 sm:px-3"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Back to Layouts</span>
            </Button>
            )}
            {!isEmbed && <div className="hidden sm:block h-6 w-px bg-slate-200" />}
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Layout Name"
              className="w-24 sm:w-48 h-8 sm:h-9 text-sm"
            />
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              onClick={undo}
              variant="outline"
              size="sm"
              disabled={historyIndex <= 0}
              title="Undo (Ctrl+Z)"
              className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3"
            >
              <Undo2 className="w-4 h-4" />
            </Button>
            <Button
              onClick={redo}
              variant="outline"
              size="sm"
              disabled={historyIndex >= history.length - 1}
              title="Redo (Ctrl+Y)"
              className="h-8 w-8 p-0 sm:h-9 sm:w-auto sm:px-3"
            >
              <Redo2 className="w-4 h-4" />
            </Button>
            <div className="h-6 w-px bg-slate-200" />
            <Button
              onClick={() => setFormData({ ...formData, grid_snap: !formData.grid_snap })}
              variant={formData.grid_snap ? 'default' : 'outline'}
              size="sm"
              className="hidden sm:flex"
            >
              <Grid3x3 className="w-4 h-4 mr-2" />
              Snap
            </Button>
            <Button
              onClick={() => setFormData({ 
                ...formData, 
                layout_display_mode: formData.layout_display_mode === 'auto' ? 'manual' : 'auto' 
              })}
              variant={formData.layout_display_mode === 'manual' ? 'default' : 'outline'}
              size="sm"
              title={formData.layout_display_mode === 'auto' 
                ? 'Auto-arrange: Seats grouped by rows in checkout' 
                : 'Manual: Exact canvas layout in checkout'}
              className="hidden sm:flex"
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              {formData.layout_display_mode === 'auto' ? 'Auto' : 'Manual'}
            </Button>
            {/* Stats in Top Bar when toggled - Compact on mobile, full on desktop */}
            {statsInTopBar && (
              <div className="flex items-center gap-2 sm:gap-4 px-2 sm:px-3 py-1 bg-slate-100 rounded-lg">
                <div className="flex items-center gap-1">
                  <span className="text-[10px] sm:text-xs text-slate-500 hidden sm:inline">Capacity:</span>
                  <span className="font-bold text-indigo-600 text-sm">{(() => {
                    let total = 0;
                    canvasObjects.forEach(obj => {
                      if (obj.type === 'seat') total += 1;
                      else if (obj.type === 'single_row') total += obj.properties?.seat_count || 0;
                      else if (obj.type === 'multiple_rows') {
                        total += (obj.properties?.row_count || 0) * (obj.properties?.seat_count || 0);
                      }
                      else if (obj.type === 'bus_layout') {
                        total += obj.seats?.length || 0;
                      }
                      else if (obj.type === 'round_table' || obj.type === 'rect_table') total += obj.properties?.seats || 0;
                      else if (obj.type === 'ga_zone' || obj.type === 'vip_pod') total += obj.properties?.capacity || 0;
                      else if (obj.type === 'sofa') total += obj.properties?.seater_count || 3;
                    });
                    return total;
                  })()}</span>
                  <span className="text-[10px] text-slate-400 sm:hidden">seats</span>
                </div>
                <div className="hidden sm:flex items-center gap-1">
                  <span className="text-xs text-slate-500">Objects:</span>
                  <span className="font-semibold text-slate-700">{canvasObjects.length}</span>
                </div>
                <div data-walkthrough="walkthrough-autosave" className={`w-2 h-2 rounded-full ${autoSaveEnabled ? 'bg-green-500' : 'bg-slate-400'}`} title={autoSaveEnabled ? 'Auto-save ON' : 'Auto-save OFF'}></div>
                {hasChanges && <div className="w-2 h-2 rounded-full bg-amber-500" title="Unsaved changes"></div>}
                <button
                  onClick={() => setStatsInTopBar(false)}
                  className="text-slate-400 hover:text-slate-600 ml-1"
                  title="Move to canvas"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            <Button
              onClick={() => {
                if (currentLayoutId) {
                  window.open(`/widget/${currentLayoutId}`, '_blank');
                } else {
                  showSuccessMessage({ type: 'error', text: 'Please save the layout first to preview' });
                }
              }}
              variant="outline"
              size="sm"
              title="Preview as checkout page"
              className="h-8 px-2 sm:px-3"
            >
              <Eye className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Preview</span>
            </Button>
            <Button
              onClick={walkthrough.replay}
              variant="outline"
              size="sm"
              title="Replay the interactive tour — teach your organizers how to build a layout"
              className="h-8 px-2"
              data-testid="replay-walkthrough-btn"
            >
              <HelpCircle className="w-4 h-4" />
              <span className="hidden lg:inline ml-1.5">Help</span>
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-indigo-600 hover:bg-indigo-700 h-8 px-2 sm:px-3"
              size="sm"
              disabled={loading}
              data-walkthrough="walkthrough-publish"
            >
              <Save className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{loading ? 'Saving...' : 'Save'}</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Tools (Collapsible) - Hidden on very small screens */}
        <div 
          data-walkthrough="walkthrough-toolbox"
          className={`${leftPanelCollapsed ? 'w-12 sm:w-16' : 'w-48 sm:w-64'} border-r border-slate-200 bg-white overflow-y-auto transition-all duration-300 flex flex-col`}
        >
          {/* Collapse Toggle */}
          <div className="p-2 border-b border-slate-100 flex justify-end">
            <button
              onClick={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
              title={leftPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
            >
              {leftPanelCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>
          
          {leftPanelCollapsed ? (
            /* Collapsed view - show relevant icons based on mode */
            <div className="p-2 space-y-3 overflow-y-auto">
              {busLayoutMode ? (
                /* Bus Layout Mode - Only show bus-related tools */
                <>
                  <div className="p-2 bg-amber-50 rounded border border-amber-200 mb-2">
                    <div className="flex items-center justify-center gap-1 text-amber-700">
                      <Bus className="w-3 h-3" />
                      <span className="text-[8px] font-bold">BUS MODE</span>
                    </div>
                  </div>
                  <div className="border-b border-slate-100 pb-2">
                    <div className="text-[9px] font-bold text-slate-400 mb-1 text-center">BUS</div>
                    <div className="space-y-1">
                      {/* Bus Layout */}
                      <button
                        onClick={() => addObject('bus_layout', TOOL_CATEGORIES.seating.tools.bus_layout)}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-blue-50 bg-blue-50 border border-blue-200"
                        title="Bus Layout (Active)"
                      >
                        <div className="w-6 h-6 rounded flex items-center justify-center bg-blue-100">
                          <Bus className="w-4 h-4 text-blue-700" />
                        </div>
                        <span className="text-[10px] text-blue-700 font-medium">Bus</span>
                      </button>
                      {/* Bus features */}
                      {Object.entries(TOOL_CATEGORIES.bus_features?.tools || {}).map(([toolKey, tool]) => {
                        const Icon = tool.icon;
                        return (
                          <button
                            key={toolKey}
                            onClick={() => addObject(toolKey, tool)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 transition-colors"
                            title={tool.label}
                          >
                            <div className="w-6 h-6 rounded flex items-center justify-center bg-slate-100">
                              <Icon className="w-4 h-4 text-slate-700" />
                            </div>
                            <span className="text-[10px] text-slate-600 truncate">{tool.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Exit bus mode option */}
                  <div>
                    <div className="text-[9px] font-bold text-slate-400 mb-1 text-center">EXIT</div>
                    <button
                      onClick={() => {
                        if (window.confirm('Add a regular seating object? This will remove the bus layout.')) {
                          addObject('single_row', TOOL_CATEGORIES.seating.tools.single_row);
                        }
                      }}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-red-50 text-red-600 border border-red-200"
                      title="Exit Bus Mode"
                    >
                      <X className="w-4 h-4" />
                      <span className="text-[10px]">Exit</span>
                    </button>
                  </div>
                </>
              ) : (
                /* Normal Mode - Show all seating icons */
                <>
                  {/* SEATING - Always show all with labels */}
                  <div className="border-b border-slate-100 pb-2">
                    <div className="text-[9px] font-bold text-slate-400 mb-1 text-center">SEATING</div>
                    <div className="space-y-1">
                      {Object.entries(TOOL_CATEGORIES.seating.tools).map(([toolKey, tool]) => {
                        // Use correct icons for row types
                        let Icon = tool.icon;
                        if (toolKey === 'single_row') Icon = Rows3;
                        if (toolKey === 'multiple_rows') Icon = LayoutGrid;
                        
                        return (
                          <button
                            key={toolKey}
                            onClick={() => addObject(toolKey, tool)}
                            className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 transition-colors"
                            title={tool.label}
                          >
                            <div className="w-6 h-6 rounded flex items-center justify-center bg-slate-100">
                              <Icon className="w-4 h-4 text-slate-700" style={{ strokeWidth: 2 }} />
                            </div>
                            <span className="text-[10px] text-slate-600 truncate">{tool.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* OTHER - Text Label */}
                  <div className="border-b border-slate-100 pb-2">
                    <div className="text-[9px] font-bold text-slate-400 mb-1 text-center">OTHER</div>
                    <button
                      onClick={() => addObject('text', TOOL_CATEGORIES.other.tools.text)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 transition-colors"
                      title="Text Label"
                    >
                      <div className="w-6 h-6 rounded flex items-center justify-center bg-slate-100">
                        <Type className="w-4 h-4 text-slate-700" style={{ strokeWidth: 2 }} />
                      </div>
                      <span className="text-[10px] text-slate-600">Text</span>
                    </button>
                  </div>
                  
                  {/* STAGE & FACILITIES - Icons only with hover */}
                  <div>
                    <div className="text-[9px] font-bold text-slate-400 mb-1 text-center">MORE</div>
                    <div className="grid grid-cols-2 gap-1">
                      {[...Object.entries(TOOL_CATEGORIES.areas.tools), ...Object.entries(TOOL_CATEGORIES.facilities.tools).slice(0, 4)].map(([toolKey, tool]) => {
                        const Icon = tool.icon;
                        return (
                          <button
                            key={toolKey}
                            onClick={() => addObject(toolKey, tool)}
                            className="w-full aspect-square rounded flex items-center justify-center hover:bg-slate-100 transition-colors"
                            title={tool.label}
                          >
                            <Icon className="w-4 h-4 text-slate-600" style={{ strokeWidth: 2 }} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Expanded view - full menu */
            <div className="p-4 flex-1 overflow-y-auto">
              {/* Bus Layout Mode Banner */}
              {busLayoutMode && (
                <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="flex items-center gap-2 text-amber-800">
                    <Bus className="w-4 h-4" />
                    <span className="text-xs font-semibold">Bus Layout Mode</span>
                  </div>
                  <p className="text-[10px] text-amber-600 mt-1">
                    Only bus features available. Add another seating object to exit.
                  </p>
                </div>
              )}
              
              {Object.entries(TOOL_CATEGORIES).map(([categoryKey, category]) => {
                // In bus mode: only show bus_features and hide seating/areas
                // Not in bus mode: show everything except bus_features
                const isBusCategory = category.busOnly;
                
                if (busLayoutMode && !isBusCategory && categoryKey !== 'seating') {
                  // Hide non-bus categories except seating (to allow exiting bus mode)
                  return null;
                }
                if (!busLayoutMode && isBusCategory) {
                  // Hide bus-only categories when not in bus mode
                  return null;
                }
                
                return (
                <div key={categoryKey} className="mb-4">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Toggle: if already active, collapse. Otherwise expand.
                      setActiveCategory(activeCategory === categoryKey ? null : categoryKey);
                    }}
                    className={`w-full text-left px-3 py-2.5 rounded-lg mb-2 text-xs font-bold tracking-wider transition-all cursor-pointer ${
                      activeCategory === categoryKey
                        ? 'bg-indigo-100 text-indigo-700 shadow-sm'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                    }`}
                  >
                    <span className="flex items-center justify-between">
                      {category.label}
                      <span className={`text-[10px] transition-transform ${activeCategory === categoryKey ? 'rotate-180' : ''}`}>▼</span>
                    </span>
                  </button>
                  {activeCategory === categoryKey && (
                    <div className="space-y-1">
                      {Object.entries(category.tools).map(([toolKey, tool]) => {
                        // In bus mode, completely hide other seating options (don't show grayed out)
                        if (busLayoutMode && categoryKey === 'seating' && toolKey !== 'bus_layout') {
                          return null; // Don't render at all
                        }
                        
                        // Use correct icons for row types
                        let Icon = tool.icon;
                        if (toolKey === 'single_row') Icon = Rows3;
                        if (toolKey === 'multiple_rows') Icon = LayoutGrid;
                        
                        return (
                          <button
                            key={toolKey}
                            onClick={() => addObject(toolKey, tool)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors text-left"
                            title={tool.label}
                          >
                            <div
                              className="w-8 h-8 rounded flex items-center justify-center border border-slate-200"
                              style={{ backgroundColor: `${tool.color}60` }}
                            >
                              <Icon className="w-5 h-5 text-slate-700" style={{ strokeWidth: 2 }} />
                            </div>
                            <span className="text-sm font-medium text-slate-700">{tool.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          )}
        </div>

        {/* Canvas Area - Scrollable with fixed zoom controls */}
        <div className="flex-1 bg-slate-50 canvas-bg relative">
          {/* Scrollable Canvas */}
          <div data-walkthrough="walkthrough-canvas" className="absolute inset-0 overflow-auto">
            <div
              ref={canvasRef}
              className="relative"
              onMouseDown={handleCanvasMouseDown}
              onMouseMove={handleCanvasMouseMove}
              onMouseUp={handleCanvasMouseUp}
              onMouseLeave={handleCanvasMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                cursor: draggedObject ? 'grabbing' : 'default',
                transform: `scale(${zoom})`,
                transformOrigin: '0 0',
                width: '3000px',
                height: '2000px',
                minWidth: '100%',
                minHeight: '100%',
                touchAction: draggedObject ? 'none' : 'pan-x pan-y' // Allow scrolling when not dragging
              }}
            >
              {/* Render zones first (background) */}
              {canvasObjects.filter(obj => obj.type === 'zone').map(obj => renderObject(obj))}
              {/* Render all other objects on top */}
              {canvasObjects.filter(obj => obj.type !== 'zone').map(obj => renderObject(obj))}
            </div>
          </div>

          {/* Fixed Zoom Controls - Outside scrollable area - Mobile responsive */}
          <div className="absolute bottom-4 right-2 sm:bottom-6 sm:right-6 flex flex-col gap-2 z-50 pointer-events-auto">
            <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-1 sm:p-2 flex items-center gap-0.5 sm:gap-1">
              <Button size="sm" variant="ghost" className="h-7 w-7 sm:h-8 sm:w-8 p-0" onClick={() => setZoom(Math.min(zoom + 0.1, 2))}>
                <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
              </Button>
              <div className="px-1 sm:px-2 text-[10px] sm:text-xs font-mono font-semibold min-w-[35px] sm:min-w-[50px] text-center">
                {Math.round(zoom * 100)}%
              </div>
              <Button size="sm" variant="ghost" className="h-7 w-7 sm:h-8 sm:w-8 p-0" onClick={() => setZoom(Math.max(zoom - 0.1, 0.5))}>
                <Minus className="w-3 h-3 sm:w-4 sm:h-4" />
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-7 sm:h-8 px-1.5 sm:px-2 text-[10px] sm:text-xs font-semibold" 
                onClick={() => {
                  // Fit all objects to screen
                  if (canvasObjects.length === 0) {
                    setZoom(1);
                    return;
                  }
                  
                  // Find bounding box of all objects
                  let minX = Infinity, minY = Infinity, maxX = 0, maxY = 0;
                  canvasObjects.forEach(obj => {
                    minX = Math.min(minX, obj.x || 0);
                    minY = Math.min(minY, obj.y || 0);
                    maxX = Math.max(maxX, (obj.x || 0) + (obj.width || 100));
                    maxY = Math.max(maxY, (obj.y || 0) + (obj.height || 100));
                  });
                  
                  const contentWidth = maxX - minX + 100;
                  const contentHeight = maxY - minY + 100;
                  const viewportWidth = window.innerWidth - (leftPanelCollapsed ? 60 : 280);
                  const viewportHeight = window.innerHeight - 150;
                  
                  const zoomToFitWidth = viewportWidth / contentWidth;
                  const zoomToFitHeight = viewportHeight / contentHeight;
                  const optimalZoom = Math.min(zoomToFitWidth, zoomToFitHeight, 1.5);
                  
                  setZoom(Math.max(0.3, Math.min(optimalZoom, 1.5)));
                }}
                title="Fit to screen"
              >
                Fit
              </Button>
            </div>
          </div>

          {/* Stats - Only show floating panel when not in top bar */}
          {!statsInTopBar && (
          <div className="absolute top-6 left-6 bg-white rounded-lg shadow-lg border border-slate-200 p-4 space-y-3 z-50">
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-500">Total Capacity</div>
              <button
                onClick={() => setStatsInTopBar(true)}
                className="text-slate-400 hover:text-indigo-600 transition-colors"
                title="Move to top bar"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div>
              <div className="text-2xl font-bold text-indigo-600">{(() => {
                let total = 0;
                canvasObjects.forEach(obj => {
                  if (obj.type === 'seat') total += 1;
                  else if (obj.type === 'single_row') total += obj.properties?.seat_count || 0;
                  else if (obj.type === 'multiple_rows') {
                    const rows = obj.properties?.row_count || 0;
                    const seatsPerRow = obj.properties?.seat_count || 0;
                    total += rows * seatsPerRow;
                  }
                  else if (obj.type === 'bus_layout') {
                    total += obj.seats?.length || 0;
                  }
                  else if (obj.type === 'round_table' || obj.type === 'rect_table') total += obj.properties?.seats || 0;
                  else if (obj.type === 'ga_zone' || obj.type === 'vip_pod') total += obj.properties?.capacity || 0;
                  else if (obj.type === 'sofa') total += obj.properties?.seater_count || 3;
                });
                return total;
              })()} seats</div>
            </div>
            <div className="border-t pt-2">
              <div className="text-xs text-slate-500">Objects</div>
              <div className="text-lg font-semibold text-slate-900">{canvasObjects.length}</div>
            </div>
            {/* Auto-save toggle */}
            <div className="border-t pt-2">
              <button 
                onClick={() => {
                  setAutoSaveEnabled(!autoSaveEnabled);
                  showSuccessMessage({ 
                    type: 'success', 
                    text: !autoSaveEnabled ? '✨ Auto-save ON' : 'Auto-save OFF - Remember to save manually!' 
                  });
                }}
                className="flex items-center gap-2 w-full text-left"
              >
                <div className={`w-8 h-4 rounded-full relative transition-colors ${autoSaveEnabled ? 'bg-green-500' : 'bg-slate-300'}`}>
                  <div className={`absolute w-3 h-3 bg-white rounded-full top-0.5 transition-all ${autoSaveEnabled ? 'right-0.5' : 'left-0.5'}`}></div>
                </div>
                <span className="text-xs text-slate-600">Auto-save</span>
              </button>
              {hasChanges && (
                <div className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full bg-amber-500"></div>
                  Unsaved changes
                </div>
              )}
              {lastSaved && !hasChanges && (
                <div className="text-xs text-slate-400 mt-1">
                  Saved: {lastSaved.toLocaleTimeString()}
                </div>
              )}
            </div>
            {/* Overlap Warning */}
            {overlapWarnings.length > 0 && (
              <div className="border-t pt-2">
                <div className="flex items-center gap-2 text-red-600 text-xs font-semibold mb-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {overlapWarnings.length} Overlap(s)
                </div>
                <div className="text-xs text-red-500 space-y-1 max-h-24 overflow-y-auto">
                  {overlapWarnings.slice(0, 3).map((w) => (
                    <div key={`${w.labelA}-${w.labelB}`} className="truncate">
                      &quot;{w.labelA}&quot; &amp; &quot;{w.labelB}&quot;
                    </div>
                  ))}
                  {overlapWarnings.length > 3 && (
                    <div className="text-slate-400">+{overlapWarnings.length - 3} more...</div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Right Panel - Properties (Collapsible) */}
        <div data-walkthrough="walkthrough-properties" className={`${rightPanelCollapsed ? 'w-12' : 'w-80'} border-l border-slate-200 bg-white overflow-y-auto transition-all duration-300 flex flex-col`}>
          {/* Collapse Toggle */}
          <div className="p-2 border-b border-slate-100 flex justify-start">
            <button
              onClick={() => setRightPanelCollapsed(!rightPanelCollapsed)}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
              title={rightPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
            >
              {rightPanelCollapsed ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
          
          {rightPanelCollapsed ? (
            /* Collapsed view */
            <div className="p-2 flex flex-col items-center gap-2">
              {(selectedObj || selectedObjects.length > 1) && (
                <>
                  <button
                    onClick={deleteObject}
                    className="w-8 h-8 rounded flex items-center justify-center hover:bg-red-50 text-red-500"
                    title={selectedObjects.length > 1 ? `Delete ${selectedObjects.length}` : 'Delete'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={duplicateObject}
                    className="w-8 h-8 rounded flex items-center justify-center hover:bg-slate-100 text-slate-600"
                    title={selectedObjects.length > 1 ? `Duplicate ${selectedObjects.length}` : 'Duplicate'}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          ) : (
            /* Expanded view */
            <div className="flex-1 p-6">
          
          {/* Multi-Select Panel - When more than 1 object selected */}
          {selectedObjects.length > 1 ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Multi-Select ({selectedObjects.length})
              </h3>
              <div className="text-xs text-slate-500 mb-4 bg-slate-50 p-2 rounded">
                Drag to move all selected objects together
              </div>
              
              {/* Action Buttons with Count */}
              <div className="flex gap-2 mb-4">
                <Button
                  onClick={confirmDeleteSelectedObjects}
                  variant="destructive"
                  size="sm"
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete ({selectedObjects.length})
                </Button>
                <Button
                  onClick={confirmDuplicateSelectedObjects}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate ({selectedObjects.length})
                </Button>
              </div>
              
              {/* Check if all selected are seating types for shared properties */}
              {(() => {
                const selectedObjs = canvasObjects.filter(o => selectedObjects.includes(o.id));
                const seatingTypes = ['single_row', 'multiple_rows', 'sofa', 'seat', 'round_table', 'rect_table'];
                const allSeating = selectedObjs.every(o => seatingTypes.includes(o.type));
                const hasRows = selectedObjs.some(o => ['single_row', 'multiple_rows'].includes(o.type));
                const hasSofas = selectedObjs.some(o => o.type === 'sofa');
                
                return (
                  <div className="space-y-4">
                    {/* Seat Color - Simple color picker */}
                    {allSeating && (
                      <div>
                        <Label className="text-slate-700 text-xs mb-2 block">Seat Color</Label>
                        <div className="flex gap-2 items-center">
                          <Input
                            type="color"
                            defaultValue="#22c55e"
                            onChange={(e) => updateMultipleObjectsProperty('properties', { seat_color: e.target.value })}
                            className="h-10 w-16 cursor-pointer"
                          />
                          <div className="flex gap-1 flex-wrap flex-1">
                            <button
                              className="w-6 h-6 rounded border-2 border-white shadow hover:ring-2 hover:ring-indigo-500"
                              style={{ backgroundColor: '#22c55e' }}
                              title="Green"
                              onClick={() => updateMultipleObjectsProperty('properties', { seat_color: '#22c55e' })}
                            />
                            <button
                              className="w-6 h-6 rounded border-2 border-white shadow hover:ring-2 hover:ring-indigo-500"
                              style={{ backgroundColor: '#ef4444' }}
                              title="Red"
                              onClick={() => updateMultipleObjectsProperty('properties', { seat_color: '#ef4444' })}
                            />
                            <button
                              className="w-6 h-6 rounded border-2 border-white shadow hover:ring-2 hover:ring-indigo-500"
                              style={{ backgroundColor: '#eab308' }}
                              title="Yellow"
                              onClick={() => updateMultipleObjectsProperty('properties', { seat_color: '#eab308' })}
                            />
                            <button
                              className="w-6 h-6 rounded border-2 border-white shadow hover:ring-2 hover:ring-indigo-500"
                              style={{ backgroundColor: '#3b82f6' }}
                              title="Blue"
                              onClick={() => updateMultipleObjectsProperty('properties', { seat_color: '#3b82f6' })}
                            />
                            <button
                              className="w-6 h-6 rounded border-2 border-white shadow hover:ring-2 hover:ring-indigo-500"
                              style={{ backgroundColor: '#a855f7' }}
                              title="Purple"
                              onClick={() => updateMultipleObjectsProperty('properties', { seat_color: '#a855f7' })}
                            />
                          </div>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">This color shows in checkout for available seats</div>
                      </div>
                    )}
                    
                    {/* Curve Amount - for rows and sofas */}
                    {(hasRows || hasSofas) && (
                      <div>
                        <Label className="text-slate-700 text-xs mb-2 block">Curve Amount (-500 to 500)</Label>
                        <div className="flex gap-2 items-center">
                          <Input
                            type="range"
                            min="-500"
                            max="500"
                            step="5"
                            defaultValue="0"
                            onChange={(e) => updateMultipleObjectsProperty('properties', { curve_amount: parseFloat(e.target.value) })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Spacing - for rows */}
                    {hasRows && (
                      <div>
                        <Label className="text-slate-700 text-xs mb-2 block">Spacing (0-50)</Label>
                        <div className="flex gap-2 items-center">
                          <Input
                            type="range"
                            min="0"
                            max="50"
                            step="2"
                            defaultValue="8"
                            onChange={(e) => updateMultipleObjectsProperty('properties', { spacing: parseInt(e.target.value) })}
                            className="flex-1"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Section Divider - for rows */}
                    {hasRows && (
                      <div className="pt-3 border-t">
                        <div className="text-xs font-semibold text-slate-600 mb-3">Section Divider (Aisle)</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-slate-700 text-xs mb-2 block">Gap After Seat #</Label>
                            <Input
                              type="number"
                              min="0"
                              defaultValue="0"
                              onChange={(e) => updateMultipleObjectsProperty('properties', { section_after: parseInt(e.target.value) || 0 })}
                              placeholder="0 = no gap"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-700 text-xs mb-2 block">Gap Width (px)</Label>
                            <Input
                              type="number"
                              min="0"
                              max="200"
                              defaultValue="0"
                              onChange={(e) => updateMultipleObjectsProperty('properties', { section_gap: parseInt(e.target.value) || 0 })}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Zone Assignment */}
                    <div className="pt-3 border-t">
                      <Label className="text-slate-700 text-xs mb-2 block">Assign to Zone</Label>
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          const targetZone = e.target.value;
                          showConfirmDialog(
                            'zone',
                            selectedObjects.length,
                            `Move ${selectedObjects.length} object(s) to ${targetZone || 'No Zone'}?`,
                            () => {
                              updateMultipleObjectsProperty('properties', { zone: targetZone });
                              hideConfirmDialog();
                            }
                          );
                        }}
                        className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">No Zone (Default)</option>
                        {getAvailableZones().map(zone => (
                          <option key={zone} value={zone}>{zone}</option>
                        ))}
                      </select>
                      <div className="text-xs text-slate-500 mt-1">
                        Move all selected objects to zone
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <>
          <h3 className="text-lg font-semibold text-slate-900 mb-4">
            {selectedObj ? 'Properties' : 'No Selection'}
          </h3>

          {selectedObj ? (
            <div className="space-y-4">
              {/* For Text Label objects, show Text Content first */}
              {selectedObj.type === 'text' && (
                <div>
                  <Label className="text-slate-700 text-xs mb-2 block">Text Content</Label>
                  <Input
                    value={selectedObj.properties?.text_content || 'Custom Text'}
                    onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, text_content: e.target.value })}
                    placeholder="Enter text content..."
                  />
                </div>
              )}
              
              {/* Hide generic Label field for text objects and bus_layout since they use their own settings */}
              {selectedObj.type !== 'text' && selectedObj.type !== 'bus_layout' && (
              <div>
                <Label className="text-slate-700 text-xs mb-2 block">Label</Label>
                <Input
                  value={selectedObj.label}
                  className={errorField === 'label' ? 'border-red-500 ring-2 ring-red-200' : ''}
                  onChange={(e) => {
                    const newLabel = e.target.value;
                    const currentZone = selectedObj.properties?.zone || '';
                    
                    // Check for duplicates and prevent if found
                    let hasDuplicate = false;
                    
                    if (newLabel.trim()) {
                      // Check for duplicate table names in the same zone
                      if ((selectedObj.type === 'round_table' || selectedObj.type === 'rect_table') && isTableNameDuplicate(newLabel, selectedObj.id, currentZone)) {
                        showSuccessMessage({ type: 'error', text: `Table "${newLabel}" already exists${currentZone ? ` in ${currentZone}` : ''}! Please use a different name.` }, 'label');
                        hasDuplicate = true;
                      }
                      // Check for duplicate zone names
                      else if ((selectedObj.type === 'ga_zone' || selectedObj.type === 'vip_pod' || selectedObj.type === 'grouped_block' || selectedObj.type === 'zone') && isZoneNameDuplicate(newLabel, selectedObj.id)) {
                        showSuccessMessage({ type: 'error', text: `Zone "${newLabel}" already exists! Please use a different name.` }, 'label');
                        hasDuplicate = true;
                      }
                      // Check for duplicate sofa names in the same zone
                      else if (selectedObj.type === 'sofa' && isSofaNameDuplicate(newLabel, selectedObj.id, currentZone)) {
                        showSuccessMessage({ type: 'error', text: `Sofa "${newLabel}" already exists${currentZone ? ` in ${currentZone}` : ''}! Please use a different name.` }, 'label');
                        hasDuplicate = true;
                      }
                      // Check for duplicate single seat names in the same zone
                      else if (selectedObj.type === 'seat' && isSingleSeatNameDuplicate(newLabel, selectedObj.id, currentZone)) {
                        showSuccessMessage({ type: 'error', text: `Seat "${newLabel}" already exists${currentZone ? ` in ${currentZone}` : ''}! Please use a different name.` }, 'label');
                        hasDuplicate = true;
                      }
                    }
                    
                    // Only prevent update if duplicate found AND label is not shorter (allow deletion)
                    if (hasDuplicate && newLabel.length >= selectedObj.label.length) {
                      return;
                    }
                    
                    updateObjectProperty('label', newLabel);
                  }}
                />
              </div>
              )}

              {/* Show width/height only for non-row objects and non-bus_layout */}
              {!['single_row', 'multiple_rows', 'bus_layout'].includes(selectedObj.type) && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Width</Label>
                      <div className="flex gap-1">
                        <Input
                          type="number"
                          value={selectedObj.width}
                          onChange={(e) => updateObjectProperty('width', parseFloat(e.target.value))}
                          className="flex-1"
                        />
                        <button
                          onClick={() => {
                            const defaultWidth = TOOL_CATEGORIES[Object.keys(TOOL_CATEGORIES).find(cat => TOOL_CATEGORIES[cat].tools[selectedObj.type])]?.tools[selectedObj.type]?.width || 100;
                            updateObjectProperty('width', defaultWidth);
                          }}
                          className="px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded"
                          title="Reset to default"
                        >↺</button>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Height</Label>
                      <div className="flex gap-1">
                        <Input
                          type="number"
                          value={selectedObj.height}
                          onChange={(e) => updateObjectProperty('height', parseFloat(e.target.value))}
                          className="flex-1"
                        />
                        <button
                          onClick={() => {
                            const defaultHeight = TOOL_CATEGORIES[Object.keys(TOOL_CATEGORIES).find(cat => TOOL_CATEGORIES[cat].tools[selectedObj.type])]?.tools[selectedObj.type]?.height || 100;
                            updateObjectProperty('height', defaultHeight);
                          }}
                          className="px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded"
                          title="Reset to default"
                        >↺</button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Screen positioning buttons - only for screen objects */}
                  {selectedObj.type === 'screen' && (
                    <div className="mt-3 space-y-2">
                      <Label className="text-slate-700 text-xs mb-2 block">Screen Size & Position</Label>
                      
                      {/* Row 1: Fit to Row Width + Fit to Center */}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            // Find all rows and calculate the maximum width
                            const rows = canvasObjects.filter(o => o.type === 'single_row' || o.type === 'multiple_rows');
                            if (rows.length === 0) {
                              showSuccessMessage({ type: 'info', text: 'No rows found on canvas' });
                              return;
                            }
                            // Find the widest row
                            let maxRowWidth = 0;
                            rows.forEach(row => {
                              const rowWidth = row.width || 0;
                              if (rowWidth > maxRowWidth) maxRowWidth = rowWidth;
                            });
                            if (maxRowWidth > 0) {
                              updateObjectProperty('width', maxRowWidth);
                              showSuccessMessage({ type: 'success', text: `Width: ${maxRowWidth}px` });
                            }
                          }}
                          className="px-2 py-2 text-xs bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-lg font-medium flex items-center justify-center gap-1 transition-colors"
                          title="Set screen width to match the widest row"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                          </svg>
                          Fit Row Width
                        </button>
                        
                        <button
                          onClick={() => {
                            // Find all rows to center the screen above them
                            const rows = canvasObjects.filter(o => o.type === 'single_row' || o.type === 'multiple_rows');
                            if (rows.length === 0) {
                              showSuccessMessage({ type: 'info', text: 'No rows found on canvas' });
                              return;
                            }
                            // Find the center x position of all rows
                            let minX = Infinity, maxX = 0;
                            rows.forEach(row => {
                              const rowLeft = row.x || 0;
                              const rowRight = rowLeft + (row.width || 0);
                              if (rowLeft < minX) minX = rowLeft;
                              if (rowRight > maxX) maxX = rowRight;
                            });
                            const centerX = minX + (maxX - minX) / 2;
                            const screenWidth = selectedObj.width || 400;
                            const newX = centerX - screenWidth / 2;
                            updateObjectProperty('x', Math.max(0, newX));
                            showSuccessMessage({ type: 'success', text: 'Screen centered above rows' });
                          }}
                          className="px-2 py-2 text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg font-medium flex items-center justify-center gap-1 transition-colors"
                          title="Center screen horizontally above the rows"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 12h16M12 4v16" />
                          </svg>
                          Fit Center
                        </button>
                      </div>
                      
                      {/* Row 2: Fit Small + Reset Default */}
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            // Set to a compact size
                            updateObjectProperty('width', 200);
                            updateObjectProperty('height', 40);
                            showSuccessMessage({ type: 'success', text: 'Screen set to small size (200x40)' });
                          }}
                          className="px-2 py-2 text-xs bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-lg font-medium flex items-center justify-center gap-1 transition-colors"
                          title="Set screen to a smaller size"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                          </svg>
                          Fit Small
                        </button>
                        
                        <button
                          onClick={() => {
                            // Reset to default values
                            const defaultWidth = 400;
                            const defaultHeight = 60;
                            updateObjectProperty('width', defaultWidth);
                            updateObjectProperty('height', defaultHeight);
                            showSuccessMessage({ type: 'success', text: 'Screen reset to default (400x60)' });
                          }}
                          className="px-2 py-2 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium flex items-center justify-center gap-1 transition-colors"
                          title="Reset screen to default size"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Reset Default
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Rotation - Hide for bus layouts */}
              {selectedObj.type !== 'bus_layout' && (
                <div>
                  <Label className="text-slate-700 text-xs mb-2 block">Rotation (°)</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={selectedObj.rotation}
                      onChange={(e) => updateObjectProperty('rotation', parseFloat(e.target.value))}
                      className="flex-1"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateObjectProperty('rotation', (selectedObj.rotation + 45) % 360)}
                      title="Rotate 45°"
                    >
                      <RotateCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Show color only for non-row, non-text, non-zone, non-sofa, non-bus objects */}
              {!['single_row', 'multiple_rows', 'text', 'zone', 'seat', 'sofa', 'bus_layout'].includes(selectedObj.type) && (
                <div>
                  <Label className="text-slate-700 text-xs mb-2 block">Background Color</Label>
                  <Input
                    type="color"
                    value={selectedObj.color}
                    onChange={(e) => updateObjectProperty('color', e.target.value)}
                    className="h-10 cursor-pointer"
                  />
                </div>
              )}

              {selectedObj.type === 'single_row' && (
                <div className="space-y-3 pt-3 border-t">
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Row Label</Label>
                    <Input
                      value={selectedObj.properties?.row_label ?? 'A'}
                      className={errorField === 'row_label' ? 'border-red-500 ring-2 ring-red-200' : ''}
                      onChange={(e) => {
                        const newLabel = e.target.value.toUpperCase();
                        // Allow empty for typing - will validate on blur
                        if (newLabel !== '' && isRowLabelDuplicate(newLabel, selectedObj.id, selectedObj.properties?.zone || '')) {
                          showSuccessMessage({ type: 'error', text: `Row "${newLabel}" already exists in this zone! Use different label or change zone.` }, 'row_label');
                          // Still update so user can see their input
                        }
                        updateObjectProperty('properties', { ...selectedObj.properties, row_label: newLabel });
                      }}
                      onBlur={(e) => {
                        // Restore default if empty on blur
                        if (!e.target.value.trim()) {
                          const nextLabel = getNextAvailableRowLabel(selectedObj.properties?.zone || '');
                          updateObjectProperty('properties', { ...selectedObj.properties, row_label: nextLabel });
                        }
                      }}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Seat Count</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={selectedObj.properties?.seat_count ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty value for typing
                        if (value === '') {
                          setCanvasObjects(prev => prev.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, properties: { ...obj.properties, seat_count: '' } }
                              : obj
                          ));
                          return;
                        }
                        const count = parseInt(value);
                        if (isNaN(count) || count < 1) return;
                        const curve = selectedObj.properties?.curve_amount || 0;
                        const spacing = selectedObj.properties?.spacing || 8;
                        const sectionGap = selectedObj.properties?.section_gap || 0;
                        const sectionAfter = selectedObj.properties?.section_after || 0;
                        const seats = [];
                        let xOffset = 0;
                        for (let i = 0; i < count; i++) {
                          // Add gap after section_after seats
                          if (sectionAfter > 0 && i === sectionAfter) {
                            xOffset += sectionGap;
                          }
                          const centerOffset = (count - 1) / 2;
                          const distanceFromCenter = i - centerOffset;
                          const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                          seats.push({
                            id: `seat_${i}`,
                            seat_number: i + 1,
                            x: i * (36 + spacing) + xOffset,
                            y: curveY,
                            bookable: true
                          });
                        }
                        const newWidth = count * (36 + spacing) + (sectionGap || 0);
                        const maxCurve = curve !== 0 ? Math.pow((count - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                        const newHeight = 36 + maxCurve + 20;
                        const newObjects = canvasObjects.map(obj =>
                          obj.id === selectedObj.id
                            ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, seat_count: count }, seats }
                            : obj
                        );
                        setCanvasObjects(newObjects);
                      }}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Spacing (0-50)</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="range"
                        min="0"
                        max="50"
                        step="2"
                        value={selectedObj.properties?.spacing || 8}
                        onChange={(e) => {
                          const spacing = parseInt(e.target.value);
                          const count = selectedObj.properties?.seat_count || 10;
                          const curve = selectedObj.properties?.curve_amount || 0;
                          const sectionGap = selectedObj.properties?.section_gap || 0;
                          const sectionAfter = selectedObj.properties?.section_after || 0;
                          const seats = [];
                          let xOffset = 0;
                          for (let i = 0; i < count; i++) {
                            if (sectionAfter > 0 && i === sectionAfter) {
                              xOffset += sectionGap;
                            }
                            const centerOffset = (count - 1) / 2;
                            const distanceFromCenter = i - centerOffset;
                            const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                            seats.push({
                              id: `seat_${i}`,
                              seat_number: i + 1,
                              x: i * (36 + spacing) + xOffset,
                              y: curveY,
                              bookable: true
                            });
                          }
                          const newWidth = count * (36 + spacing) + (sectionGap || 0);
                          const maxCurve = curve !== 0 ? Math.pow((count - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                          const newHeight = 36 + maxCurve + 20;
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, spacing }, seats }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }}
                        className="flex-1"
                      />
                      <span className="text-xs font-mono w-12 text-center">{selectedObj.properties?.spacing || 8}px</span>
                      <button
                        onClick={() => {
                          const spacing = 8; // Default
                          const count = selectedObj.properties?.seat_count || 10;
                          const curve = selectedObj.properties?.curve_amount || 0;
                          const sectionGap = selectedObj.properties?.section_gap || 0;
                          const sectionAfter = selectedObj.properties?.section_after || 0;
                          const seats = [];
                          let xOffset = 0;
                          for (let i = 0; i < count; i++) {
                            if (sectionAfter > 0 && i === sectionAfter) xOffset += sectionGap;
                            const centerOffset = (count - 1) / 2;
                            const distanceFromCenter = i - centerOffset;
                            const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                            seats.push({ id: `seat_${i}`, seat_number: i + 1, x: i * (36 + spacing) + xOffset, y: curveY, bookable: true });
                          }
                          const newWidth = count * (36 + spacing) + (sectionGap || 0);
                          const maxCurve = curve !== 0 ? Math.pow((count - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                          const newHeight = 36 + maxCurve + 20;
                          const newObjects = canvasObjects.map(obj => obj.id === selectedObj.id ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, spacing }, seats } : obj);
                          setCanvasObjects(newObjects);
                        }}
                        className="px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded"
                        title="Reset to default (8px)"
                      >↺</button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Curve Amount (-500 to 500)</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="range"
                        min="-500"
                        max="500"
                        step="5"
                        value={selectedObj.properties?.curve_amount || 0}
                        onChange={(e) => {
                          const curve = parseFloat(e.target.value);
                          const count = selectedObj.properties?.seat_count || 10;
                          const spacing = selectedObj.properties?.spacing || 8;
                          const sectionGap = selectedObj.properties?.section_gap || 0;
                          const sectionAfter = selectedObj.properties?.section_after || 0;
                          const seats = [];
                          let xOffset = 0;
                          for (let i = 0; i < count; i++) {
                            if (sectionAfter > 0 && i === sectionAfter) {
                              xOffset += sectionGap;
                            }
                            const centerOffset = (count - 1) / 2;
                            const distanceFromCenter = i - centerOffset;
                            const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                            seats.push({
                              id: `seat_${i}`,
                              seat_number: i + 1,
                              x: i * (36 + spacing) + xOffset,
                              y: curveY,
                              bookable: true
                            });
                          }
                          const newWidth = count * (36 + spacing) + (sectionGap || 0);
                          const maxCurve = curve !== 0 ? Math.pow((count - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                          const newHeight = 36 + maxCurve + 20;
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, curve_amount: curve }, seats }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }}
                        className="flex-1"
                      />
                      <span className="text-xs font-mono w-12 text-center">{selectedObj.properties?.curve_amount || 0}</span>
                      <button
                        onClick={() => {
                          const curve = 0; // Default
                          const count = selectedObj.properties?.seat_count || 10;
                          const spacing = selectedObj.properties?.spacing || 8;
                          const sectionGap = selectedObj.properties?.section_gap || 0;
                          const sectionAfter = selectedObj.properties?.section_after || 0;
                          const seats = [];
                          let xOffset = 0;
                          for (let i = 0; i < count; i++) {
                            if (sectionAfter > 0 && i === sectionAfter) xOffset += sectionGap;
                            const centerOffset = (count - 1) / 2;
                            const distanceFromCenter = i - centerOffset;
                            const curveY = 0;
                            seats.push({ id: `seat_${i}`, seat_number: i + 1, x: i * (36 + spacing) + xOffset, y: curveY, bookable: true });
                          }
                          const newWidth = count * (36 + spacing) + (sectionGap || 0);
                          const newHeight = 36 + 20;
                          const newObjects = canvasObjects.map(obj => obj.id === selectedObj.id ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, curve_amount: curve }, seats } : obj);
                          setCanvasObjects(newObjects);
                        }}
                        className="px-2 py-1 text-xs bg-slate-100 hover:bg-slate-200 rounded"
                        title="Reset to default (0)"
                      >↺</button>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Negative curves down, positive curves up
                    </div>
                  </div>
                  
                  {/* Section Divider */}
                  <div className="pt-3 border-t">
                    <div className="text-xs font-semibold text-slate-600 mb-3">Section Divider (Aisle)</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700 text-xs mb-2 block">Gap After Seat #</Label>
                        <Input
                          type="number"
                          min="0"
                          max={selectedObj.properties?.seat_count || 10}
                          value={selectedObj.properties?.section_after || 0}
                          onChange={(e) => {
                            const sectionAfter = parseInt(e.target.value) || 0;
                            const count = selectedObj.properties?.seat_count || 10;
                            const curve = selectedObj.properties?.curve_amount || 0;
                            const spacing = selectedObj.properties?.spacing || 8;
                            const sectionGap = selectedObj.properties?.section_gap || 0;
                            const seats = [];
                            let xOffset = 0;
                            for (let i = 0; i < count; i++) {
                              if (sectionAfter > 0 && i === sectionAfter) {
                                xOffset += sectionGap;
                              }
                              const centerOffset = (count - 1) / 2;
                              const distanceFromCenter = i - centerOffset;
                              const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                              seats.push({
                                id: `seat_${i}`,
                                seat_number: i + 1,
                                x: i * (36 + spacing) + xOffset,
                                y: curveY,
                                bookable: true
                              });
                            }
                            const newWidth = count * (36 + spacing) + (sectionGap || 0);
                            const maxCurve = curve !== 0 ? Math.pow((count - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                            const newHeight = 36 + maxCurve + 20;
                            const newObjects = canvasObjects.map(obj =>
                              obj.id === selectedObj.id
                                ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, section_after: sectionAfter }, seats }
                                : obj
                            );
                            setCanvasObjects(newObjects);
                          }}
                          placeholder="0 = no gap"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 text-xs mb-2 block">Gap Width (px)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="200"
                          value={selectedObj.properties?.section_gap || 0}
                          onChange={(e) => {
                            const sectionGap = parseInt(e.target.value) || 0;
                            const count = selectedObj.properties?.seat_count || 10;
                            const curve = selectedObj.properties?.curve_amount || 0;
                            const spacing = selectedObj.properties?.spacing || 8;
                            const sectionAfter = selectedObj.properties?.section_after || 0;
                            const seats = [];
                            let xOffset = 0;
                            for (let i = 0; i < count; i++) {
                              if (sectionAfter > 0 && i === sectionAfter) {
                                xOffset += sectionGap;
                              }
                              const centerOffset = (count - 1) / 2;
                              const distanceFromCenter = i - centerOffset;
                              const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                              seats.push({
                                id: `seat_${i}`,
                                seat_number: i + 1,
                                x: i * (36 + spacing) + xOffset,
                                y: curveY,
                                bookable: true
                              });
                            }
                            const newWidth = count * (36 + spacing) + sectionGap;
                            const maxCurve = curve !== 0 ? Math.pow((count - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                            const newHeight = 36 + maxCurve + 20;
                            const newObjects = canvasObjects.map(obj =>
                              obj.id === selectedObj.id
                                ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, section_gap: sectionGap }, seats }
                                : obj
                            );
                            setCanvasObjects(newObjects);
                          }}
                        />
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Creates a pathway/aisle between seats
                    </div>
                  </div>
                  
                  {/* Zone Assignment */}
                  <div className="pt-3 border-t">
                    <Label className="text-slate-700 text-xs mb-2 block">Assign to Zone</Label>
                    <select
                      value={selectedObj.properties?.zone || ''}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, zone: e.target.value })}
                      className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">No Zone (Default)</option>
                      {getAvailableZones().map(zone => (
                        <option key={zone} value={zone}>{zone}</option>
                      ))}
                    </select>
                    <div className="text-xs text-slate-500 mt-1">
                      Rows in same zone share seat numbering
                    </div>
                  </div>
                  
                  {/* Seat Color */}
                  <div className="pt-3 border-t">
                    <Label className="text-slate-700 text-xs mb-2 block">Seat Color</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="color"
                        value={selectedObj.properties?.seat_color || '#22c55e'}
                        onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: e.target.value })}
                        className="h-10 w-16 cursor-pointer"
                      />
                      <div className="flex gap-1 flex-wrap flex-1">
                        <button
                          className={`w-6 h-6 rounded border-2 shadow hover:ring-2 hover:ring-indigo-500 ${selectedObj.properties?.seat_color === '#22c55e' ? 'ring-2 ring-indigo-500' : 'border-white'}`}
                          style={{ backgroundColor: '#22c55e' }}
                          title="Green"
                          onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: '#22c55e' })}
                        />
                        <button
                          className={`w-6 h-6 rounded border-2 shadow hover:ring-2 hover:ring-indigo-500 ${selectedObj.properties?.seat_color === '#ef4444' ? 'ring-2 ring-indigo-500' : 'border-white'}`}
                          style={{ backgroundColor: '#ef4444' }}
                          title="Red"
                          onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: '#ef4444' })}
                        />
                        <button
                          className={`w-6 h-6 rounded border-2 shadow hover:ring-2 hover:ring-indigo-500 ${selectedObj.properties?.seat_color === '#eab308' ? 'ring-2 ring-indigo-500' : 'border-white'}`}
                          style={{ backgroundColor: '#eab308' }}
                          title="Yellow"
                          onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: '#eab308' })}
                        />
                        <button
                          className={`w-6 h-6 rounded border-2 shadow hover:ring-2 hover:ring-indigo-500 ${selectedObj.properties?.seat_color === '#3b82f6' ? 'ring-2 ring-indigo-500' : 'border-white'}`}
                          style={{ backgroundColor: '#3b82f6' }}
                          title="Blue"
                          onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: '#3b82f6' })}
                        />
                        <button
                          className={`w-6 h-6 rounded border-2 shadow hover:ring-2 hover:ring-indigo-500 ${selectedObj.properties?.seat_color === '#a855f7' ? 'ring-2 ring-indigo-500' : 'border-white'}`}
                          style={{ backgroundColor: '#a855f7' }}
                          title="Purple"
                          onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: '#a855f7' })}
                        />
                        <button
                          className="w-6 h-6 rounded border-2 shadow hover:ring-2 hover:ring-indigo-500 border-slate-300 flex items-center justify-center text-xs bg-slate-100"
                          title="Reset to default green"
                          onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: '#22c55e' })}
                        >↺</button>
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">This color shows in checkout for available seats</div>
                  </div>
                </div>
              )}

              {selectedObj.type === 'multiple_rows' && (
                <div className="space-y-3 pt-3 border-t">
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Start Label</Label>
                    <Input
                      value={selectedObj.properties?.start_label || 'A'}
                      className={errorField === 'start_label' ? 'border-red-500 ring-2 ring-red-200' : ''}
                      onChange={(e) => {
                        const newLabel = e.target.value.toUpperCase();
                        const rowCount = selectedObj.properties?.row_count || 3;
                        const zone = selectedObj.properties?.zone || '';
                        const conflicts = wouldMultiRowConflict(newLabel, rowCount, selectedObj.id, zone);
                        if (conflicts.length > 0) {
                          showSuccessMessage({ type: 'error', text: `Rows "${conflicts.join(', ')}" already exist in this zone!` }, 'start_label');
                          return;
                        }
                        updateObjectProperty('properties', { ...selectedObj.properties, start_label: newLabel });
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Rows</Label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={selectedObj.properties?.row_count || 3}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          // Allow empty value temporarily but don't process
                          if (rawValue === '' || rawValue === null) {
                            return;
                          }
                          const rowCount = parseInt(rawValue);
                          // Validate: must be a positive number
                          if (isNaN(rowCount) || rowCount < 1) {
                            return;
                          }
                          // Cap at max 50 rows
                          const validRowCount = Math.min(rowCount, 50);
                          const startLabel = selectedObj.properties?.start_label || 'A';
                          const zone = selectedObj.properties?.zone || '';
                          const conflicts = wouldMultiRowConflict(startLabel, validRowCount, selectedObj.id, zone);
                          if (conflicts.length > 0) {
                            showSuccessMessage({ type: 'error', text: `Rows "${conflicts.join(', ')}" already exist in this zone!` }, 'row_count');
                            return;
                          }
                          const seatCount = selectedObj.properties?.seat_count || 10;
                          const spacing = selectedObj.properties?.spacing || 8;
                          const curve = selectedObj.properties?.curve_amount || 0;
                          const sectionGap = selectedObj.properties?.section_gap || 0;
                          const sectionAfter = selectedObj.properties?.section_after || 0;
                          const rowSpacing = 50;
                          const seats = [];
                          for (let row = 0; row < validRowCount; row++) {
                            let xOffset = 0;
                            for (let i = 0; i < seatCount; i++) {
                              if (sectionAfter > 0 && i === sectionAfter) {
                                xOffset += sectionGap;
                              }
                              const centerOffset = (seatCount - 1) / 2;
                              const distanceFromCenter = i - centerOffset;
                              const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                              seats.push({
                                id: `row${row}_seat_${i}`,
                                row: row,
                                seat_number: i + 1,
                                x: i * (36 + spacing) + xOffset,
                                y: row * rowSpacing + curveY,
                                bookable: true
                              });
                            }
                          }
                          const newWidth = seatCount * (36 + spacing) + (sectionGap || 0);
                          const maxCurve = curve !== 0 ? Math.pow((seatCount - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                          const newHeight = validRowCount * rowSpacing + maxCurve;
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, row_count: validRowCount }, seats }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Seats/Row</Label>
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        value={selectedObj.properties?.seat_count || 10}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          // Allow empty value temporarily but don't process
                          if (rawValue === '' || rawValue === null) {
                            return;
                          }
                          const seatCount = parseInt(rawValue);
                          // Validate: must be a positive number
                          if (isNaN(seatCount) || seatCount < 1) {
                            return;
                          }
                          // Allow up to 100 seats per row (increased from 50)
                          const validSeatCount = Math.min(seatCount, 100);
                          const rowCount = selectedObj.properties?.row_count || 3;
                          const spacing = selectedObj.properties?.spacing || 8;
                          const curve = selectedObj.properties?.curve_amount || 0;
                          const sectionGap = selectedObj.properties?.section_gap || 0;
                          const sectionAfter = selectedObj.properties?.section_after || 0;
                          const rowSpacing = 50;
                          const seats = [];
                          for (let row = 0; row < rowCount; row++) {
                            let xOffset = 0;
                            for (let i = 0; i < validSeatCount; i++) {
                              if (sectionAfter > 0 && i === sectionAfter) {
                                xOffset += sectionGap;
                              }
                              const centerOffset = (validSeatCount - 1) / 2;
                              const distanceFromCenter = i - centerOffset;
                              const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                              seats.push({
                                id: `row${row}_seat_${i}`,
                                row: row,
                                seat_number: i + 1,
                                x: i * (36 + spacing) + xOffset,
                                y: row * rowSpacing + curveY,
                                bookable: true
                              });
                            }
                          }
                          const newWidth = validSeatCount * (36 + spacing) + (sectionGap || 0);
                          const maxCurve = curve !== 0 ? Math.pow((validSeatCount - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                          const newHeight = rowCount * rowSpacing + maxCurve;
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, seat_count: validSeatCount }, seats }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Spacing (0-50)</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="range"
                        min="0"
                        max="50"
                        step="2"
                        value={selectedObj.properties?.spacing || 8}
                        onChange={(e) => {
                          const spacing = parseInt(e.target.value);
                          const seatCount = selectedObj.properties?.seat_count || 10;
                          const rowCount = selectedObj.properties?.row_count || 3;
                          const curve = selectedObj.properties?.curve_amount || 0;
                          const sectionGap = selectedObj.properties?.section_gap || 0;
                          const sectionAfter = selectedObj.properties?.section_after || 0;
                          const rowSpacing = 50;
                          const seats = [];
                          for (let row = 0; row < rowCount; row++) {
                            let xOffset = 0;
                            for (let i = 0; i < seatCount; i++) {
                              if (sectionAfter > 0 && i === sectionAfter) {
                                xOffset += sectionGap;
                              }
                              const centerOffset = (seatCount - 1) / 2;
                              const distanceFromCenter = i - centerOffset;
                              const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                              seats.push({
                                id: `row${row}_seat_${i}`,
                                row: row,
                                seat_number: i + 1,
                                x: i * (36 + spacing) + xOffset,
                                y: row * rowSpacing + curveY,
                                bookable: true
                              });
                            }
                          }
                          const newWidth = seatCount * (36 + spacing) + (sectionGap || 0);
                          const maxCurve = curve !== 0 ? Math.pow((seatCount - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                          const newHeight = rowCount * rowSpacing + maxCurve;
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, spacing }, seats }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }}
                        className="flex-1"
                      />
                      <span className="text-xs font-mono w-12 text-center">{selectedObj.properties?.spacing || 8}px</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Curve Amount (-500 to 500)</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="range"
                        min="-500"
                        max="500"
                        step="5"
                        value={selectedObj.properties?.curve_amount || 0}
                        onChange={(e) => {
                          const curve = parseFloat(e.target.value);
                          const seatCount = selectedObj.properties?.seat_count || 10;
                          const rowCount = selectedObj.properties?.row_count || 3;
                          const spacing = selectedObj.properties?.spacing || 8;
                          const sectionGap = selectedObj.properties?.section_gap || 0;
                          const sectionAfter = selectedObj.properties?.section_after || 0;
                          const rowSpacing = 50;
                          const seats = [];
                          for (let row = 0; row < rowCount; row++) {
                            let xOffset = 0;
                            for (let i = 0; i < seatCount; i++) {
                              if (sectionAfter > 0 && i === sectionAfter) {
                                xOffset += sectionGap;
                              }
                              const centerOffset = (seatCount - 1) / 2;
                              const distanceFromCenter = i - centerOffset;
                              const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                              seats.push({
                                id: `row${row}_seat_${i}`,
                                row: row,
                                seat_number: i + 1,
                                x: i * (36 + spacing) + xOffset,
                                y: row * rowSpacing + curveY,
                                bookable: true
                              });
                            }
                          }
                          const newWidth = seatCount * (36 + spacing) + (sectionGap || 0);
                          const maxCurve = curve !== 0 ? Math.pow((seatCount - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                          const newHeight = rowCount * rowSpacing + maxCurve;
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, curve_amount: curve }, seats }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }}
                        className="flex-1"
                      />
                      <span className="text-xs font-mono w-12 text-center">{selectedObj.properties?.curve_amount || 0}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Negative curves down, positive curves up
                    </div>
                  </div>
                  
                  {/* Section Divider for Multi-row */}
                  <div className="pt-3 border-t">
                    <div className="text-xs font-semibold text-slate-600 mb-3">Section Divider (Aisle)</div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-700 text-xs mb-2 block">Gap After Seat #</Label>
                        <Input
                          type="number"
                          min="0"
                          max={selectedObj.properties?.seat_count || 10}
                          value={selectedObj.properties?.section_after || 0}
                          onChange={(e) => {
                            const sectionAfter = parseInt(e.target.value) || 0;
                            const seatCount = selectedObj.properties?.seat_count || 10;
                            const rowCount = selectedObj.properties?.row_count || 3;
                            const curve = selectedObj.properties?.curve_amount || 0;
                            const spacing = selectedObj.properties?.spacing || 8;
                            const sectionGap = selectedObj.properties?.section_gap || 0;
                            const rowSpacing = 50;
                            const seats = [];
                            for (let row = 0; row < rowCount; row++) {
                              let xOffset = 0;
                              for (let i = 0; i < seatCount; i++) {
                                if (sectionAfter > 0 && i === sectionAfter) {
                                  xOffset += sectionGap;
                                }
                                const centerOffset = (seatCount - 1) / 2;
                                const distanceFromCenter = i - centerOffset;
                                const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                                seats.push({
                                  id: `row${row}_seat_${i}`,
                                  row: row,
                                  seat_number: i + 1,
                                  x: i * (36 + spacing) + xOffset,
                                  y: row * rowSpacing + curveY,
                                  bookable: true
                                });
                              }
                            }
                            const newWidth = seatCount * (36 + spacing) + (sectionGap || 0);
                            const maxCurve = curve !== 0 ? Math.pow((seatCount - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                            const newHeight = rowCount * rowSpacing + maxCurve;
                            const newObjects = canvasObjects.map(obj =>
                              obj.id === selectedObj.id
                                ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, section_after: sectionAfter }, seats }
                                : obj
                            );
                            setCanvasObjects(newObjects);
                          }}
                          placeholder="0 = no gap"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 text-xs mb-2 block">Gap Width (px)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="200"
                          value={selectedObj.properties?.section_gap || 0}
                          onChange={(e) => {
                            const sectionGap = parseInt(e.target.value) || 0;
                            const seatCount = selectedObj.properties?.seat_count || 10;
                            const rowCount = selectedObj.properties?.row_count || 3;
                            const curve = selectedObj.properties?.curve_amount || 0;
                            const spacing = selectedObj.properties?.spacing || 8;
                            const sectionAfter = selectedObj.properties?.section_after || 0;
                            const rowSpacing = 50;
                            const seats = [];
                            for (let row = 0; row < rowCount; row++) {
                              let xOffset = 0;
                              for (let i = 0; i < seatCount; i++) {
                                if (sectionAfter > 0 && i === sectionAfter) {
                                  xOffset += sectionGap;
                                }
                                const centerOffset = (seatCount - 1) / 2;
                                const distanceFromCenter = i - centerOffset;
                                const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 50) : 0;
                                seats.push({
                                  id: `row${row}_seat_${i}`,
                                  row: row,
                                  seat_number: i + 1,
                                  x: i * (36 + spacing) + xOffset,
                                  y: row * rowSpacing + curveY,
                                  bookable: true
                                });
                              }
                            }
                            const newWidth = seatCount * (36 + spacing) + sectionGap;
                            const maxCurve = curve !== 0 ? Math.pow((seatCount - 1) / 2, 2) * Math.abs(curve / 50) : 0;
                            const newHeight = rowCount * rowSpacing + maxCurve;
                            const newObjects = canvasObjects.map(obj =>
                              obj.id === selectedObj.id
                                ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, section_gap: sectionGap }, seats }
                                : obj
                            );
                            setCanvasObjects(newObjects);
                          }}
                        />
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Creates a pathway/aisle between seats
                    </div>
                  </div>
                  
                  {/* Zone Assignment for Multi-row */}
                  <div className="pt-3 border-t">
                    <Label className="text-slate-700 text-xs mb-2 block">Assign to Zone</Label>
                    <select
                      value={selectedObj.properties?.zone || ''}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, zone: e.target.value })}
                      className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">No Zone (Default)</option>
                      {getAvailableZones().map(zone => (
                        <option key={zone} value={zone}>{zone}</option>
                      ))}
                    </select>
                    <div className="text-xs text-slate-500 mt-1">
                      Rows in same zone share seat numbering
                    </div>
                  </div>
                </div>
              )}

              {/* Bus Layout Properties */}
              {selectedObj.type === 'bus_layout' && (
                <div className="space-y-3 pt-3 border-t">
                  {/* Header */}
                  <div className="flex items-center gap-2">
                    <Bus className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium text-slate-700">Bus Layout Settings</span>
                  </div>
                  
                  {/* Exclusive Mode Banner */}
                  <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-800">
                      <strong>Exclusive Mode:</strong> Bus layout cannot coexist with other seating objects.
                    </p>
                  </div>
                  
                  {/* Total Capacity - NOW AT TOP */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-blue-800">Total Capacity</span>
                      <span className="text-lg font-bold text-blue-600">{selectedObj.seats?.length || 0} seats</span>
                    </div>
                    <div className="flex gap-4 text-xs text-slate-600">
                      <span>Rows: {selectedObj.properties?.row_count || 9}</span>
                      <span>×</span>
                      <span>{(selectedObj.properties?.left_seats || 2) + (selectedObj.properties?.right_seats || 2)} per row</span>
                    </div>
                  </div>
                  
                  {/* Row and Pattern Controls */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Rows</Label>
                      <Input
                        type="number"
                        min="1"
                        max="60"
                        value={selectedObj.properties?.row_count ?? 9}
                        onChange={(e) => {
                          const rawValue = e.target.value;
                          
                          // Allow empty input while typing
                          if (rawValue === '') {
                            // Just update the display, will validate on blur
                            const newObjects = canvasObjects.map(obj =>
                              obj.id === selectedObj.id
                                ? { ...obj, properties: { ...obj.properties, row_count: '' } }
                                : obj
                            );
                            setCanvasObjects(newObjects);
                            return;
                          }
                          
                          const rowCount = parseInt(rawValue);
                          if (isNaN(rowCount)) return;
                          
                          // Allow any positive number within range
                          const validRowCount = Math.max(1, Math.min(rowCount, 60));
                          
                          const leftSeats = selectedObj.properties?.left_seats || 2;
                          const rightSeats = selectedObj.properties?.right_seats || 2;
                          const seatWidth = 40;
                          const seatSpacing = 6;
                          const rowSpacing = 48;
                          const aisleWidth = 35;
                          
                          // Use per-side row counts if available, otherwise use overall row_count
                          const leftRowCount = selectedObj.properties?.left_row_count ?? validRowCount;
                          const rightRowCount = selectedObj.properties?.right_row_count ?? validRowCount;
                          const maxRowCount = Math.max(leftRowCount, rightRowCount);
                          
                          const seats = [];
                          for (let row = 0; row < maxRowCount; row++) {
                            const rowLabel = row + 1;
                            let seatIndex = 0;
                            
                            // Only add left seats if this row is within left_row_count
                            if (row < leftRowCount) {
                              for (let i = 0; i < leftSeats; i++) {
                                const seatLetter = String.fromCharCode(65 + seatIndex);
                                seats.push({
                                  id: `row${row}_seat_${seatIndex}`,
                                  row: row,
                                  seat_number: seatIndex + 1,
                                  seat_label: `${rowLabel}${seatLetter}`,
                                  x: i * (seatWidth + seatSpacing),
                                  y: row * rowSpacing + 70,
                                  side: 'left',
                                  status: 'available',
                                  bookable: true,
                                  group_id: null
                                });
                                seatIndex++;
                              }
                            }
                            
                            // Only add right seats if this row is within right_row_count
                            if (row < rightRowCount) {
                              for (let i = 0; i < rightSeats; i++) {
                                const seatLetter = String.fromCharCode(65 + seatIndex);
                                seats.push({
                                  id: `row${row}_seat_${seatIndex}`,
                                  row: row,
                                  seat_number: seatIndex + 1,
                                  seat_label: `${rowLabel}${seatLetter}`,
                                  x: leftSeats * (seatWidth + seatSpacing) + aisleWidth + i * (seatWidth + seatSpacing),
                                  y: row * rowSpacing + 70,
                                  side: 'right',
                                  status: 'available',
                                  bookable: true,
                                  group_id: null
                                });
                                seatIndex++;
                              }
                            }
                          }
                          
                          const totalWidth = leftSeats * (seatWidth + seatSpacing) + aisleWidth + rightSeats * (seatWidth + seatSpacing) - seatSpacing;
                          const totalHeight = maxRowCount * rowSpacing + 70 + 30;
                          
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, width: totalWidth, height: totalHeight, properties: { ...obj.properties, row_count: validRowCount, left_row_count: leftRowCount, right_row_count: rightRowCount }, seats }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }}
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Seat Pattern</Label>
                      <select
                        value={selectedObj.properties?.seat_pattern || '2x2'}
                        onChange={(e) => {
                          const pattern = e.target.value;
                          const [leftSeats, rightSeats] = pattern.split('x').map(Number);
                          const rowCount = selectedObj.properties?.row_count || 9;
                          const seatWidth = 40;
                          const seatSpacing = 6;
                          const rowSpacing = 48;
                          const aisleWidth = 35;
                          
                          const seats = [];
                          for (let row = 0; row < rowCount; row++) {
                            const rowLabel = row + 1;
                            let seatIndex = 0;
                            
                            for (let i = 0; i < leftSeats; i++) {
                              const seatLetter = String.fromCharCode(65 + seatIndex);
                              seats.push({
                                id: `row${row}_seat_${seatIndex}`,
                                row: row,
                                seat_number: seatIndex + 1,
                                seat_label: `${rowLabel}${seatLetter}`,
                                x: i * (seatWidth + seatSpacing),
                                y: row * rowSpacing + 70,
                                side: 'left',
                                status: 'available',
                                bookable: true,
                                group_id: null
                              });
                              seatIndex++;
                            }
                            
                            for (let i = 0; i < rightSeats; i++) {
                              const seatLetter = String.fromCharCode(65 + seatIndex);
                              seats.push({
                                id: `row${row}_seat_${seatIndex}`,
                                row: row,
                                seat_number: seatIndex + 1,
                                seat_label: `${rowLabel}${seatLetter}`,
                                x: leftSeats * (seatWidth + seatSpacing) + aisleWidth + i * (seatWidth + seatSpacing),
                                y: row * rowSpacing + 70,
                                side: 'right',
                                status: 'available',
                                bookable: true,
                                group_id: null
                              });
                              seatIndex++;
                            }
                          }
                          
                          const totalWidth = leftSeats * (seatWidth + seatSpacing) + aisleWidth + rightSeats * (seatWidth + seatSpacing) - seatSpacing;
                          const totalHeight = rowCount * rowSpacing + 70 + 30;
                          
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { 
                                  ...obj, 
                                  width: totalWidth, 
                                  height: totalHeight, 
                                  properties: { ...obj.properties, seat_pattern: pattern, left_seats: leftSeats, right_seats: rightSeats }, 
                                  seats 
                                }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }}
                        className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="2x2">2x2 (4 seats/row)</option>
                        <option value="2x3">2x3 (5 seats/row)</option>
                        <option value="3x2">3x2 (5 seats/row)</option>
                        <option value="3x3">3x3 (6 seats/row)</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Per-Side Row Configuration */}
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-slate-700 text-xs font-medium">Row Configuration Per Side</Label>
                      <span className="text-[10px] text-slate-500">For doors/gaps</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-600 text-[10px] mb-1 block">Left Side Rows</Label>
                        <Input
                          type="number"
                          min="0"
                          max="60"
                          value={selectedObj.properties?.left_row_count ?? selectedObj.properties?.row_count ?? 9}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            // Allow empty string while typing, validate on actual number
                            if (inputValue === '') return;
                            const leftRows = Math.max(0, Math.min(60, parseInt(inputValue) || 0));
                            const rightRows = selectedObj.properties?.right_row_count ?? selectedObj.properties?.row_count ?? 9;
                            const pattern = selectedObj.properties?.seat_pattern || '2x2';
                            const [leftSeats, rightSeats] = pattern.split('x').map(Number);
                            const maxRowCount = Math.max(leftRows, rightRows);
                            
                            // Regenerate seats with new row config
                            const seatWidth = 40, seatSpacing = 6, rowSpacing = 48, aisleWidth = 35;
                            const seats = [];
                            for (let row = 0; row < maxRowCount; row++) {
                              const rowLabel = row + 1;
                              let seatIndex = 0;
                              if (row < leftRows) {
                                for (let i = 0; i < leftSeats; i++) {
                                  const seatLetter = String.fromCharCode(65 + seatIndex);
                                  seats.push({ id: `row${row}_seat_${seatIndex}`, row, seat_number: seatIndex + 1, seat_label: `${rowLabel}${seatLetter}`, x: i * (seatWidth + seatSpacing), y: row * rowSpacing + 70, side: 'left', status: 'available', bookable: true, group_id: null });
                                  seatIndex++;
                                }
                              }
                              if (row < rightRows) {
                                for (let i = 0; i < rightSeats; i++) {
                                  const seatLetter = String.fromCharCode(65 + seatIndex);
                                  seats.push({ id: `row${row}_seat_${seatIndex}`, row, seat_number: seatIndex + 1, seat_label: `${rowLabel}${seatLetter}`, x: leftSeats * (seatWidth + seatSpacing) + aisleWidth + i * (seatWidth + seatSpacing), y: row * rowSpacing + 70, side: 'right', status: 'available', bookable: true, group_id: null });
                                  seatIndex++;
                                }
                              }
                            }
                            const totalWidth = leftSeats * (seatWidth + seatSpacing) + aisleWidth + rightSeats * (seatWidth + seatSpacing) - seatSpacing;
                            const totalHeight = maxRowCount * rowSpacing + 70 + 30;
                            
                            const newObjects = canvasObjects.map(obj => obj.id === selectedObj.id ? { ...obj, width: totalWidth, height: totalHeight, properties: { ...obj.properties, left_row_count: leftRows, right_row_count: rightRows }, seats } : obj);
                            setCanvasObjects(newObjects);
                          }}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-[10px] mb-1 block">Right Side Rows</Label>
                        <Input
                          type="number"
                          min="0"
                          max="60"
                          value={selectedObj.properties?.right_row_count ?? selectedObj.properties?.row_count ?? 9}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            // Allow empty string while typing, validate on actual number
                            if (inputValue === '') return;
                            const rightRows = Math.max(0, Math.min(60, parseInt(inputValue) || 0));
                            const leftRows = selectedObj.properties?.left_row_count ?? selectedObj.properties?.row_count ?? 9;
                            const pattern = selectedObj.properties?.seat_pattern || '2x2';
                            const [leftSeats, rightSeats] = pattern.split('x').map(Number);
                            const maxRowCount = Math.max(leftRows, rightRows);
                            
                            // Regenerate seats with new row config
                            const seatWidth = 40, seatSpacing = 6, rowSpacing = 48, aisleWidth = 35;
                            const seats = [];
                            for (let row = 0; row < maxRowCount; row++) {
                              const rowLabel = row + 1;
                              let seatIndex = 0;
                              if (row < leftRows) {
                                for (let i = 0; i < leftSeats; i++) {
                                  const seatLetter = String.fromCharCode(65 + seatIndex);
                                  seats.push({ id: `row${row}_seat_${seatIndex}`, row, seat_number: seatIndex + 1, seat_label: `${rowLabel}${seatLetter}`, x: i * (seatWidth + seatSpacing), y: row * rowSpacing + 70, side: 'left', status: 'available', bookable: true, group_id: null });
                                  seatIndex++;
                                }
                              }
                              if (row < rightRows) {
                                for (let i = 0; i < rightSeats; i++) {
                                  const seatLetter = String.fromCharCode(65 + seatIndex);
                                  seats.push({ id: `row${row}_seat_${seatIndex}`, row, seat_number: seatIndex + 1, seat_label: `${rowLabel}${seatLetter}`, x: leftSeats * (seatWidth + seatSpacing) + aisleWidth + i * (seatWidth + seatSpacing), y: row * rowSpacing + 70, side: 'right', status: 'available', bookable: true, group_id: null });
                                  seatIndex++;
                                }
                              }
                            }
                            const totalWidth = leftSeats * (seatWidth + seatSpacing) + aisleWidth + rightSeats * (seatWidth + seatSpacing) - seatSpacing;
                            const totalHeight = maxRowCount * rowSpacing + 70 + 30;
                            
                            const newObjects = canvasObjects.map(obj => obj.id === selectedObj.id ? { ...obj, width: totalWidth, height: totalHeight, properties: { ...obj.properties, left_row_count: leftRows, right_row_count: rightRows }, seats } : obj);
                            setCanvasObjects(newObjects);
                          }}
                          className="h-8 text-sm"
                        />
                      </div>
                    </div>
                    <p className="text-[9px] text-slate-500 mt-2">
                      Set different row counts for left/right sides. Useful when one side has a door.
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2 pt-2">
                    <input
                      type="checkbox"
                      id="front_at_top"
                      checked={selectedObj.properties?.front_at_top !== false}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, front_at_top: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <label htmlFor="front_at_top" className="text-sm text-slate-600">
                      Front at top
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="has_driver_seat"
                      checked={selectedObj.properties?.has_driver_seat !== false}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, has_driver_seat: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                    <label htmlFor="has_driver_seat" className="text-sm text-slate-600">
                      Show driver seat
                    </label>
                  </div>
                  
                  {/* Multi-Gap Rules System - Replaces old Space Configuration */}
                  <div className="pt-3 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-slate-700 text-xs">Gap Rules (Vertical Spacing)</Label>
                      <button
                        onClick={() => {
                          const currentGaps = selectedObj.properties?.gap_rules || [];
                          const newGap = {
                            id: `gap_${Date.now()}`,
                            afterRowNumber: Math.min(currentGaps.length + 3, selectedObj.properties?.row_count || 9),
                            gapHeightPx: 30,
                            applyTo: 'both',
                            showDivider: false,
                            dividerLabel: '',
                            dividerStyle: 'dashed'
                          };
                          updateObjectProperty('properties', {
                            ...selectedObj.properties,
                            gap_rules: [...currentGaps, newGap]
                          });
                        }}
                        className="text-[10px] px-2 py-1 bg-blue-50 text-blue-600 rounded hover:bg-blue-100 flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Add Gap
                      </button>
                    </div>
                    
                    {(!selectedObj.properties?.gap_rules || selectedObj.properties.gap_rules.length === 0) ? (
                      <p className="text-[10px] text-slate-400 italic">No gap rules. Click &quot;Add Gap&quot; to create one.</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {selectedObj.properties.gap_rules.map((gap, index) => (
                          <div key={gap.id || index} className="bg-slate-50 p-2 rounded border border-slate-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[10px] font-medium text-slate-600">Gap #{index + 1}</span>
                              <button
                                onClick={() => {
                                  const updatedGaps = selectedObj.properties.gap_rules.filter((_, i) => i !== index);
                                  updateObjectProperty('properties', {
                                    ...selectedObj.properties,
                                    gap_rules: updatedGaps
                                  });
                                }}
                                className="text-red-500 hover:text-red-700 p-0.5"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                            
                            <div className="grid grid-cols-3 gap-2 mb-2">
                              <div>
                                <Label className="text-[9px] text-slate-500">After Row</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  max={selectedObj.properties?.row_count || 9}
                                  value={gap.afterRowNumber}
                                  onChange={(e) => {
                                    const updatedGaps = [...selectedObj.properties.gap_rules];
                                    updatedGaps[index] = { ...gap, afterRowNumber: parseInt(e.target.value) || 1 };
                                    updateObjectProperty('properties', { ...selectedObj.properties, gap_rules: updatedGaps });
                                  }}
                                  className="h-6 text-[10px]"
                                />
                              </div>
                              <div>
                                <Label className="text-[9px] text-slate-500">Height (px)</Label>
                                <Input
                                  type="number"
                                  min="10"
                                  max="100"
                                  value={gap.gapHeightPx}
                                  onChange={(e) => {
                                    const updatedGaps = [...selectedObj.properties.gap_rules];
                                    updatedGaps[index] = { ...gap, gapHeightPx: parseInt(e.target.value) || 30 };
                                    updateObjectProperty('properties', { ...selectedObj.properties, gap_rules: updatedGaps });
                                  }}
                                  className="h-6 text-[10px]"
                                />
                              </div>
                              <div>
                                <Label className="text-[9px] text-slate-500">Apply To</Label>
                                <select
                                  value={gap.applyTo}
                                  onChange={(e) => {
                                    const updatedGaps = [...selectedObj.properties.gap_rules];
                                    updatedGaps[index] = { ...gap, applyTo: e.target.value };
                                    updateObjectProperty('properties', { ...selectedObj.properties, gap_rules: updatedGaps });
                                  }}
                                  className="h-6 w-full text-[10px] border rounded px-1"
                                >
                                  <option value="both">Both</option>
                                  <option value="left">Left Only</option>
                                  <option value="right">Right Only</option>
                                </select>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={gap.showDivider || false}
                                onChange={(e) => {
                                  const updatedGaps = [...selectedObj.properties.gap_rules];
                                  updatedGaps[index] = { ...gap, showDivider: e.target.checked };
                                  updateObjectProperty('properties', { ...selectedObj.properties, gap_rules: updatedGaps });
                                }}
                                className="h-3 w-3 rounded"
                              />
                              <span className="text-[9px] text-slate-500">Show divider line</span>
                              {gap.showDivider && (
                                <>
                                  <Input
                                    value={gap.dividerLabel || ''}
                                    placeholder="Label"
                                    onChange={(e) => {
                                      const updatedGaps = [...selectedObj.properties.gap_rules];
                                      updatedGaps[index] = { ...gap, dividerLabel: e.target.value };
                                      updateObjectProperty('properties', { ...selectedObj.properties, gap_rules: updatedGaps });
                                    }}
                                    className="h-5 text-[9px] flex-1"
                                  />
                                  <select
                                    value={gap.dividerStyle || 'dashed'}
                                    onChange={(e) => {
                                      const updatedGaps = [...selectedObj.properties.gap_rules];
                                      updatedGaps[index] = { ...gap, dividerStyle: e.target.value };
                                      updateObjectProperty('properties', { ...selectedObj.properties, gap_rules: updatedGaps });
                                    }}
                                    className="h-5 text-[9px] border rounded px-1"
                                  >
                                    <option value="dashed">Dash</option>
                                    <option value="solid">Solid</option>
                                  </select>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Per-Row Configuration - Advanced */}
                  <div className="pt-3 border-t">
                    <Label className="text-slate-700 text-xs mb-2 block">Custom Row Configuration</Label>
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        id="custom_rows_enabled"
                        checked={selectedObj.properties?.custom_rows_enabled || false}
                        onChange={(e) => updateObjectProperty('properties', { 
                          ...selectedObj.properties, 
                          custom_rows_enabled: e.target.checked 
                        })}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <label htmlFor="custom_rows_enabled" className="text-xs text-slate-600">
                        Enable per-row seat customization
                      </label>
                    </div>
                    
                    {selectedObj.properties?.custom_rows_enabled && (
                      <div className="max-h-40 overflow-y-auto space-y-1 bg-slate-50 p-2 rounded">
                        {Array.from({ length: selectedObj.properties?.row_count || 9 }, (_, idx) => {
                          const rowNum = (selectedObj.properties?.start_row_number || 1) + idx;
                          const customRows = selectedObj.properties?.custom_rows || {};
                          const rowConfig = customRows[rowNum] || {};
                          
                          return (
                            <div key={rowNum} className="flex items-center gap-2 text-[10px]">
                              <span className="w-8 text-slate-500">Row {rowNum}:</span>
                              <span className="text-slate-400">L</span>
                              <select
                                value={rowConfig.left_seats ?? (selectedObj.properties?.left_seats || 2)}
                                onChange={(e) => {
                                  const newCustomRows = { ...customRows };
                                  newCustomRows[rowNum] = { 
                                    ...rowConfig, 
                                    left_seats: parseInt(e.target.value) 
                                  };
                                  updateObjectProperty('properties', { 
                                    ...selectedObj.properties, 
                                    custom_rows: newCustomRows 
                                  });
                                }}
                                className="h-6 w-12 text-[10px] border rounded px-1"
                              >
                                <option value="0">0</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                              </select>
                              <span className="text-slate-400">R</span>
                              <select
                                value={rowConfig.right_seats ?? (selectedObj.properties?.right_seats || 2)}
                                onChange={(e) => {
                                  const newCustomRows = { ...customRows };
                                  newCustomRows[rowNum] = { 
                                    ...rowConfig, 
                                    right_seats: parseInt(e.target.value) 
                                  };
                                  updateObjectProperty('properties', { 
                                    ...selectedObj.properties, 
                                    custom_rows: newCustomRows 
                                  });
                                }}
                                className="h-6 w-12 text-[10px] border rounded px-1"
                              >
                                <option value="0">0</option>
                                <option value="1">1</option>
                                <option value="2">2</option>
                                <option value="3">3</option>
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  
                  {/* Booking Rules - Advanced System */}
                  <div className="pt-3 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-slate-700 text-xs">Booking Rules</Label>
                      <button
                        onClick={() => {
                          const currentRules = selectedObj.properties?.booking_rules || [];
                          const newRule = {
                            id: `rule_${Date.now()}`,
                            rows: 'all', // 'all' or array of row numbers
                            side: 'both', // 'left', 'right', 'both'
                            seats: 'all', // 'all' or specific seat refs
                            policy: 'individual', // 'individual', 'group_only', 'either'
                            groupType: 'adjacent_pair' // for group_only/either
                          };
                          updateObjectProperty('properties', {
                            ...selectedObj.properties,
                            booking_rules: [...currentRules, newRule]
                          });
                        }}
                        className="text-[10px] px-2 py-1 bg-purple-50 text-purple-600 rounded hover:bg-purple-100 flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Add Rule
                      </button>
                    </div>
                    
                    {(!selectedObj.properties?.booking_rules || selectedObj.properties.booking_rules.length === 0) ? (
                      <p className="text-[10px] text-slate-400 italic mb-2">No booking rules. Default: individual booking allowed.</p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto mb-2">
                        {selectedObj.properties.booking_rules.map((rule, index) => (
                          <div key={rule.id || index} className="bg-purple-50 p-2 rounded border border-purple-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[10px] font-medium text-purple-700">Rule #{index + 1}</span>
                              <button
                                onClick={() => {
                                  const updatedRules = selectedObj.properties.booking_rules.filter((_, i) => i !== index);
                                  updateObjectProperty('properties', {
                                    ...selectedObj.properties,
                                    booking_rules: updatedRules
                                  });
                                }}
                                className="text-red-500 hover:text-red-700 p-0.5"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                            
                            {/* Row Scope */}
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <div>
                                <Label className="text-[9px] text-slate-500">Rows</Label>
                                <select
                                  value={rule.rows === 'all' ? 'all' : 'selected'}
                                  onChange={(e) => {
                                    const updatedRules = [...selectedObj.properties.booking_rules];
                                    updatedRules[index] = { 
                                      ...rule, 
                                      rows: e.target.value === 'all' ? 'all' : [] 
                                    };
                                    updateObjectProperty('properties', { ...selectedObj.properties, booking_rules: updatedRules });
                                  }}
                                  className="h-6 w-full text-[10px] border rounded px-1"
                                >
                                  <option value="all">All Rows</option>
                                  <option value="selected">Selected Rows</option>
                                </select>
                              </div>
                              <div>
                                <Label className="text-[9px] text-slate-500">Side</Label>
                                <select
                                  value={rule.side}
                                  onChange={(e) => {
                                    const updatedRules = [...selectedObj.properties.booking_rules];
                                    updatedRules[index] = { ...rule, side: e.target.value };
                                    updateObjectProperty('properties', { ...selectedObj.properties, booking_rules: updatedRules });
                                  }}
                                  className="h-6 w-full text-[10px] border rounded px-1"
                                >
                                  <option value="both">Both Sides</option>
                                  <option value="left">Left Only</option>
                                  <option value="right">Right Only</option>
                                </select>
                              </div>
                            </div>
                            
                            {/* Row Selection (if selected rows) */}
                            {rule.rows !== 'all' && (
                              <div className="mb-2">
                                <Label className="text-[9px] text-slate-500">Select Rows (comma-separated)</Label>
                                <Input
                                  value={rule.rowsInput !== undefined ? rule.rowsInput : (Array.isArray(rule.rows) ? rule.rows.join(', ') : '')}
                                  placeholder="e.g., 1, 2, 3"
                                  onChange={(e) => {
                                    // Store raw input to allow comma typing
                                    const updatedRules = [...selectedObj.properties.booking_rules];
                                    updatedRules[index] = { ...rule, rowsInput: e.target.value };
                                    updateObjectProperty('properties', { ...selectedObj.properties, booking_rules: updatedRules });
                                  }}
                                  onBlur={(e) => {
                                    // Parse on blur and store both raw and parsed
                                    const rowNums = e.target.value.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n) && n > 0);
                                    const updatedRules = [...selectedObj.properties.booking_rules];
                                    updatedRules[index] = { ...rule, rows: rowNums, rowsInput: rowNums.join(', ') };
                                    updateObjectProperty('properties', { ...selectedObj.properties, booking_rules: updatedRules });
                                  }}
                                  className="h-6 text-[10px]"
                                />
                              </div>
                            )}
                            
                            {/* Policy */}
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-[9px] text-slate-500">Policy</Label>
                                <select
                                  value={rule.policy}
                                  onChange={(e) => {
                                    const updatedRules = [...selectedObj.properties.booking_rules];
                                    updatedRules[index] = { ...rule, policy: e.target.value };
                                    updateObjectProperty('properties', { ...selectedObj.properties, booking_rules: updatedRules });
                                  }}
                                  className="h-6 w-full text-[10px] border rounded px-1"
                                >
                                  <option value="individual">Individual</option>
                                  <option value="group_only">Group Only</option>
                                  <option value="either">Either</option>
                                </select>
                              </div>
                              {(rule.policy === 'group_only' || rule.policy === 'either') && (
                                <div>
                                  <Label className="text-[9px] text-slate-500">Group Type</Label>
                                  <select
                                    value={rule.groupType || 'adjacent_pair'}
                                    onChange={(e) => {
                                      const updatedRules = [...selectedObj.properties.booking_rules];
                                      updatedRules[index] = { ...rule, groupType: e.target.value };
                                      updateObjectProperty('properties', { ...selectedObj.properties, booking_rules: updatedRules });
                                    }}
                                    className="h-6 w-full text-[10px] border rounded px-1"
                                  >
                                    <option value="adjacent_pair">Adjacent Pair</option>
                                    <option value="full_row">Full Row</option>
                                    <option value="side_block">Side Block</option>
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <p className="text-[9px] text-slate-400">
                      Rules are applied in order. More specific rules override general ones.
                    </p>
                    
                    {/* Prevent Middle Seat Option - Only show if 3+ seats per side */}
                    {((selectedObj.properties?.left_seats || 2) >= 3 || (selectedObj.properties?.right_seats || 2) >= 3) && (
                      <div className="mt-3 pt-3 border-t border-purple-200">
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            id="prevent_middle_seat"
                            checked={selectedObj.properties?.prevent_middle_seat || false}
                            onChange={(e) => updateObjectProperty('properties', { 
                              ...selectedObj.properties, 
                              prevent_middle_seat: e.target.checked 
                            })}
                            className="h-4 w-4 rounded border-gray-300 text-amber-600"
                          />
                          <label htmlFor="prevent_middle_seat" className="text-xs text-slate-700 font-medium">
                            Prevent Middle Seat Selection
                          </label>
                        </div>
                        <p className="text-[9px] text-slate-400 pl-6">
                          Prevents booking only the middle seat. Customers must select edge seats first.
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Seat Label Customization */}
                  <div className="pt-3 border-t">
                    <Label className="text-slate-700 text-xs mb-2 block">Seat Label Format</Label>
                    
                    {/* Label Mode Selector */}
                    <div className="flex gap-2 mb-3">
                      <button
                        onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, label_mode: 'combined' })}
                        className={`flex-1 px-2 py-1.5 text-xs rounded-md transition-all ${
                          (selectedObj.properties?.label_mode || 'combined') === 'combined'
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        Same for All
                      </button>
                      <button
                        onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, label_mode: 'separate' })}
                        className={`flex-1 px-2 py-1.5 text-xs rounded-md transition-all ${
                          selectedObj.properties?.label_mode === 'separate'
                            ? 'bg-blue-100 text-blue-700 border border-blue-300'
                            : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        Left / Right
                      </button>
                    </div>
                    
                    {/* Combined Mode */}
                    {(selectedObj.properties?.label_mode || 'combined') === 'combined' && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-slate-500 text-[10px] mb-1 block">Column Letters</Label>
                          <Input
                            value={selectedObj.properties?.column_letters ?? 'ABCD'}
                            placeholder="ABCD"
                            maxLength={10}
                            onChange={(e) => {
                              // Allow any input including empty
                              const rawValue = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
                              
                              // Check for duplicates only if there are letters
                              if (rawValue.length > 0) {
                                const uniqueLetters = [...new Set(rawValue.split(''))].join('');
                                if (rawValue !== uniqueLetters) {
                                  showSuccessMessage({ type: 'error', text: 'Duplicate letters not allowed!' }, 'column_letters');
                                  return;
                                }
                              }
                              
                              // Use raw value or fallback to ABCD for seat generation
                              const letters = rawValue || 'ABCD';
                              const rowCount = selectedObj.properties?.row_count || 9;
                              const leftSeats = selectedObj.properties?.left_seats || 2;
                              const rightSeats = selectedObj.properties?.right_seats || 2;
                              const startRowNum = selectedObj.properties?.start_row_number || 1;
                              const seatWidth = 40;
                              const seatSpacing = 6;
                              const rowSpacing = 48;
                              const aisleWidth = 35;
                              
                              // Regenerate seats with new letters
                              const seats = [];
                              for (let row = 0; row < rowCount; row++) {
                                const rowLabel = startRowNum + row;
                                let seatIndex = 0;
                                
                                for (let i = 0; i < leftSeats; i++) {
                                  const seatLetter = letters[seatIndex] || String.fromCharCode(65 + seatIndex);
                                  seats.push({
                                    id: `row${row}_seat_${seatIndex}`,
                                    row: row,
                                    seat_number: seatIndex + 1,
                                    seat_label: `${rowLabel}${seatLetter}`,
                                    x: i * (seatWidth + seatSpacing),
                                    y: row * rowSpacing + 70,
                                    side: 'left',
                                    status: 'available',
                                    bookable: true,
                                    group_id: null
                                  });
                                  seatIndex++;
                                }
                                
                                for (let i = 0; i < rightSeats; i++) {
                                  const seatLetter = letters[seatIndex] || String.fromCharCode(65 + seatIndex);
                                  seats.push({
                                    id: `row${row}_seat_${seatIndex}`,
                                    row: row,
                                    seat_number: seatIndex + 1,
                                    seat_label: `${rowLabel}${seatLetter}`,
                                    x: leftSeats * (seatWidth + seatSpacing) + aisleWidth + i * (seatWidth + seatSpacing),
                                    y: row * rowSpacing + 70,
                                    side: 'right',
                                    status: 'available',
                                    bookable: true,
                                    group_id: null
                                  });
                                  seatIndex++;
                                }
                              }
                              
                              const newObjects = canvasObjects.map(obj =>
                                obj.id === selectedObj.id
                                  ? { ...obj, properties: { ...obj.properties, column_letters: rawValue }, seats }
                                  : obj
                              );
                              setCanvasObjects(newObjects);
                            }}
                            className="h-8 text-xs"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-500 text-[10px] mb-1 block">Start Row #</Label>
                          <Input
                            type="number"
                            min="1"
                            value={selectedObj.properties?.start_row_number || 1}
                            onChange={(e) => {
                              const startRow = parseInt(e.target.value) || 1;
                              const rowCount = selectedObj.properties?.row_count || 9;
                              const leftSeats = selectedObj.properties?.left_seats || 2;
                              const rightSeats = selectedObj.properties?.right_seats || 2;
                              const letters = selectedObj.properties?.column_letters || 'ABCD';
                              const seatWidth = 40;
                              const seatSpacing = 6;
                              const rowSpacing = 48;
                              const aisleWidth = 35;
                              
                              const seats = [];
                              for (let row = 0; row < rowCount; row++) {
                                const rowLabel = startRow + row;
                                let seatIndex = 0;
                                
                                for (let i = 0; i < leftSeats; i++) {
                                  const seatLetter = letters[seatIndex] || String.fromCharCode(65 + seatIndex);
                                  seats.push({
                                    id: `row${row}_seat_${seatIndex}`,
                                    row: row,
                                    seat_number: seatIndex + 1,
                                    seat_label: `${rowLabel}${seatLetter}`,
                                    x: i * (seatWidth + seatSpacing),
                                    y: row * rowSpacing + 70,
                                    side: 'left',
                                    status: 'available',
                                    bookable: true,
                                    group_id: null
                                  });
                                  seatIndex++;
                                }
                                
                                for (let i = 0; i < rightSeats; i++) {
                                  const seatLetter = letters[seatIndex] || String.fromCharCode(65 + seatIndex);
                                  seats.push({
                                    id: `row${row}_seat_${seatIndex}`,
                                    row: row,
                                    seat_number: seatIndex + 1,
                                    seat_label: `${rowLabel}${seatLetter}`,
                                    x: leftSeats * (seatWidth + seatSpacing) + aisleWidth + i * (seatWidth + seatSpacing),
                                    y: row * rowSpacing + 70,
                                    side: 'right',
                                    status: 'available',
                                    bookable: true,
                                    group_id: null
                                  });
                                  seatIndex++;
                                }
                              }
                              
                              const newObjects = canvasObjects.map(obj =>
                                obj.id === selectedObj.id
                                  ? { ...obj, properties: { ...obj.properties, start_row_number: startRow }, seats }
                                  : obj
                              );
                              setCanvasObjects(newObjects);
                            }}
                            className="h-8 text-xs"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Separate Left/Right Mode */}
                    {selectedObj.properties?.label_mode === 'separate' && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-slate-500 text-[10px] mb-1 block">Left Side Letters</Label>
                            <Input
                              value={selectedObj.properties?.left_letters ?? 'AB'}
                              placeholder="AB"
                              maxLength={5}
                              onChange={(e) => {
                                const rawValue = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
                                
                                // Check duplicates
                                if (rawValue.length > 0) {
                                  const unique = [...new Set(rawValue.split(''))].join('');
                                  if (rawValue !== unique) {
                                    showSuccessMessage({ type: 'error', text: 'Duplicate letters not allowed!' });
                                    return;
                                  }
                                }
                                
                                const leftLetters = rawValue || 'AB';
                                const rightLetters = selectedObj.properties?.right_letters || 'CD';
                                const rowCount = selectedObj.properties?.row_count || 9;
                                const leftSeats = selectedObj.properties?.left_seats || 2;
                                const rightSeats = selectedObj.properties?.right_seats || 2;
                                const startRowNum = selectedObj.properties?.start_row_number || 1;
                                const seatWidth = 40;
                                const seatSpacing = 6;
                                const rowSpacing = 48;
                                const aisleWidth = 35;
                                
                                const seats = [];
                                for (let row = 0; row < rowCount; row++) {
                                  const rowLabel = startRowNum + row;
                                  let seatIndex = 0;
                                  
                                  for (let i = 0; i < leftSeats; i++) {
                                    const seatLetter = leftLetters[i] || String.fromCharCode(65 + i);
                                    seats.push({
                                      id: `row${row}_seat_${seatIndex}`,
                                      row: row,
                                      seat_number: seatIndex + 1,
                                      seat_label: `${rowLabel}${seatLetter}`,
                                      x: i * (seatWidth + seatSpacing),
                                      y: row * rowSpacing + 70,
                                      side: 'left',
                                      status: 'available',
                                      bookable: true,
                                      group_id: null
                                    });
                                    seatIndex++;
                                  }
                                  
                                  for (let i = 0; i < rightSeats; i++) {
                                    const seatLetter = rightLetters[i] || String.fromCharCode(67 + i);
                                    seats.push({
                                      id: `row${row}_seat_${seatIndex}`,
                                      row: row,
                                      seat_number: seatIndex + 1,
                                      seat_label: `${rowLabel}${seatLetter}`,
                                      x: leftSeats * (seatWidth + seatSpacing) + aisleWidth + i * (seatWidth + seatSpacing),
                                      y: row * rowSpacing + 70,
                                      side: 'right',
                                      status: 'available',
                                      bookable: true,
                                      group_id: null
                                    });
                                    seatIndex++;
                                  }
                                }
                                
                                const newObjects = canvasObjects.map(obj =>
                                  obj.id === selectedObj.id
                                    ? { ...obj, properties: { ...obj.properties, left_letters: rawValue }, seats }
                                    : obj
                                );
                                setCanvasObjects(newObjects);
                              }}
                              className="h-8 text-xs"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-500 text-[10px] mb-1 block">Right Side Letters</Label>
                            <Input
                              value={selectedObj.properties?.right_letters ?? 'CD'}
                              placeholder="CD"
                              maxLength={5}
                              onChange={(e) => {
                                const rawValue = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
                                
                                // Check duplicates
                                if (rawValue.length > 0) {
                                  const unique = [...new Set(rawValue.split(''))].join('');
                                  if (rawValue !== unique) {
                                    showSuccessMessage({ type: 'error', text: 'Duplicate letters not allowed!' });
                                    return;
                                  }
                                }
                                
                                const leftLetters = selectedObj.properties?.left_letters || 'AB';
                                const rightLetters = rawValue || 'CD';
                                const rowCount = selectedObj.properties?.row_count || 9;
                                const leftSeats = selectedObj.properties?.left_seats || 2;
                                const rightSeats = selectedObj.properties?.right_seats || 2;
                                const startRowNum = selectedObj.properties?.start_row_number || 1;
                                const seatWidth = 40;
                                const seatSpacing = 6;
                                const rowSpacing = 48;
                                const aisleWidth = 35;
                                
                                const seats = [];
                                for (let row = 0; row < rowCount; row++) {
                                  const rowLabel = startRowNum + row;
                                  let seatIndex = 0;
                                  
                                  for (let i = 0; i < leftSeats; i++) {
                                    const seatLetter = leftLetters[i] || String.fromCharCode(65 + i);
                                    seats.push({
                                      id: `row${row}_seat_${seatIndex}`,
                                      row: row,
                                      seat_number: seatIndex + 1,
                                      seat_label: `${rowLabel}${seatLetter}`,
                                      x: i * (seatWidth + seatSpacing),
                                      y: row * rowSpacing + 70,
                                      side: 'left',
                                      status: 'available',
                                      bookable: true,
                                      group_id: null
                                    });
                                    seatIndex++;
                                  }
                                  
                                  for (let i = 0; i < rightSeats; i++) {
                                    const seatLetter = rightLetters[i] || String.fromCharCode(67 + i);
                                    seats.push({
                                      id: `row${row}_seat_${seatIndex}`,
                                      row: row,
                                      seat_number: seatIndex + 1,
                                      seat_label: `${rowLabel}${seatLetter}`,
                                      x: leftSeats * (seatWidth + seatSpacing) + aisleWidth + i * (seatWidth + seatSpacing),
                                      y: row * rowSpacing + 70,
                                      side: 'right',
                                      status: 'available',
                                      bookable: true,
                                      group_id: null
                                    });
                                    seatIndex++;
                                  }
                                }
                                
                                const newObjects = canvasObjects.map(obj =>
                                  obj.id === selectedObj.id
                                    ? { ...obj, properties: { ...obj.properties, right_letters: rawValue }, seats }
                                    : obj
                                );
                                setCanvasObjects(newObjects);
                              }}
                              className="h-8 text-xs"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-slate-500 text-[10px] mb-1 block">Start Row #</Label>
                          <Input
                            type="number"
                            min="1"
                            value={selectedObj.properties?.start_row_number || 1}
                            onChange={(e) => {
                              const startRow = parseInt(e.target.value) || 1;
                              const rowCount = selectedObj.properties?.row_count || 9;
                              const leftSeats = selectedObj.properties?.left_seats || 2;
                              const rightSeats = selectedObj.properties?.right_seats || 2;
                              const leftLetters = selectedObj.properties?.left_letters || 'AB';
                              const rightLetters = selectedObj.properties?.right_letters || 'CD';
                              const seatWidth = 40;
                              const seatSpacing = 6;
                              const rowSpacing = 48;
                              const aisleWidth = 35;
                              
                              const seats = [];
                              for (let row = 0; row < rowCount; row++) {
                                const rowLabel = startRow + row;
                                let seatIndex = 0;
                                
                                for (let i = 0; i < leftSeats; i++) {
                                  const seatLetter = leftLetters[i] || String.fromCharCode(65 + i);
                                  seats.push({
                                    id: `row${row}_seat_${seatIndex}`,
                                    row: row,
                                    seat_number: seatIndex + 1,
                                    seat_label: `${rowLabel}${seatLetter}`,
                                    x: i * (seatWidth + seatSpacing),
                                    y: row * rowSpacing + 70,
                                    side: 'left',
                                    status: 'available',
                                    bookable: true,
                                    group_id: null
                                  });
                                  seatIndex++;
                                }
                                
                                for (let i = 0; i < rightSeats; i++) {
                                  const seatLetter = rightLetters[i] || String.fromCharCode(67 + i);
                                  seats.push({
                                    id: `row${row}_seat_${seatIndex}`,
                                    row: row,
                                    seat_number: seatIndex + 1,
                                    seat_label: `${rowLabel}${seatLetter}`,
                                    x: leftSeats * (seatWidth + seatSpacing) + aisleWidth + i * (seatWidth + seatSpacing),
                                    y: row * rowSpacing + 70,
                                    side: 'right',
                                    status: 'available',
                                    bookable: true,
                                    group_id: null
                                  });
                                  seatIndex++;
                                }
                              }
                              
                              const newObjects = canvasObjects.map(obj =>
                                obj.id === selectedObj.id
                                  ? { ...obj, properties: { ...obj.properties, start_row_number: startRow }, seats }
                                  : obj
                              );
                              setCanvasObjects(newObjects);
                            }}
                            className="h-8 text-xs"
                          />
                        </div>
                      </div>
                    )}
                    
                    <p className="text-[10px] text-slate-400 mt-2">
                      Format: Row# + Letter (e.g., 1A, 1B, 2W, 2X)
                    </p>
                  </div>
                </div>
              )}

              {(selectedObj.type === 'round_table' || selectedObj.type === 'rect_table') && (
                <div className="space-y-3 pt-3 border-t">
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Seats (1-20)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={selectedObj.properties?.seats ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty value for typing
                        if (value === '') {
                          setCanvasObjects(prev => prev.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, properties: { ...obj.properties, seats: '' } }
                              : obj
                          ));
                          return;
                        }
                        let count = parseInt(value);
                        if (isNaN(count) || count < 1) return;
                        // Limit to max 20 seats for tables
                        if (count > 20) count = 20;
                        const seatSpacing = selectedObj.properties?.seat_spacing || 20;
                        const seats = [];
                        for (let i = 0; i < count; i++) {
                          seats.push({
                            id: `seat_${i}`,
                            seat_number: i + 1,
                            angle: (360 / count) * i,
                            offset: seatSpacing,
                            bookable: true
                          });
                        }
                        const newObjects = canvasObjects.map(obj =>
                          obj.id === selectedObj.id
                            ? { ...obj, properties: { ...obj.properties, seats: count }, seats }
                            : obj
                        );
                        setCanvasObjects(newObjects);
                      }}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Seat Spacing (10-60)</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="range"
                        min="10"
                        max="60"
                        step="5"
                        value={selectedObj.properties?.seat_spacing || 20}
                        onChange={(e) => {
                          const seatSpacing = parseInt(e.target.value);
                          const count = selectedObj.properties?.seats || 4;
                          const seats = [];
                          for (let i = 0; i < count; i++) {
                            seats.push({
                              id: `seat_${i}`,
                              seat_number: i + 1,
                              angle: (360 / count) * i,
                              offset: seatSpacing,
                              bookable: true
                            });
                          }
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, properties: { ...obj.properties, seat_spacing: seatSpacing }, seats }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }}
                        className="flex-1"
                      />
                      <span className="text-xs font-mono w-12 text-center">{selectedObj.properties?.seat_spacing || 20}px</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedObj.properties?.book_whole_table || false}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, book_whole_table: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <Label className="text-slate-700 text-xs">Book whole table</Label>
                  </div>
                  
                  {/* Zone Assignment */}
                  <div className="pt-3 border-t">
                    <Label className="text-slate-700 text-xs mb-2 block">Assign to Zone</Label>
                    <select
                      value={selectedObj.properties?.zone || ''}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, zone: e.target.value })}
                      className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">No Zone (Default)</option>
                      {getAvailableZones().map(zone => (
                        <option key={zone} value={zone}>{zone}</option>
                      ))}
                    </select>
                    <div className="text-xs text-slate-500 mt-1">
                      Tables in same zone share seat numbering
                    </div>
                  </div>
                </div>
              )}

              {(selectedObj.type === 'ga_zone' || selectedObj.type === 'vip_pod' || selectedObj.type === 'grouped_block') && (
                <div className="pt-3 border-t space-y-3">
                  {/* Pricing Configuration for GA/VIP zones */}
                  <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-3 rounded-lg border border-emerald-200">
                    <Label className="text-emerald-800 text-xs font-semibold mb-2 block flex items-center gap-2">
                      💰 Pricing & Ticketing
                    </Label>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-slate-700 text-xs mb-1 block">Ticket Type ID</Label>
                        <Input
                          value={selectedObj.properties?.ticket_type_id || ''}
                          onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, ticket_type_id: e.target.value })}
                          placeholder="e.g., TKT-GA-001"
                          className="h-9 text-sm"
                        />
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={selectedObj.properties?.currency || 'GBP'}
                          onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, currency: e.target.value })}
                          className="w-20 h-9 px-2 py-1 border rounded-md text-sm border-slate-200"
                        >
                          <option value="GBP">£</option>
                          <option value="USD">$</option>
                          <option value="EUR">€</option>
                          <option value="INR">₹</option>
                        </select>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={selectedObj.properties?.display_price || ''}
                          onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, display_price: parseFloat(e.target.value) || 0 })}
                          placeholder="Display price"
                          className="flex-1 h-9 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Capacity</Label>
                    <Input
                      type="number"
                      value={selectedObj.properties?.capacity || 20}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, capacity: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Icon Color</Label>
                      <Input
                        type="color"
                        value={selectedObj.properties?.icon_color || '#ffffff'}
                        onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, icon_color: e.target.value })}
                        className="h-10 cursor-pointer"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Text Color</Label>
                      <Input
                        type="color"
                        value={selectedObj.properties?.text_color || '#ffffff'}
                        onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, text_color: e.target.value })}
                        className="h-10 cursor-pointer"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedObj.properties?.text_bold !== false}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, text_bold: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <Label className="text-slate-700 text-xs">Bold Text</Label>
                  </div>
                </div>
              )}

              {/* Facilities styling options */}
              {['screen', 'stage', 'dj_booth', 'bar', 'toilet', 'entrance', 'exit', 'kitchen', 'wheelchair', 'camera', 'security', 'greenroom', 'backstage', 'foodcourt', 'merchandise', 'table_lamp', 'coffee_table'].includes(selectedObj.type) && (
                <div className="pt-3 border-t space-y-3">
                  <div className="text-xs font-semibold text-slate-600 mb-2">Styling</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Icon Color</Label>
                      <Input
                        type="color"
                        value={selectedObj.properties?.icon_color || '#ffffff'}
                        onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, icon_color: e.target.value })}
                        className="h-10 cursor-pointer"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Text Color</Label>
                      <Input
                        type="color"
                        value={selectedObj.properties?.text_color || '#ffffff'}
                        onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, text_color: e.target.value })}
                        className="h-10 cursor-pointer"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Font Size</Label>
                    <Input
                      type="number"
                      min="8"
                      max="36"
                      value={selectedObj.properties?.font_size || 12}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, font_size: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Text Style</Label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={selectedObj.properties?.text_bold ? "default" : "outline"}
                        onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, text_bold: !selectedObj.properties?.text_bold })}
                        className="flex-1"
                      >
                        <Bold className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedObj.properties?.text_italic ? "default" : "outline"}
                        onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, text_italic: !selectedObj.properties?.text_italic })}
                        className="flex-1"
                      >
                        <Italic className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedObj.properties?.text_underline ? "default" : "outline"}
                        onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, text_underline: !selectedObj.properties?.text_underline })}
                        className="flex-1"
                      >
                        <Underline className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Text object properties - additional styling options */}
              {selectedObj.type === 'text' && (
                <div className="pt-3 border-t space-y-3">
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Icon</Label>
                    <select
                      value={selectedObj.properties?.icon || ''}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, icon: e.target.value || null })}
                      className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {TEXT_ICONS.map((item, idx) => (
                        <option key={idx} value={item.label === 'None' ? '' : item.label}>{item.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Font Family</Label>
                    <select
                      value={selectedObj.properties?.font_family || 'Inter, sans-serif'}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, font_family: e.target.value })}
                      className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {FONT_FAMILIES.map((font, idx) => (
                        <option key={idx} value={font.value}>{font.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Font Size</Label>
                      <Input
                        type="number"
                        min="8"
                        max="72"
                        value={selectedObj.properties?.font_size || 16}
                        onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, font_size: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Background</Label>
                      <Input
                        type="color"
                        value={selectedObj.properties?.bg_color || '#ffffff'}
                        onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, bg_color: e.target.value })}
                        className="h-10 cursor-pointer"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Text Color</Label>
                    <Input
                      type="color"
                      value={selectedObj.properties?.text_color || '#334155'}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, text_color: e.target.value })}
                      className="h-10 cursor-pointer"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Text Style</Label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={selectedObj.properties?.text_bold ? "default" : "outline"}
                        onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, text_bold: !selectedObj.properties?.text_bold })}
                        className="flex-1"
                      >
                        <Bold className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedObj.properties?.text_italic ? "default" : "outline"}
                        onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, text_italic: !selectedObj.properties?.text_italic })}
                        className="flex-1"
                      >
                        <Italic className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedObj.properties?.text_underline ? "default" : "outline"}
                        onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, text_underline: !selectedObj.properties?.text_underline })}
                        className="flex-1"
                      >
                        <Underline className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Single Seat properties */}
              {selectedObj.type === 'seat' && (
                <div className="pt-3 border-t space-y-3">
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Assign to Zone</Label>
                    <select
                      value={selectedObj.properties?.zone || ''}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, zone: e.target.value })}
                      className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">No Zone (Default)</option>
                      {getAvailableZones().map(zone => (
                        <option key={zone} value={zone}>{zone}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Sofa properties */}
              {selectedObj.type === 'sofa' && (
                <div className="pt-3 border-t space-y-3">
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Seater Count (1-10)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={selectedObj.properties?.seater_count ?? ''}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        
                        // Allow empty string while typing
                        if (rawValue === '') {
                          // Update with empty to allow clearing
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, properties: { ...obj.properties, seater_count: '' } }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                          return;
                        }
                        
                        const count = Math.min(10, Math.max(1, parseInt(rawValue) || 1));
                        const spacing = selectedObj.properties?.spacing || 8;
                        const prefix = selectedObj.properties?.seat_label_prefix || 'S';
                        const seats = [];
                        for (let i = 0; i < count; i++) {
                          seats.push({
                            id: `seat_${i}`,
                            seat_number: i + 1,
                            seat_label: `${prefix}${i + 1}`,
                            x: i * (40 + spacing),
                            y: 10,
                            bookable: true,
                            status: 'available'
                          });
                        }
                        const newWidth = Math.max(80, count * (40 + spacing) + 30);
                        const newObjects = canvasObjects.map(obj =>
                          obj.id === selectedObj.id
                            ? { ...obj, width: newWidth, properties: { ...obj.properties, seater_count: count }, seats }
                            : obj
                        );
                        setCanvasObjects(newObjects);
                      }}
                      onBlur={(e) => {
                        // Restore default if empty on blur
                        const rawValue = e.target.value;
                        if (rawValue === '' || isNaN(parseInt(rawValue))) {
                          const count = 3; // Default seater count
                          const spacing = selectedObj.properties?.spacing || 8;
                          const prefix = selectedObj.properties?.seat_label_prefix || 'S';
                          const seats = [];
                          for (let i = 0; i < count; i++) {
                            seats.push({
                              id: `seat_${i}`,
                              seat_number: i + 1,
                              seat_label: `${prefix}${i + 1}`,
                              x: i * (40 + spacing),
                              y: 10,
                              bookable: true,
                              status: 'available'
                            });
                          }
                          const newWidth = Math.max(80, count * (40 + spacing) + 30);
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, width: newWidth, properties: { ...obj.properties, seater_count: count }, seats }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }
                      }}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Seat Label Prefix</Label>
                    <Input
                      type="text"
                      value={selectedObj.properties?.seat_label_prefix ?? 'S'}
                      onChange={(e) => {
                        const prefix = e.target.value.toUpperCase();
                        // Extract sofa number from label (e.g., "S1" -> "1")
                        const sofaNum = selectedObj.label?.replace(/\D/g, '') || '1';
                        // Allow empty while typing
                        const count = parseInt(selectedObj.properties?.seater_count) || 3;
                        const spacing = selectedObj.properties?.spacing || 8;
                        const seats = [];
                        for (let i = 0; i < count; i++) {
                          seats.push({
                            id: `seat_${i}`,
                            seat_number: i + 1,
                            seat_label: `${prefix || 'S'}${sofaNum}-${i + 1}`,
                            x: i * (40 + spacing),
                            y: 10,
                            bookable: true,
                            status: 'available'
                          });
                        }
                        const newObjects = canvasObjects.map(obj =>
                          obj.id === selectedObj.id
                            ? { ...obj, properties: { ...obj.properties, seat_label_prefix: prefix }, seats }
                            : obj
                        );
                        setCanvasObjects(newObjects);
                      }}
                      onBlur={(e) => {
                        // Restore default 'S' if empty on blur
                        if (!e.target.value.trim()) {
                          const prefix = 'S';
                          const sofaNum = selectedObj.label?.replace(/\D/g, '') || '1';
                          const count = parseInt(selectedObj.properties?.seater_count) || 3;
                          const spacing = selectedObj.properties?.spacing || 8;
                          const seats = [];
                          for (let i = 0; i < count; i++) {
                            seats.push({
                              id: `seat_${i}`,
                              seat_number: i + 1,
                              seat_label: `${prefix}${sofaNum}-${i + 1}`,
                              x: i * (40 + spacing),
                              y: 10,
                              bookable: true,
                              status: 'available'
                            });
                          }
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, properties: { ...obj.properties, seat_label_prefix: prefix }, seats }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }
                      }}
                      placeholder="S"
                      maxLength={3}
                    />
                    <div className="text-xs text-slate-500 mt-1">Labels: {selectedObj.properties?.seat_label_prefix || 'S'}{selectedObj.label?.replace(/\D/g, '') || '1'}-1, {selectedObj.properties?.seat_label_prefix || 'S'}{selectedObj.label?.replace(/\D/g, '') || '1'}-2, etc.</div>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Spacing (0-30)</Label>
                    <Input
                      type="range"
                      min="0"
                      max="30"
                      value={selectedObj.properties?.spacing || 8}
                      onChange={(e) => {
                        const spacing = parseInt(e.target.value);
                        const count = selectedObj.properties?.seater_count || 3;
                        const prefix = selectedObj.properties?.seat_label_prefix || 'S';
                        const curve = selectedObj.properties?.curve_amount || 0;
                        const seats = [];
                        for (let i = 0; i < count; i++) {
                          const centerOffset = (count - 1) / 2;
                          const distanceFromCenter = i - centerOffset;
                          const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 100) : 0;
                          seats.push({
                            id: `seat_${i}`,
                            seat_number: i + 1,
                            seat_label: `${prefix}${i + 1}`,
                            x: i * (40 + spacing),
                            y: 10 + curveY,
                            bookable: true,
                            status: 'available'
                          });
                        }
                        const maxCurve = curve !== 0 ? Math.pow((count - 1) / 2, 2) * Math.abs(curve / 100) : 0;
                        const newWidth = Math.max(80, count * (40 + spacing) + 30);
                        const newHeight = Math.max(60, 36 + maxCurve + 30);
                        const newObjects = canvasObjects.map(obj =>
                          obj.id === selectedObj.id
                            ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, spacing }, seats }
                            : obj
                        );
                        setCanvasObjects(newObjects);
                      }}
                    />
                    <div className="text-xs text-slate-500 mt-1 text-center">{selectedObj.properties?.spacing || 8}px</div>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Curve Amount (-200 to 200)</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="range"
                        min="-200"
                        max="200"
                        step="10"
                        value={selectedObj.properties?.curve_amount || 0}
                        onChange={(e) => {
                          const curve = parseFloat(e.target.value);
                          const count = selectedObj.properties?.seater_count || 3;
                          const spacing = selectedObj.properties?.spacing || 8;
                          const prefix = selectedObj.properties?.seat_label_prefix || 'S';
                          const seats = [];
                          for (let i = 0; i < count; i++) {
                            const centerOffset = (count - 1) / 2;
                            const distanceFromCenter = i - centerOffset;
                            const curveY = curve !== 0 ? Math.pow(distanceFromCenter, 2) * (curve / 100) : 0;
                            seats.push({
                              id: `seat_${i}`,
                              seat_number: i + 1,
                              seat_label: `${prefix}${i + 1}`,
                              x: i * (40 + spacing),
                              y: 10 + curveY,
                              bookable: true,
                              status: 'available'
                            });
                          }
                          const maxCurve = curve !== 0 ? Math.pow((count - 1) / 2, 2) * Math.abs(curve / 100) : 0;
                          const newWidth = Math.max(80, count * (40 + spacing) + 30);
                          const newHeight = Math.max(60, 36 + maxCurve + 30);
                          const newObjects = canvasObjects.map(obj =>
                            obj.id === selectedObj.id
                              ? { ...obj, width: newWidth, height: newHeight, properties: { ...obj.properties, curve_amount: curve }, seats }
                              : obj
                          );
                          setCanvasObjects(newObjects);
                        }}
                        className="flex-1"
                      />
                      <span className="text-xs font-mono w-10 text-center">{selectedObj.properties?.curve_amount || 0}</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Sofa Color</Label>
                    <Input
                      type="color"
                      value={selectedObj.properties?.sofa_color || '#a78bfa'}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, sofa_color: e.target.value })}
                      className="h-10 cursor-pointer"
                    />
                  </div>
                  
                  {/* Seat Color */}
                  <div className="pt-2 border-t">
                    <Label className="text-slate-700 text-xs mb-2 block">Seat Color</Label>
                    <div className="flex gap-2 items-center">
                      <Input
                        type="color"
                        value={selectedObj.properties?.seat_color || '#22c55e'}
                        onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: e.target.value })}
                        className="h-10 w-16 cursor-pointer"
                      />
                      <div className="flex gap-1 flex-wrap flex-1">
                        <button
                          className={`w-6 h-6 rounded border-2 shadow hover:ring-2 hover:ring-indigo-500 ${selectedObj.properties?.seat_color === '#22c55e' ? 'ring-2 ring-indigo-500' : 'border-white'}`}
                          style={{ backgroundColor: '#22c55e' }}
                          title="Green"
                          onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: '#22c55e' })}
                        />
                        <button
                          className={`w-6 h-6 rounded border-2 shadow hover:ring-2 hover:ring-indigo-500 ${selectedObj.properties?.seat_color === '#ef4444' ? 'ring-2 ring-indigo-500' : 'border-white'}`}
                          style={{ backgroundColor: '#ef4444' }}
                          title="Red"
                          onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: '#ef4444' })}
                        />
                        <button
                          className={`w-6 h-6 rounded border-2 shadow hover:ring-2 hover:ring-indigo-500 ${selectedObj.properties?.seat_color === '#eab308' ? 'ring-2 ring-indigo-500' : 'border-white'}`}
                          style={{ backgroundColor: '#eab308' }}
                          title="Yellow"
                          onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: '#eab308' })}
                        />
                        <button
                          className={`w-6 h-6 rounded border-2 shadow hover:ring-2 hover:ring-indigo-500 ${selectedObj.properties?.seat_color === '#3b82f6' ? 'ring-2 ring-indigo-500' : 'border-white'}`}
                          style={{ backgroundColor: '#3b82f6' }}
                          title="Blue"
                          onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: '#3b82f6' })}
                        />
                        <button
                          className={`w-6 h-6 rounded border-2 shadow hover:ring-2 hover:ring-indigo-500 ${selectedObj.properties?.seat_color === '#a855f7' ? 'ring-2 ring-indigo-500' : 'border-white'}`}
                          style={{ backgroundColor: '#a855f7' }}
                          title="Purple"
                          onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, seat_color: '#a855f7' })}
                        />
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">This color shows in checkout for available seats</div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedObj.properties?.with_table || false}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, with_table: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <Label className="text-slate-700 text-xs">Add Coffee Table</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={selectedObj.properties?.with_lights || false}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, with_lights: e.target.checked })}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <Label className="text-slate-700 text-xs">Add Table Lights</Label>
                  </div>
                  
                  {/* Booking Mode */}
                  <div className="pt-2 border-t">
                    <Label className="text-slate-700 text-xs mb-2 block">Booking Mode</Label>
                    <select
                      value={selectedObj.properties?.booking_mode || 'individual'}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, booking_mode: e.target.value })}
                      className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="individual">Individual Seats</option>
                      <option value="whole">Book Entire Sofa</option>
                    </select>
                    <div className="text-xs text-slate-500 mt-1">
                      {selectedObj.properties?.booking_mode === 'whole' 
                        ? 'Users book all seats together' 
                        : `Users can book individual seats (${selectedObj.properties?.seater_count || 3} seats)`}
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Assign to Zone</Label>
                    <select
                      value={selectedObj.properties?.zone || ''}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, zone: e.target.value })}
                      className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">No Zone (Default)</option>
                      {getAvailableZones().map(zone => (
                        <option key={zone} value={zone}>{zone}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Zone / Section properties */}
              {selectedObj.type === 'zone' && (
                <div className="pt-3 border-t space-y-3">
                  {/* Section/Zone Pricing Configuration */}
                  <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-3 rounded-lg border border-emerald-200">
                    <Label className="text-emerald-800 text-xs font-semibold mb-2 block flex items-center gap-2">
                      💰 Pricing Configuration
                    </Label>
                    <p className="text-xs text-emerald-600 mb-3">Configure pricing for this section (linked to ticketing platform)</p>
                    
                    {/* Price Tier Selection */}
                    <div className="space-y-2">
                      <div>
                        <Label className="text-slate-700 text-xs mb-1 block">Price Tier</Label>
                        <select
                          value={selectedObj.properties?.price_tier || 'standard'}
                          onChange={(e) => {
                            const tier = e.target.value;
                            const tierColors = {
                              'vip': { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' },
                              'premium': { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' },
                              'standard': { bg: '#dcfce7', border: '#22c55e', text: '#166534' },
                              'economy': { bg: '#f3f4f6', border: '#9ca3af', text: '#374151' },
                              'custom': selectedObj.properties?.custom_tier_color || { bg: '#e2e8f0', border: '#64748b', text: '#334155' }
                            };
                            const colors = tierColors[tier] || tierColors['standard'];
                            updateObjectProperty('properties', { 
                              ...selectedObj.properties, 
                              price_tier: tier,
                              bg_color: colors.bg,
                              border_color: colors.border,
                              text_color: colors.text
                            });
                          }}
                          className="w-full h-9 px-3 py-1 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        >
                          <option value="vip">🌟 VIP (Highest)</option>
                          <option value="premium">💎 Premium</option>
                          <option value="standard">✓ Standard</option>
                          <option value="economy">📉 Economy</option>
                          <option value="custom">🎨 Custom</option>
                        </select>
                      </div>
                      
                      {/* External Ticket Type ID */}
                      <div>
                        <Label className="text-slate-700 text-xs mb-1 block">Ticket Type ID (from ticketing API)</Label>
                        <Input
                          value={selectedObj.properties?.ticket_type_id || ''}
                          onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, ticket_type_id: e.target.value })}
                          placeholder="e.g., TKT-VIP-001"
                          className="h-9 text-sm"
                        />
                        <p className="text-xs text-slate-400 mt-1">Link to external ticketing system</p>
                      </div>
                      
                      {/* Display Price (for preview) */}
                      <div>
                        <Label className="text-slate-700 text-xs mb-1 block">Display Price (Preview)</Label>
                        <div className="flex gap-2">
                          <select
                            value={selectedObj.properties?.currency || 'GBP'}
                            onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, currency: e.target.value })}
                            className="w-20 h-9 px-2 py-1 border rounded-md text-sm border-slate-200"
                          >
                            <option value="GBP">£</option>
                            <option value="USD">$</option>
                            <option value="EUR">€</option>
                            <option value="INR">₹</option>
                          </select>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={selectedObj.properties?.display_price || ''}
                            onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, display_price: parseFloat(e.target.value) || 0 })}
                            placeholder="0.00"
                            className="flex-1 h-9 text-sm"
                          />
                        </div>
                        <p className="text-xs text-slate-400 mt-1">Actual price comes from event API at checkout</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Auto-resize Zone Button */}
                  <Button
                    onClick={() => autoResizeZone(selectedObj.id)}
                    variant="outline"
                    className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  >
                    <Maximize2 className="w-4 h-4 mr-2" />
                    Fit to Contents
                  </Button>
                  
                  {/* Lock Zone Toggle */}
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div>
                      <Label className="text-slate-700 text-xs font-semibold block">Lock Zone (Background)</Label>
                      <p className="text-xs text-slate-500">When locked, clicks pass through to seats</p>
                    </div>
                    <button
                      onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, locked: !selectedObj.properties?.locked })}
                      className={`w-10 h-6 rounded-full relative transition-colors ${selectedObj.properties?.locked ? 'bg-indigo-600' : 'bg-slate-300'}`}
                    >
                      <div className={`absolute w-4 h-4 bg-white rounded-full top-1 transition-all shadow ${selectedObj.properties?.locked ? 'right-1' : 'left-1'}`}></div>
                    </button>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Label Position</Label>
                    <select
                      value={selectedObj.properties?.label_position || 'top'}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, label_position: e.target.value })}
                      className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="top">Top (Outside)</option>
                      <option value="top-inside">Top (Inside)</option>
                      <option value="bottom">Bottom (Outside)</option>
                      <option value="bottom-inside">Bottom (Inside)</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                      <option value="center">Center</option>
                      <option value="corner-top-left">Corner Top Left</option>
                      <option value="corner-top-right">Corner Top Right</option>
                      <option value="corner-bottom-left">Corner Bottom Left</option>
                      <option value="corner-bottom-right">Corner Bottom Right</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Text Color</Label>
                      <Input
                        type="color"
                        value={selectedObj.properties?.text_color || '#1e40af'}
                        onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, text_color: e.target.value })}
                        className="h-10 cursor-pointer"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-700 text-xs mb-2 block">Background</Label>
                      <Input
                        type="color"
                        value={selectedObj.properties?.bg_color || '#dbeafe'}
                        onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, bg_color: e.target.value })}
                        className="h-10 cursor-pointer"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Font Size</Label>
                    <Input
                      type="number"
                      min="8"
                      max="36"
                      value={selectedObj.properties?.font_size || 12}
                      onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, font_size: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs mb-2 block">Text Style</Label>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={selectedObj.properties?.text_bold ? "default" : "outline"}
                        onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, text_bold: !selectedObj.properties?.text_bold })}
                        className="flex-1"
                      >
                        <Bold className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedObj.properties?.text_italic ? "default" : "outline"}
                        onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, text_italic: !selectedObj.properties?.text_italic })}
                        className="flex-1"
                      >
                        <Italic className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant={selectedObj.properties?.text_underline ? "default" : "outline"}
                        onClick={() => updateObjectProperty('properties', { ...selectedObj.properties, text_underline: !selectedObj.properties?.text_underline })}
                        className="flex-1"
                      >
                        <Underline className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Border Customization */}
                  <div className="pt-3 border-t">
                    <Label className="text-slate-700 text-xs font-semibold mb-2 block flex items-center gap-2">
                      🖌️ Border Style
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-slate-600 text-xs mb-1.5 block">Border Color</Label>
                        <Input
                          type="color"
                          value={selectedObj.properties?.border_color || '#3b82f6'}
                          onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, border_color: e.target.value })}
                          className="h-10 cursor-pointer"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-600 text-xs mb-1.5 block">Border Style</Label>
                        <select
                          value={selectedObj.properties?.border_style || 'dashed'}
                          onChange={(e) => updateObjectProperty('properties', { ...selectedObj.properties, border_style: e.target.value })}
                          className="w-full h-10 px-3 py-2 border rounded-md text-sm border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="solid">Solid</option>
                          <option value="dashed">Dashed</option>
                          <option value="dotted">Dotted</option>
                          <option value="double">Double</option>
                        </select>
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Border style will also appear on the checkout page</p>
                  </div>
                  
                  <div className="text-xs text-slate-500 p-2 bg-blue-50 rounded">
                    <strong>Resize:</strong> Drag corner/edge handles to resize.
                    <br />
                    <strong>Assign:</strong> Select rows/tables and choose this zone from their properties.
                  </div>
                </div>
              )}

              <div className="pt-4 border-t space-y-2">
                <Button onClick={duplicateObject} variant="outline" className="w-full">
                  <Copy className="w-4 h-4 mr-2" />
                  Duplicate
                </Button>
                <Button 
                  onClick={deleteObject} 
                  variant="outline" 
                  className="w-full border-red-200 text-red-600 hover:bg-red-50"
                  disabled={layoutAssigned}
                  title={layoutAssigned ? "Cannot delete - layout is assigned to an event" : "Delete"}
                >
                  {layoutAssigned && <Lock className="w-4 h-4 mr-2" />}
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Select an object on the canvas to edit its properties.</p>
          )}
          </>
          )}
            </div>
          )}
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {confirmDialog.action === 'delete' ? 'Confirm Delete' : 
               confirmDialog.action === 'duplicate' ? 'Confirm Duplicate' : 
               confirmDialog.action === 'linked_warning' ? '⚠️ Layout Linked to Event' : 
               'Confirm Action'}
            </h3>
            <p className="text-slate-600 mb-4 whitespace-pre-line">{confirmDialog.message}</p>
            
            {/* Show event info for linked warning */}
            {confirmDialog.action === 'linked_warning' && confirmDialog.showUnlink && (
              <div className="mb-4 p-3 bg-slate-50 rounded-lg border">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-slate-500">Event:</span>
                  <span className="text-sm font-semibold">{confirmDialog.eventName}</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-slate-500">Status:</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    confirmDialog.eventStatus === 'Draft' ? 'bg-gray-100 text-gray-600' :
                    confirmDialog.eventStatus === 'On Sale' ? 'bg-green-100 text-green-700' :
                    confirmDialog.eventStatus === 'Published' ? 'bg-blue-100 text-blue-700' :
                    confirmDialog.eventStatus === 'Sold Out' ? 'bg-purple-100 text-purple-700' :
                    confirmDialog.eventStatus === 'Cancelled' ? 'bg-red-100 text-red-700' :
                    confirmDialog.eventStatus === 'Postponed' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {confirmDialog.eventStatus}
                  </span>
                </div>
                {confirmDialog.bookedCount > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-500">Booked Tickets:</span>
                    <span className="text-sm font-semibold text-red-600">{confirmDialog.bookedCount}</span>
                  </div>
                )}
              </div>
            )}
            
            {confirmDialog.action === 'delete' && (
              <label className="flex items-center gap-2 mb-4 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={skipDeleteConfirm}
                  onChange={(e) => {
                    setSkipDeleteConfirm(e.target.checked);
                    sessionStorage.setItem('skipDeleteConfirm', e.target.checked);
                  }}
                  className="w-4 h-4 rounded border-slate-300"
                />
                Don&apos;t ask me again this session
              </label>
            )}
            
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={hideConfirmDialog}>
                {confirmDialog.action === 'linked_warning' ? 'Close' : 'Cancel'}
              </Button>
              
              {/* For linked warning, show unlink button if allowed */}
              {confirmDialog.action === 'linked_warning' && confirmDialog.showUnlink && (
                <Button
                  variant="destructive"
                  onClick={confirmDialog.onConfirm}
                  disabled={!confirmDialog.canUnlink}
                  title={!confirmDialog.canUnlink ? 
                    (confirmDialog.bookedCount > 0 ? 'Cannot unlink: tickets are booked' : 'Cannot unlink: event is active') : 
                    'Unlink this layout from the event'}
                >
                  {confirmDialog.canUnlink ? 'Unlink from Event' : 'Cannot Unlink'}
                </Button>
              )}
              
              {/* Regular confirm buttons */}
              {confirmDialog.action !== 'linked_warning' && (
                <Button
                  variant={confirmDialog.action === 'delete' ? 'destructive' : 'default'}
                  onClick={confirmDialog.onConfirm}
                >
                  {confirmDialog.action === 'delete' ? `Delete ${confirmDialog.count}` : 
                   confirmDialog.action === 'duplicate' ? `Duplicate ${confirmDialog.count}` : 
                   'Confirm'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Bus Layout Exclusive Mode Warning Modal */}
      {busLayoutWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-100 rounded-full">
                <Bus className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                Bus Layout Mode
              </h3>
            </div>
            <p className="text-slate-600 mb-4 whitespace-pre-line">{busLayoutWarning.message}</p>
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Bus Layout is designed for bus/coach seating with a flight-style booking experience. It operates in exclusive mode and cannot be combined with other seating arrangements.
              </p>
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={busLayoutWarning.onCancel}>
                Cancel
              </Button>
              <Button 
                onClick={busLayoutWarning.onConfirm}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* On-canvas walkthrough for new organizers (auto-opens on first visit,
          replayable via the "?" Help button in the top bar) */}
      <CanvasWalkthrough open={walkthrough.open} onClose={walkthrough.close} brandName={embedBrandName} />
    </div>
  );
}